/**
 * src/config/productDetails.js
 *
 * VISINVOKE Dashboard - PRODUCT DETAIL CONTENT (single source of truth).
 *
 * Content for the seven product detail pages reached from a category card's
 * "CLICK HERE FOR MORE INFO" pill. ProductDetailPage.js renders this through
 * one template, so every page belongs to the same VISINVOKE design family while
 * carrying as much real information as the product actually has.
 *
 * SOURCE AUTHORITY (in order, per the current product-content hierarchy):
 *   1. Current local product implementation / project files
 *        customer-portal\src\modules\scc\navigation\navigationConfig.js      (SCC sections + workspaces)
 *        customer-portal\src\modules\scc\businessSetup\SccBusinessSetup.js   (160 BT profession packages, libraries)
 *        C:\visinvoke-desktop-app\src\contentplanner\plans.js                (Content Planner plans/features/providers)
 *        C:\visinvoke-desktop-app\docs\CONTENT_PLANNER_INITIAL_BUILD.md      (Content Planner build record, 20 Aug 2026)
 *        C:\visinvoke-desktop-app\src\commandcentre\catalogue\canonical-card-inventory.json
 *   2. Current local VISINVOKE product websites (E:\Visinvoke\Visinvoke Websites)
 *        Visinvoke Service Command Centre\...  (VI-036 page + fact sheet)
 *        Visinvoke Website Capture Suite\...   (VI-049 page + fact sheet)
 *        Visinvoke PC Protection\pc-protection.html + VI-047 fact sheet
 *        Visinvoke Track Wipe Lock\track-lock-and-wipe.html + VI-048 fact sheet
 *        Visinvoke RPG Veridian\Rpg-Veridian.html + VI-033 fact sheet
 *        Visinvoke RPG Cybertech\RPG-Cybertech.html + VI-034 fact sheet
 *   3. Current local Dashboard files
 *        UtilitiesPage.js (PAID_UTILITIES, WebsiteIntelligenceSection, ContentPlannerSection,
 *                          SCC_CLOUD_TIERS, WORKFORCE_EXTENSIONS, CONTENT_PLANNER_TIERS)
 *        SecurityPage.js  (canonical Security names, capabilities, pricing, enquiry flow)
 *        RpgPage.js       (canonical world names, status, $14.99 / $24.95 pricing)
 *
 * Nothing here is invented. No price, capability, platform, device, guarantee or
 * release date appears unless one of the sources above states it. Unresolved
 * matters stay unresolved, and are stated quietly rather than hidden.
 *
 * These are customer-facing pages: internal route names, engineering audit
 * counts and implementation metadata are deliberately kept off them.
 *
 * BLOCK TYPES (rendered identically for every product):
 *   para statement chips stats groups list steps cards split tiers facts callout note
 */

import { PROFESSION_COUNT, PROFESSION_ALL_NAMES } from './sccProfessions';
import { SCC_CLOUD_OPTIONS, SCC_WORKFORCE_EXTENSIONS } from './sccPricing';

// Category pages a detail page returns to.
const BACK_PAID = { label: 'Paid VISINVOKE Utilities', route: '/dashboard/utilities/paid' };
const BACK_SEC = { label: 'VISINVOKE Security', route: '/dashboard/security' };
const BACK_RPG = { label: 'VISINVOKE RPG Worlds', route: '/dashboard/rpg' };
const BACK_FREE = { label: 'Free VISINVOKE Utilities', route: '/dashboard/utilities/free' };

const SECURITY_ENQUIRY_EMAIL = 'support@visinvoke.com';

// ===========================================================================
// 1. VISINVOKE SERVICE COMMAND CENTRE
// ===========================================================================
const SERVICE_COMMAND_CENTRE = {
  key: 'service-command-centre',
  backTo: BACK_PAID,
  category: 'Paid VISINVOKE Utility',
  eyebrow: 'OPERATIONAL BUSINESS PLATFORM',
  name: 'VISINVOKE Service Command Centre',
  shortName: 'Service Command Centre',
  icon: '\u{1F527}',
  accent: '#38bdf8',
  accent2: '#0ea5e9',
  tagline: 'Every booking, customer, device, job, invoice and payment your service business handles - in one workspace, on one connected record.',
  purpose: 'Service Command Centre is the operating system for a service business. Fifteen operational sections share the same records, so a booking becomes a job, a job consumes stock and produces an invoice, and an invoice becomes a payment - without anything being retyped, re-filed or chased through a spreadsheet.',
  price: { main: '$65 USD', billing: 'Per Month', note: 'Base subscription \u00b7 up to 10 staff \u00b7 unlimited jobs \u00b7 unlimited locations' },
  status: { label: 'Subscription required', tone: '#fbbf24' },
  accessNote: 'Available in VISINVOKE Desktop Command Centre. Use DCC on your computer to launch Service Command Centre.',
  action: { kind: 'scc' },
  heroFacts: [
    { v: '15', l: 'Operational sections' },
    { v: '10', l: 'Staff on base access' },
    { v: 'Unlimited', l: 'Jobs' },
    { v: 'Unlimited', l: 'Locations' },
  ],
  highlights: [
    'Bookings & scheduling', 'Customers & devices', 'Job workflows', 'Quotes & invoices',
    'Payments', 'Communications', 'Inventory & suppliers', 'Documents & compliance',
    'Employee management', 'Operational reporting',
  ],
  closing: {
    title: 'Run the whole business from one place.',
    text: 'One subscription covers every section, up to ten staff, with unlimited jobs and unlimited locations. Managed cloud backup and extra workforce capacity are optional extras, never requirements.',
  },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it actually does',
      title: 'One record, from the first phone call to the final payment',
      lead: 'Most service businesses run on a diary, a spreadsheet, an accounting package and a shoebox of job notes. SCC replaces that with a single workspace where the same record moves through every stage of the work.',
      blocks: [
        { t: 'statement', headline: 'The booking you take on Monday is the job on Tuesday, the invoice on Wednesday and the payment on Thursday - entered once.', items: [
          'When a booking is created, the customer, device, service, assigned technician and schedule are captured together. That information carries into the job, so nobody re-keys a phone number or re-describes a fault. The parts used come out of inventory, the customer gets the message from the same record, and the invoice is raised against the job that produced it.',
          'Because every section reads from the same records, the questions that normally cost a service business an afternoon - which jobs are overdue, what did we quote this customer last year, which device is still under warranty, who is free on Thursday, what stock did that repair consume - are answered inside the workspace instead of reconstructed from memory.',
        ] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Continuity', title: 'Nothing gets retyped', text: 'Customer, device, fault, service, technician and schedule are captured once at booking and follow the work through job, communications, invoice and payment.' },
          { sub: 'Memory', title: 'History stays with the record', text: 'Devices keep their fault history, repair history, warranty position, notes and documents, so returning work starts with context instead of guesswork.' },
          { sub: 'Ergonomics', title: 'Built for the counter and the van', text: 'Collapse the left navigation and the workspace expands for a laptop screen. Open the right options panel when you need deeper controls. From one laptop to a wide multi-monitor counter.' },
        ] },
      ],
    },
    {
      id: 'sections',
      eyebrow: 'The workspace',
      title: 'Fifteen operational sections, and what lives in each',
      lead: 'These are the real sections of the workspace and the real workspaces inside them - not a feature list written for a brochure.',
      blocks: [
        { t: 'groups', cols: 4, items: [
          { title: 'Overview', count: '8 workspaces', items: ['Overview', 'New Booking', 'New Customer', 'New Job', 'Find Customer', 'Find Job', 'Check Booking', 'View History'] },
          { title: 'Bookings', count: '42 workspaces', items: ['Operations', 'Scheduling', 'Lifecycle & Exceptions', 'Records', 'Financial', 'Reporting & Intelligence', 'Configuration'] },
          { title: 'Customers', count: '6 workspaces', items: ['Customers', 'New Customer', 'Customer Intake', 'Find Customer', 'Filter View', 'Export View', 'View History'] },
          { title: 'Devices', count: '6 workspaces', items: ['Devices', 'New Device', 'Device Intake', 'Find Device', 'Check Warranty', 'Filter View', 'Export View'] },
          { title: 'Jobs', count: '6 workspaces', items: ['Job Board', 'New Job', 'Fault Details', 'Job Details', 'Find Job', 'Export View', 'Print View'] },
          { title: 'Calendar', count: '5 workspaces', items: ['Calendar', "Today's Bookings", "Today's Jobs", 'New Booking', 'Booking Details', 'Check Booking'] },
          { title: 'Employees', count: '15 workspaces', items: ['Employee Directory', 'Roles & Permissions', 'Skills & Certifications', 'Availability & Leave', 'Scheduling', 'Time Tracking'] },
          { title: 'Communications', count: '5 workspaces', items: ['Communications', 'Send Message', 'Find Customer', 'Filter View', 'Refresh View', 'Export View'] },
          { title: 'Invoices', count: '6 workspaces', items: ['Invoices', 'New Invoice', 'Quote / Invoice', 'Find Job', 'Filter View', 'Export View', 'Print View'] },
          { title: 'Payments', count: '5 workspaces', items: ['Payments', 'Take Payment', 'Check Payment', 'Find Customer', 'Filter View', 'Export View'] },
          { title: 'Inventory', count: '5 workspaces', items: ['Inventory', 'Add Stock Item', 'Check Stock', 'Filter View', 'Export View', 'Print View'] },
          { title: 'Suppliers', count: '3 workspaces', items: ['Suppliers', 'Filter View', 'Export View', 'Refresh View'] },
          { title: 'Documents', count: '70 workspaces', items: ['Document Management', 'Assignments & Read Tracking', 'Collections & Packs', 'Compliance & Review', 'Knowledge Management', 'Import & Export'] },
          { title: 'Reports', count: '4 workspaces', items: ['Reports', 'Filter View', 'Export View', 'Print View', 'Refresh View'] },
          { title: 'Settings', count: '20 workspaces', items: ['Business Setup', 'Booking Engine', 'Libraries', 'Document Templates', 'Communications', 'Integrations & Audit'] },
        ] },
      ],
    },
    {
      id: 'bookings',
      eyebrow: 'Deep dive',
      title: 'Bookings is a system, not a calendar',
      lead: 'The booking area alone contains forty-two workspaces across seven groups - because taking the booking is the easy part, and everything that happens to it afterwards is where service businesses lose money.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Operations', title: 'The daily desk', text: 'Everything between the enquiry and the work being scheduled.', items: ['Booking Workspace', 'Pending Confirmations', 'Follow-Ups', 'Reminder Centre', 'Customer Requests', 'Wait List', 'Recurring Services', 'Filter & Print Bookings'] },
          { sub: 'Scheduling', title: 'Who goes where, and when', text: 'Turning confirmed bookings into a workable day for the people and vehicles you actually have.', items: ["Today's Schedule", 'Upcoming Schedule', 'Calendar View', 'Technician Operations', 'Capacity Planning', 'Route Planning', 'Resource Allocation'] },
          { sub: 'Lifecycle & exceptions', title: 'When plans go wrong', text: 'The workspaces that exist because real service work slips, clashes, escalates and gets cancelled.', items: ['Booking Health', 'Booking Priority', 'Booking SLA', 'Booking Fulfilment', 'Booking Dependencies', 'Escalations', 'Booking Conflicts', 'Reschedules', 'No Shows'] },
          { sub: 'Records', title: 'The paper trail', text: 'Everything attached to a booking so the history survives staff turnover and disputes.', items: ['Booking Details', 'Booking Communications', 'Booking Notes', 'Booking Assets', 'Booking Documents', 'Booking Timeline', 'Booking Activity'] },
          { sub: 'Reporting & intelligence', title: 'What the bookings are telling you', text: 'Booking financials and performance, so pricing and capacity decisions come from your own numbers.', items: ['Booking Financials', 'Booking Analytics', 'Booking Performance', 'Booking Intelligence', 'Service Categories', 'Export Bookings'] },
          { sub: 'Configuration', title: 'Rules that hold the diary together', text: 'Set the shape of a booking once and the whole system follows it.', items: ['Booking Templates', 'Service Duration Rules', 'Availability Rules', 'Booking Types & Statuses', 'Booking Fields & Workflows', 'Booking Notifications'] },
        ] },
      ],
    },
    {
      id: 'flow',
      eyebrow: 'How it works',
      title: 'How a job actually moves through SCC',
      lead: 'The same record, picked up by a different section at each stage of the work.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'The booking is taken', text: 'Customer, device, service, preferred time and assigned technician are captured together - at the counter, over the phone or through Quick Book.' },
          { n: '02', title: 'Customer and device go on file', text: 'Customer intake and device intake create the records that carry warranty position, fault history and past repairs from now on.' },
          { n: '03', title: 'The job is raised', text: 'The booking becomes a job on the Job Board with fault details, job details and a workflow status the rest of the team can see.' },
          { n: '04', title: 'Work is done, stock is used', text: 'Parts come out of inventory, suppliers are reordered from, and job notes, assets and documents attach to the record as work progresses.' },
          { n: '05', title: 'The customer is kept informed', text: 'Messages sent from Communications are logged against the booking, so the conversation lives with the job rather than in someone\u2019s phone.' },
          { n: '06', title: 'Quote or invoice is raised', text: 'The invoice is created against the job that produced it, with the service, parts and labour already known to the system.' },
          { n: '07', title: 'Payment is taken', text: 'Payments are recorded against the invoice and the customer, so outstanding work and outstanding money are the same picture.' },
          { n: '08', title: 'The business gets its numbers', text: 'Reporting, booking analytics and employee KPI tracking read from the real operational record rather than a separate spreadsheet.' },
        ] },
      ],
    },
    {
      id: 'setup',
      eyebrow: 'Business setup',
      title: 'Configured for your profession, not a generic template',
      lead: 'Most business software hands you an empty database and wishes you luck. SCC is one platform that already understands the kind of business you run: choose your profession and the same application adapts its terminology, services, booking structure, work subjects, faults, jobs and inventory context around the way that profession works.',
      blocks: [
        { t: 'callout', eyebrow: 'Choose your profession', title: PROFESSION_COUNT + ' profession operating packages, not cosmetic templates', text: 'Choosing a profession loads a researched operating package. It brings that profession\u2019s own services, devices, repairs, faults, booking categories, job categories, parts, accessories, consumables, inventory categories and search terms, and shapes terminology, booking questions, Quick Book, work subjects, diagnostics, the Profession Workspace and reporting context around it - so a computer repair shop opens to laptop screens and no-power faults while a dental equipment technician opens to something else entirely. Everything stays editable, so what ships is a starting point rather than a cage.', items: [
          'Services', 'Devices', 'Repairs', 'Faults', 'Booking categories', 'Job categories',
          'Parts', 'Accessories', 'Consumables', 'Inventory categories', 'Search terms',
        ] },
        { t: 'list', cols: 5, items: [...PROFESSION_ALL_NAMES] },
        { t: 'groups', cols: 4, items: [
          { title: 'Profession families', items: ['Computing & IT', 'Electronics & Specialist Repair', 'Networks & Security', 'AV & Smart Systems', 'Appliances', 'Mechanical Equipment', 'Mobility'] },
          { title: 'Profession families continued', items: ['Automotive-adjacent', 'Energy', 'Commercial Equipment', 'Medical / Laboratory Equipment', 'Industrial Equipment', 'Property & Field Service', 'Specialist Technical Services'] },
          { title: 'Also configured here', items: ['Locations for multi-site businesses', 'Booking types, statuses and fields', 'Booking workflows and notifications', 'Service, device, repair and custom libraries'] },
          { title: 'And here', items: ['Document templates and the document builder', 'SMS and email templates', 'Automation rules', 'Integrations and audit'] },
        ] },
      ],
    },
    {
      id: 'people',
      eyebrow: 'Your team',
      title: 'Employees, permissions and time',
      lead: 'Base access covers up to ten staff across unlimited locations, with fifteen workspaces dedicated to managing them.',
      blocks: [
        { t: 'groups', cols: 3, items: [
          { title: 'Who works here', items: ['Employee Directory', 'Employee Profiles', 'Roles', 'Permissions', 'Audit'] },
          { title: 'What they can do', items: ['Skills', 'Certifications', 'Training Records', 'Performance Reviews', 'KPI Tracking'] },
          { title: 'When they work', items: ['Availability', 'Leave Management', 'Scheduling', 'Time Tracking', 'Payroll Integration'] },
        ] },
        { t: 'para', items: [
          'Skills and certifications are not decoration - they sit next to availability and scheduling, so the person assigned to a booking is someone who can actually do that work and is actually free. Roles and permissions decide what each person sees, and the audit workspace records what changed.',
        ] },
      ],
    },
    {
      id: 'documents',
      eyebrow: 'Records & compliance',
      title: 'Seventy workspaces for the paperwork nobody wants to lose',
      lead: 'Policies, procedures, safety documents, contracts, training packs - and the evidence that your people have actually read them.',
      blocks: [
        { t: 'groups', cols: 5, items: [
          { title: 'Document management', items: ['All Documents', 'Recent Documents', 'Upload Centre', 'Create Document', 'Favourites', 'Search Documents'] },
          { title: 'Folders & tags', items: ['Folder Structure', 'Categories', 'Custom Folders', 'Archive', 'Tags'] },
          { title: 'Assignments', items: ['Role Assignments', 'Employee Assignments', 'Department & Team Assignments', 'Location Assignments', 'Mandatory Documents', 'Read Tracking', 'Acknowledgements'] },
          { title: 'Collections & packs', items: ['Document Collections', 'Onboarding Packs', 'Role Packs', 'Department Packs', 'Safety Packs', 'Training Packs'] },
          { title: 'Business documents', items: ['Policies', 'Procedures', 'Training', 'Manuals', 'Forms', 'Contracts', 'Compliance', 'HR', 'Safety', 'Operations'] },
          { title: 'Compliance & review', items: ['Compliance Dashboard', 'Approval Queue', 'Review Queue', 'Expiry Queue', 'Retention Queue', 'Outstanding Reads', 'Overdue Documents'] },
          { title: 'Knowledge management', items: ['Document Health', 'Knowledge Coverage', 'Process Coverage', 'Coverage Reports', 'Risk Areas', 'Documentation Gaps'] },
          { title: 'Reporting', items: ['Usage Reports', 'Read Reports', 'Acknowledgement Reports', 'Compliance Reports', 'Storage Reports', 'Department Reports'] },
          { title: 'Import & export', items: ['Import Centre', 'Migration Tools', 'Export Centre', 'Backup Exports', 'Compliance Exports', 'Audit Exports'] },
          { title: 'Administration', items: ['Permissions', 'Notification Rules', 'Notification History', 'Storage Usage', 'Version History', 'Audit History'] },
        ] },
      ],
    },
    {
      id: 'cloud',
      eyebrow: 'Optional extras',
      title: 'Managed cloud protection and extra workforce capacity',
      lead: 'Both are optional. Neither unlocks SCC functionality - the base subscription is the whole application.',
      blocks: [
        { t: 'tiers', cols: 4, items: [
          ...SCC_CLOUD_OPTIONS,
          { eyebrow: 'EXPAND BEYOND 10 STAFF', title: 'Workforce Extensions', price: '+$80 \u2013 $480', billing: '/mo', items: [...SCC_WORKFORCE_EXTENSIONS.map((tier) => `${tier.title} - ${tier.price}/mo (${tier.total} total)`), 'Total staff allowance only - no features unlocked'] },
        ] },
        { t: 'para', items: [
          'Cloud Services sit on top of whatever you already use - OneDrive, SharePoint, a NAS, a file server or your existing backups - and add VISINVOKE-managed backup, retention and recovery. Workforce Extensions do one thing: raise the total staff count. Between the base subscription and the largest extension, SCC covers solo operators through to fifty-person service teams.',
        ] },
      ],
    },
  ],
};

// ===========================================================================
// 2. VISINVOKE WEBSITE CAPTURE SUITE
// ===========================================================================
const WEBSITE_CAPTURE_SUITE = {
  key: 'website-capture-suite',
  backTo: BACK_PAID,
  category: 'Paid VISINVOKE Utility',
  eyebrow: 'PROFESSIONAL WEBSITE INTELLIGENCE PLATFORM',
  name: 'VISINVOKE Website Capture Suite',
  shortName: 'Website Capture Suite',
  icon: '\u{1F310}',
  accent: '#22d3ee',
  accent2: '#0891b2',
  tagline: 'Turn any website - or an entire portfolio of them - into a structured, searchable, AI-ready record instead of a folder of screenshots and half-remembered notes.',
  purpose: 'Website Capture Suite discovers, crawls, analyses and documents websites into a structured database built for auditing, migration, compliance, documentation, AI processing and long-term website management. Unlimited projects, unlimited websites, and a crawl you can actually configure, validate and repeat.',
  price: { main: '$14.99 USD', billing: 'Per Month', note: 'Unlimited projects \u00b7 unlimited websites \u00b7 ongoing platform updates' },
  status: { label: 'Available at launch', tone: '#22d3ee' },
  accessNote: 'Active paid product. Available to buy when VISINVOKE enables sales at launch.',
  action: { kind: 'disabled', label: 'Available at launch' },
  heroFacts: [
    { v: 'Unlimited', l: 'Projects' },
    { v: 'Unlimited', l: 'Websites' },
    { v: '16', l: 'Core capabilities' },
    { v: 'AI-ready', l: 'Structured output' },
  ],
  highlights: [
    'Unlimited Projects', 'Unlimited Websites', 'Recursive Crawling', 'Browser Rendering',
    'JavaScript Support', 'Crawl History', 'Crawl Validation', 'AI Ready',
  ],
  closing: {
    title: 'A website portfolio you can actually account for.',
    text: 'Every project, every site, every crawl - organised, validated and documented in one place, for a single monthly subscription with no per-site cost.',
  },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it actually does',
      title: 'Website work, organised as projects instead of ad-hoc crawls',
      lead: 'A project is the job: a redesign, an audit, a migration, a compliance review or ongoing site management. Websites are grouped under the project that gives them context, and everything captured stays attached to it.',
      blocks: [
        { t: 'statement', headline: 'Intelligent, browser-based discovery and recursive crawling that produces a structured record - not a pile of files you still have to interpret.', items: [
          'Most website work starts the same way: somebody needs to know what is actually on a site. Every page, every title, every canonical, every internal and external link, every piece of structured content - across a site that might be five pages or five thousand. Doing that by hand is why website audits, migrations and compliance reviews get quoted in weeks.',
          'Website Capture Suite renders pages in a real browser, follows links recursively, manages its own crawl queue, validates what it captured, and stores the result as structured website data. The same capture answers a migration question, an SEO question, a documentation question and an AI-ingestion question, because the underlying record is structured rather than a set of screenshots.',
        ] },
      ],
    },
    {
      id: 'overview',
      eyebrow: 'Overview',
      title: 'What the platform covers',
      lead: 'The full capability surface of the suite, from first discovery through to long-term archiving.',
      blocks: [
        { t: 'list', cols: 4, items: [
          'Browser-based website discovery', 'Recursive website crawling', 'Intelligent queue management', 'Crawl validation',
          'Structured website database', 'Website documentation', 'Crawl history', 'AI-ready structured capture',
          'Professional website auditing', 'Website inventory', 'Long-term website archiving', 'Migration planning',
          'Compliance reviews', 'Structured content capture', 'Browser rendering', 'Website analysis',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'Capabilities',
      title: 'Discovery, analysis, management and control',
      lead: 'Discovery finds the site, analysis reads it, management keeps the work organised, and control decides exactly how far a crawl is allowed to go.',
      blocks: [
        { t: 'groups', cols: 4, items: [
          { title: 'Discovery', text: 'Finding everything that is actually there, in the order that makes sense.', items: ['Intelligent Discovery', 'Recursive Crawling', 'Queue Management', 'Crawl Validation'] },
          { title: 'Analysis', text: 'Reading each page properly, including the parts that only exist after JavaScript runs.', items: ['Metadata Extraction', 'Canonical Detection', 'JavaScript Rendering', 'Crawl Reporting'] },
          { title: 'Management', text: 'Keeping multiple jobs and multiple sites separate, repeatable and comparable over time.', items: ['Multi Projects', 'Multi Websites', 'Saved Crawl Profiles', 'Crawl History'] },
          { title: 'Control', text: 'Deciding what is in scope before a crawl runs, rather than filtering the mess afterwards.', items: ['robots.txt Support', 'URL Include Rules', 'URL Exclude Rules', 'Internal / External Link Control'] },
        ] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Why browser rendering matters', title: 'It sees what a visitor sees', text: 'A simple fetcher reads the HTML the server sent. A rendered crawl sees the page after JavaScript has run - which on a modern site is often where the content, the navigation and the links actually live.' },
          { sub: 'Why validation matters', title: 'A crawl you can trust', text: 'Crawl validation exists so a capture can be relied on as evidence. An audit, a migration plan or a compliance review is only as good as the confidence that the crawl actually got everything.' },
          { sub: 'Why history matters', title: 'Sites change, records should not', text: 'Crawl history and saved crawl profiles mean the same site can be captured the same way again later, and the two captures compared - which is the difference between a snapshot and an inventory.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How it works',
      title: 'From a job to a documented record',
      lead: 'Four stages, repeatable across every site in the project.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Define the project', text: 'Start with the actual job - a redesign, an audit, a migration or ongoing site management - so every capture that follows has a purpose and a place to live.' },
          { n: '02', title: 'Arrange its websites', text: 'Group the relevant website locations under the project that gives them context. Both projects and websites are unlimited, so a whole client portfolio can sit in one structure.' },
          { n: '03', title: 'Set the capture scope', text: 'Decide what the crawl must cover before it runs: include and exclude rules, internal versus external link handling, robots.txt behaviour, and a saved profile you can reuse.' },
          { n: '04', title: 'Review the record', text: 'Work from the structured result - website inventory, extracted metadata, canonical positions, crawl reporting and documentation - with crawl history and validation behind it.' },
        ] },
        { t: 'split', items: [
          { title: 'What you bring to a capture', tone: 'info', items: ['Project purpose - the job the capture serves', 'Website locations - one site or a whole portfolio', 'Capture scope - include, exclude and link rules', 'Review intent - audit, migrate, document or archive'] },
          { title: 'What the capture gives back', tone: 'good', items: ['An organised project record', 'Structured website capture material', 'Reviewable documentation and crawl reporting', 'AI-ready structured content'] },
        ] },
      ],
    },
    {
      id: 'audience',
      eyebrow: 'Who it is for',
      title: 'Anyone who has to answer for what is on a website',
      lead: 'Developers and agencies use it before a rebuild. Compliance and government teams use it as evidence. Researchers and AI teams use it as a structured source.',
      blocks: [
        { t: 'list', cols: 5, items: [
          'Website Developers', 'Web Design Agencies', 'SEO Professionals', 'Digital Marketing', 'IT Consultants',
          'MSPs', 'Compliance Teams', 'Researchers', 'Website Owners', 'Corporate Organisations',
          'Government', 'Education', 'Security Professionals', 'Migration Specialists',
        ] },
        { t: 'cards', cols: 4, items: [
          { sub: 'Before a rebuild', title: 'Migration planning', text: 'Know exactly what exists, what it is called and where it points before anything moves. The inventory is the migration plan.' },
          { sub: 'Under review', title: 'Compliance reviews', text: 'A validated, repeatable capture of what a site actually published, with crawl history behind it.' },
          { sub: 'Over time', title: 'Long-term archiving', text: 'Keep a structured record of a site as it was, not a folder of screenshots nobody can search.' },
          { sub: 'Downstream', title: 'AI-ready capture', text: 'Structured content capture designed to be processed, not just read - so a site can become a dataset.' },
        ] },
      ],
    },
    {
      id: 'roadmap',
      eyebrow: 'Growing platform',
      title: 'Where the suite is going',
      lead: 'Planned platform growth, included as the suite evolves. These are direction, not shipped capability.',
      blocks: [
        { t: 'groups', cols: 4, hollow: true, items: [
          { title: 'Capture', items: ['Website Screenshots', 'Asset Capture', 'PDF Capture'] },
          { title: 'Analysis', items: ['CSS Analysis', 'JavaScript Analysis', 'Media Discovery'] },
          { title: 'Automation', items: ['Scheduled Crawls', 'Change Detection', 'Search Indexing'] },
          { title: 'Output', items: ['AI Dataset Builder', 'Export Centre', 'Reporting Suite'] },
        ] },
        { t: 'note', title: 'Release boundaries', text: 'Website Capture Suite is not released. Storage location, retention, deletion and data-processing terms, export file formats, supported browsers and operating systems, capture volume limits and the launch date are release decisions and are not published. There is no checkout, no Desktop Command Centre card and no launch target assigned yet. VISINVOKE Website Capture Suite and VISINVOKE Capture are separate products, not two names for the same thing.' },
      ],
    },
  ],
};

// ===========================================================================
// 3. VISINVOKE CONTENT PLANNER
// ===========================================================================
const CONTENT_PLANNER = {
  key: 'content-planner',
  backTo: BACK_PAID,
  category: 'Paid VISINVOKE Utility',
  eyebrow: 'CONTENT PLANNING & CLOUD PUBLISHING',
  name: 'VISINVOKE Content Planner',
  shortName: 'Content Planner',
  icon: '\u{1F4C5}',
  accent: '#f472b6',
  accent2: '#db2777',
  tagline: 'Plan a quarter of content in an afternoon, then shut your PC down and let it publish anyway.',
  purpose: 'Content Planner covers the whole content workflow - bulk import from documents and spreadsheets, a reusable content library, review and approval, campaigns, a scheduling calendar, native Windows media handling with rotation, and analytics. Committed schedules run on VISINVOKE cloud infrastructure, so publishing continues when your computer and DCC are completely switched off.',
  price: { main: '$5.99 \u2013 $8.99 USD', billing: 'Per Month', note: 'Two capacity tiers \u00b7 identical feature set', extra: 'Media+ adds cloud media capacity. It unlocks no extra features.' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Runs inside VISINVOKE DCC. Not yet released.',
  action: { kind: 'coming-soon', label: 'Coming Soon' },
  heroFacts: [
    { v: '15', l: 'Capabilities on both tiers' },
    { v: '5', l: 'Bulk import formats' },
    { v: '100 GB', l: 'Cloud media on Media+' },
    { v: 'PC-off', l: 'Scheduled publishing' },
  ],
  highlights: [
    'Bulk Import', 'DOCX Import', 'Spreadsheet Import', 'Content Library', 'Bulk Editing',
    'Review Workflow', 'Campaigns', 'Calendar', 'Saved Schedules', 'Evergreen Content',
    'Analytics', 'Media Rotation', 'Private Metadata Removal', 'PC-Off Publishing', 'All Supported Platforms',
  ],
  closing: {
    title: 'The whole workflow, not just a calendar.',
    text: 'Import, library, review, campaigns, scheduling, media rotation, privacy and analytics - identical on both tiers. The only difference is how much cloud media you need.',
  },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it actually does',
      title: 'The work between having content and having published it',
      lead: 'Content Planner is its own paid VISINVOKE product - not part of a bundle and not a free utility. It runs inside VISINVOKE DCC and covers the entire path from raw material to published post.',
      blocks: [
        { t: 'statement', headline: 'Most scheduling tools start at the calendar. This one starts at the pile of documents you already have.', items: [
          'The expensive part of content is rarely the posting. It is retyping ideas out of a document, hunting for the image that went with the third one, getting someone to approve it, remembering what was already published, and keeping a rhythm going when the week gets busy.',
          'Content Planner takes existing material in bulk from DOCX, TXT, Markdown, CSV and XLSX, turns it into a reusable library, moves it through review and approval, groups it into campaigns, places it on a calendar with collision checking, attaches media from your own Windows folders with automatic rotation, strips private metadata from every file by default, and then commits the schedule to the cloud so it runs whether or not your computer is on.',
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How it works',
      title: 'From a folder of documents to a published quarter',
      lead: 'Six stages. Every one of them exists because doing it manually is where content plans quietly die.',
      blocks: [
        { t: 'steps', cols: 3, items: [
          { n: '01', title: 'Import in bulk', text: 'Bring existing material in from DOCX, TXT, Markdown, CSV and XLSX instead of retyping it item by item. A quarter of planned content can arrive in one import.' },
          { n: '02', title: 'Build the library', text: 'Imported items become reusable library content, with bulk editing across metadata, approval state, attached media and scheduling - so changes happen to fifty items at once, not one at a time.' },
          { n: '03', title: 'Review and approve', text: 'Items move through defined workflow transitions with readable previews, so nothing reaches a destination before someone has actually signed it off.' },
          { n: '04', title: 'Plan and schedule', text: 'Group work into campaigns, place it on the calendar with collision checking, save reusable schedules, and keep evergreen content in the rotation instead of letting it expire.' },
          { n: '05', title: 'Attach and rotate media', text: 'Pull images and video from your own remembered Windows folders, set the rotation rules, and let the system precompute exactly which asset goes out with which post on which date.' },
          { n: '06', title: 'Commit and publish', text: 'Committed schedules run on VISINVOKE cloud infrastructure. Analytics report on what actually went out.' },
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'Capabilities',
      title: 'All fifteen, on both tiers',
      lead: 'Media+ is not a bigger edition. Every capability below is identical on Standard and Media+; only cloud and media capacity differ.',
      blocks: [
        { t: 'groups', cols: 4, items: [
          { title: 'Import', text: 'Get existing material in without retyping it.', items: ['Bulk Import', 'DOCX Import', 'Spreadsheet Import', 'DOCX / TXT / Markdown / CSV / XLSX'] },
          { title: 'Library & editing', text: 'Treat content as reusable assets rather than one-off posts.', items: ['Content Library', 'Bulk Editing', 'Bulk metadata & approval', 'Readable previews'] },
          { title: 'Planning', text: 'Decide what goes out, when, and as part of what.', items: ['Campaigns', 'Calendar with collision checks', 'Saved Schedules', 'Evergreen Content', 'Review Workflow'] },
          { title: 'Publishing & measurement', text: 'Deliver it, and find out what happened.', items: ['PC-Off Publishing', 'All Supported Platforms', 'Media Rotation', 'Private Metadata Removal', 'Analytics'] },
        ] },
      ],
    },
    {
      id: 'plans',
      eyebrow: 'Plans & capacity',
      title: 'Two tiers, one feature set',
      lead: 'Because scheduled publishing runs in the cloud, a larger media library genuinely costs more to carry. That is the only thing the tiers change.',
      blocks: [
        { t: 'tiers', cols: 2, items: [
          { eyebrow: 'COMPLETE CONTENT PLANNER', title: 'Content Planner Standard', price: '$5.99', billing: '/mo', capacity: 'Around 10 GB cloud media', items: ['All 15 capabilities', '1 GB source planning ceiling per file', 'Cloud-backed scheduled publishing', 'Full media rotation and privacy handling'] },
          { eyebrow: 'COMPLETE CONTENT PLANNER - MORE CLOUD MEDIA', title: 'Content Planner Media+', price: '$8.99', billing: '/mo', capacity: 'Around 100 GB cloud media', recommended: true, flag: 'More media', items: ['All 15 capabilities - identical to Standard', '5 GB source planning ceiling per file', 'Cloud-backed scheduled publishing', 'For video-heavy and multi-destination libraries'] },
        ] },
        { t: 'facts', cols: 4, items: [
          { label: 'Google Business Profile', value: 'Verification required' },
          { label: 'Facebook', value: 'Verification required' },
          { label: 'Instagram', value: 'Verification required' },
          { label: 'X', value: 'Not configured' },
        ] },
        { t: 'note', title: 'Current state', text: 'Cloud media capacities are launch-planning values held in the product configuration, not provider guarantees, and accepting a large source file never promises that a destination will accept it. The application - persistence, imports, workflow, calendar, media processing and rotation - is locally implemented and tested. The cloud publishing infrastructure is built but not yet deployed or independently proven, and no destination integration is proven yet. Content Planner is not released and there is no checkout on this page.' },
      ],
    },
  ],
};

// ===========================================================================
// 4. MANAGED PC PROTECTION
// ===========================================================================
const MANAGED_PC_PROTECTION = {
  key: 'managed-pc-protection',
  backTo: BACK_SEC,
  category: 'VISINVOKE Security Service',
  eyebrow: 'MANAGED PROTECTION',
  name: 'Managed PC Protection',
  shortName: 'Managed PC Protection',
  icon: '\u{1F6E1}\uFE0F',
  accent: '#16a34a',
  accent2: '#15803d',
  tagline: 'Serious computer protection that VISINVOKE manages, monitors and keeps working - so you never have to think about it again.',
  purpose: 'This is a managed service, not a product you install and forget. You enrol through your VISINVOKE account, and VISINVOKE then manages every layer, continuously monitors that it stays active, and keeps it updated and supported from one account.',
  price: { main: '$9.99 USD', billing: '/ month per PC', note: 'Per active Windows PC, per month', extra: 'No setup fee. Each protected PC is billed at US$9.99 per month.' },
  status: { label: 'Available now', tone: '#34d399' },
  accessNote: 'Arranged by enquiry. Your email opens with the details pre-filled.',
  action: {
    kind: 'mailto',
    label: 'Get Protection',
    email: SECURITY_ENQUIRY_EMAIL,
    subject: 'Managed PC Protection - Enquiry',
    body: 'Hello VISINVOKE,\n\nI would like to get Managed PC Protection for my computer(s).\n\nNumber of Computers:\n\nCountry:\n\nAdditional Information:\n\nThank you.\n',
  },
  heroFacts: [
    { v: '8', l: 'Protection layers' },
    { v: 'Managed', l: 'End to end by VISINVOKE' },
    { v: 'Monitored', l: 'Continuously' },
    { v: 'Included', l: 'Email support' },
  ],
  highlights: [
    'Professional-grade protection', 'Self-service enrolment', 'Professionally managed',
    'Continuous monitoring', 'Automatic updates', 'Email support included',
    'One VISINVOKE account', 'Personal, professional & business',
  ],
  closing: {
    title: 'Protect your devices, your data and your digital life.',
    text: 'Get serious, professionally managed protection that is monitored and looked after for you - all from your VISINVOKE account.',
  },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'Professional protection, managed for you',
      lead: 'VISINVOKE sets up, monitors, updates and maintains a complete protection stack on your enrolled Windows PC, so you get serious security without managing multiple products yourself.',
      blocks: [
        { t: 'para', items: [
          'You enrol through your VISINVOKE account and VISINVOKE configures every layer, confirms it is active, keeps it updated and continuously monitors that it stays on - all from one account, with email support included.',
        ] },
      ],
    },
    {
      id: 'included',
      eyebrow: "What's included",
      title: 'Eight protection layers, one managed service',
      blocks: [
        { t: 'cards', cols: 4, items: [
          { sub: 'Layer 01', title: 'Malware Protection', text: 'Blocks viruses, trojans, spyware and other malicious software before it can run.' },
          { sub: 'Layer 02', title: 'Threat Detection', text: 'Spots suspicious and known-dangerous activity early, before it causes harm.' },
          { sub: 'Layer 03', title: 'Device Security', text: 'Hardens your computer against compromise and unsafe software.' },
          { sub: 'Layer 04', title: 'Data Protection', text: 'Guards personal and business information against theft and ransomware.' },
          { sub: 'Layer 05', title: 'Security Monitoring', text: 'Continuous monitoring confirms protection stays enabled and healthy.' },
          { sub: 'Layer 06', title: 'Safe Computing', text: 'Steers you away from dangerous websites, downloads and email links.' },
          { sub: 'Layer 07', title: 'Ongoing Protection', text: 'Automatic updates keep defences effective against new threats.' },
          { sub: 'Layer 08', title: 'Managed Support', text: 'Set up, maintained and supported by VISINVOKE, with email support included.' },
        ] },
      ],
    },
    {
      id: 'setup',
      eyebrow: 'How it works',
      title: 'A simple, self-service enrolment',
      blocks: [
        { t: 'steps', cols: 6, items: [
          { n: '1', title: 'Create your account', text: 'A free VISINVOKE account.' },
          { n: '2', title: 'Open VISINVOKE Security', text: 'The Security area in your dashboard.' },
          { n: '3', title: 'Choose the service', text: 'Select Managed PC Protection.' },
          { n: '4', title: 'Enrol your PC', text: 'Contact VISINVOKE through your account to enrol.' },
          { n: '5', title: 'VISINVOKE sets it up', text: 'We configure and activate the managed service.' },
          { n: '6', title: 'Protection stays managed', text: 'It goes live and is monitored and maintained.' },
        ] },
      ],
    },
    {
      id: 'plans',
      eyebrow: 'Plan & where it runs',
      title: 'One ongoing service, billed per PC',
      blocks: [
        { t: 'facts', cols: 4, items: [
          { label: 'Price', value: '$9.99 USD / month per PC' },
          { label: 'Setup fee', value: 'None' },
          { label: 'Account', value: 'VISINVOKE account required' },
          { label: 'Runs through', value: 'VISINVOKE Security / DCC' },
        ] },
        { t: 'list', cols: 3, items: ['Professional-grade protection', 'Professionally managed', 'Continuous monitoring', 'Automatic updates', 'Email support included', 'No setup fee'] },
      ],
    },
  ],
};

// ===========================================================================
// 5. TRACK, LOCK & WIPE
// ===========================================================================
const TRACK_LOCK_WIPE = {
  key: 'track-lock-wipe',
  backTo: BACK_SEC,
  category: 'VISINVOKE Security Service',
  eyebrow: 'LOSS & THEFT PROTECTION',
  name: 'Track, Lock & Wipe',
  shortName: 'Track, Lock & Wipe',
  icon: '\u{1F512}',
  accent: '#2563eb',
  accent2: '#1d4ed8',
  tagline: 'Prepare the response before the device goes missing - because the moment it does, the information on it matters more than the hardware.',
  purpose: 'Track, Lock & Wipe is a managed service pathway for protecting the information on a lost or stolen configured device. The supported controls are prepared in advance through your VISINVOKE account, and VISINVOKE then assists with location, access restriction or wipe when the device and its connection permit them.',
  price: { main: '$9.99 USD', billing: '/ month per PC', note: 'Per active Windows PC, per month', extra: 'No setup fee. Each protected PC is billed at US$9.99 per month.' },
  status: { label: 'Available now', tone: '#34d399' },
  accessNote: 'Arranged by enquiry. Your email opens with the details pre-filled.',
  action: {
    kind: 'mailto',
    label: 'Get Protection',
    email: SECURITY_ENQUIRY_EMAIL,
    subject: 'Track, Lock & Wipe - Enquiry',
    body: 'Hello VISINVOKE,\n\nI would like to get the Track, Lock & Wipe service for my computer(s).\n\nNumber of Computers:\n\nCountry:\n\nAdditional Information:\n\nThank you.\n',
  },
  heroFacts: [
    { v: '3', l: 'Prepared response actions' },
    { v: 'Verified', l: 'Setup before you need it' },
    { v: 'Managed', l: 'Assistance pathway' },
    { v: 'Included', l: 'Email support' },
  ],
  highlights: [
    'Location assistance', 'Remote lock', 'Remote wipe', 'Prepared device controls',
    'Setup verified first', 'Managed assistance pathway', 'DCC Security category', 'Email support through VISINVOKE',
  ],
  closing: {
    title: 'A known setup, a known monthly cost, and a connected support path.',
    text: 'The service stays inside the same VISINVOKE account, Security family and support environment - rather than leaving you with an unexplained standalone control you have never tested.',
  },
  sections: [
    {
      id: 'actions',
      eyebrow: 'Three distinct actions',
      title: 'Locate. Restrict. Remove.',
      lead: 'Each control is qualified by what the configured device, its operating environment and its current connection can support. No single action is presented as universally available.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: '1 \u00b7 Locate', title: 'Location assistance', text: 'Use the configured service information to help understand where a registered device may be. Location information is an aid to your decision, not a recovery guarantee - it tells you whether this looks like a device left at a customer site or one that has genuinely gone.', items: ['Uses the configured service information', 'Informs which response is appropriate', 'Does not guarantee physical recovery'] },
          { sub: '2 \u00b7 Restrict access', title: 'Remote lock', text: 'Where the device is supported and reachable, the applicable lock or access-restriction action is sent to reduce unauthorised use. This is the proportionate response when the device may still come back.', items: ['Reduces unauthorised use', 'Appropriate when recovery is still likely', 'Depends on support and reachability'] },
          { sub: '3 \u00b7 Protect data', title: 'Remote wipe', text: 'Where the device is supported and reachable, the configured wipe capability is used when protecting the information becomes more important than retaining the contents of the device.', items: ['For when the data outweighs the device', 'Uses the capability prepared at setup', 'Depends on support and reachability'] },
        ] },
      ],
    },
    {
      id: 'setup',
      eyebrow: 'Professional setup',
      title: 'Preparation is part of the service, not an afterthought',
      lead: 'The one-time setup is where device suitability and supported controls are established. VISINVOKE configures the applicable service through your account and confirms the prepared state with you.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '1', title: 'Review the device', text: 'Confirm the relevant device, its platform and what you actually need protected - before anything is configured.' },
          { n: '2', title: 'Enrol the device', text: 'Enrol the device through the central VISINVOKE journey.' },
          { n: '3', title: 'Configure supported controls', text: 'Prepare the available tracking, lock and wipe capabilities for that specific device.' },
          { n: '4', title: 'Check readiness', text: 'Review together what is configured, what depends on connectivity, and exactly how to request assistance if the device goes missing.' },
        ] },
      ],
    },
    {
      id: 'plans',
      eyebrow: 'Current service pricing',
      title: 'One clear monthly cost per PC',
      lead: 'A single ongoing monthly amount of US$9.99 per active Windows PC. Professional setup and readiness work is included, with no separate setup fee.',
      blocks: [
        { t: 'list', cols: 3, items: ['Prepared supported controls', 'Managed assistance pathway', 'Security-family and DCC representation', 'Email support through VISINVOKE', 'No setup fee'] },
        { t: 'facts', cols: 2, items: [
          { label: 'What the monthly fee covers', value: 'The ongoing Track, Lock & Wipe service on one Windows PC' },
          { label: 'Setup fee', value: 'None. Setup is included in the service' },
          { label: 'Multi-device pricing', value: 'US$9.99 per active Windows PC, per month' },
          { label: 'Device / platform suitability', value: 'Confirmed during the service journey' },
        ] },
        { t: 'note', title: 'Technical boundaries', text: 'Device and platform compatibility, registration state, connectivity and the controls prepared during setup all affect which actions can complete. No offline-wipe, always-on-tracking, every-device or every-operating-system promise is made, and physical recovery is never guaranteed - the core purpose is information protection and access control. Final device and platform suitability is confirmed during the service journey rather than assumed here.' },
      ],
    },
  ],
};

// ===========================================================================
// 6. VERIDIAN
// ===========================================================================
const VERIDIAN = {
  key: 'veridian',
  backTo: BACK_RPG,
  category: 'VISINVOKE RPG World',
  eyebrow: 'PERSISTENT SOLO-FIRST FANTASY',
  name: 'Veridian',
  shortName: 'Veridian',
  icon: '\u2694\uFE0F',
  accent: '#7c3aed',
  accent2: '#be123c',
  tagline: 'A living world shaped by what your party leaves behind.',
  purpose: 'Veridian is a persistent fantasy world designed around one player guiding up to four characters through exploration, factions, settlements, hard choices and long-form consequence. Not a blank chatbot adventure - a structured world built to remember what you did in it.',
  price: { main: '$14.99 USD', billing: '/ month', note: 'One world', extra: '$24.95 USD/mo for Veridian and CyberTech together - planned future pricing, no checkout.' },
  status: { label: 'In development', tone: '#fbbf24' },
  accessNote: 'Early 2027 programme target. Not a guaranteed date.',
  action: { kind: 'disabled', label: 'Launch Veridian', pill: 'Due Early 2027' },
  heroFacts: [
    { v: '1', l: 'Player' },
    { v: 'Up to 4', l: 'Characters in your party' },
    { v: 'World-first', l: 'Simulation design' },
    { v: 'Early 2027', l: 'Programme target' },
  ],
  highlights: [
    'Solo-first party play', 'Up to four characters', 'World simulation first', 'Faction relationships',
    'Settlements that change', 'Long-form consequence', 'Party campaign history', 'No multiplayer required',
  ],
  closing: {
    title: 'A world worth returning to.',
    text: 'Follow Veridian as the persistent foundation is built. Access opens when the world engine and its release evidence are ready - not before.',
  },
  sections: [
    {
      id: 'world',
      eyebrow: 'The world comes first',
      title: 'Move through a realm that is designed to remember',
      lead: "Veridian's defining direction is not a blank chatbot adventure. It is a structured fantasy world where the systems are planned to retain party history, shift faction relationships, preserve consequences and carry progression from one session into the next.",
      blocks: [
        { t: 'statement', headline: 'Most solo RPG experiences reset the world the moment you close them. Veridian is built on the opposite premise.', items: [
          'The design goal is continuity: a settlement you helped should still show it, a faction you crossed should still remember, and an unresolved thread should still be waiting. Exploration is meant to feed the campaign state rather than evaporate at the end of a scene, and your party\u2019s shared history is meant to become part of the world rather than a save file you eventually abandon.',
          'That is the vision, and it is why the world engine is being built carefully rather than quickly. What follows describes the approved design direction - the systems below are the map being built to, not systems you can play today.',
        ] },
        { t: 'steps', cols: 4, items: [
          { n: '01 / EXPLORE', title: 'Cross regions with intent', text: 'Travel, discovery and investigation are designed to feed the campaign state rather than disappear after a scene.' },
          { n: '02 / CHOOSE', title: 'Leave consequences', text: 'Decisions are planned to influence relationships, reputation, unresolved threads and the state of the world itself.' },
          { n: '03 / RETURN', title: 'Find a changed context', text: 'The persistent-world vision is for factions, settlements and story threads to retain what happened before you left.' },
          { n: '04 / BECOME', title: 'Build a party history', text: 'Characters grow through extended play while their shared campaign record becomes part of the world around them.' },
        ] },
      ],
    },
    {
      id: 'party',
      eyebrow: 'One player, up to four characters',
      title: 'Lead the whole party, not just a single avatar',
      lead: "Veridian's approved control model is solo-first. You can guide one main character or build a party of up to four, carrying different roles, equipment, relationships and risks through the same campaign.",
      blocks: [
        { t: 'cards', cols: 2, items: [
          { sub: 'The control model', title: 'A party you actually command', text: 'Individual growth is designed to sit inside a collective party history, so the group has a story and not just four stat blocks.', items: ['One player owns the campaign direction and party decisions', 'Up to four characters share the persistent world', 'Different roles, equipment, relationships and risks', 'No multiplayer or live co-operative mode is promised'] },
          { sub: 'The play cycle', title: 'Explore. Decide. Endure. Return.', text: 'The planned loop joins discovery to consequence: venture out, meet people and factions, decide under pressure, live with what follows, then come back to a world that carries the record forward.', items: ['01 Read the world - location, pressure and opportunity', '02 Choose together - party roles, risks and trade-offs', '03 Resolve outcomes - success, failure and continuation', '04 Persist the record - progress, memory and consequence'] },
        ] },
      ],
    },
    {
      id: 'pillars',
      eyebrow: 'World pillars',
      title: 'What the world is built out of',
      lead: 'The Veridian architecture describes a connected foundation of world state, memory, identity, progression, NPC, faction, quest and consequence systems.',
      blocks: [
        { t: 'groups', cols: 4, items: [
          { title: 'Factions', text: 'Relationships that shift with what you actually did, not a reputation bar that resets.', items: ['Faction relationships', 'Reputation across groups', 'Consequences that carry'] },
          { title: 'Settlements', text: 'Places designed to retain their state between visits.', items: ['Settlement state', 'Changed context on return', 'Somewhere to come back to'] },
          { title: 'Threads', text: 'Story that does not quietly disappear when you walk away from it.', items: ['Unresolved threads', 'Quest and consequence systems', 'World memory'] },
          { title: 'Progression', text: 'Long-form growth measured in campaigns rather than sessions.', items: ['Long-form progression', 'Party campaign history', 'Identity and character growth'] },
        ] },
        { t: 'split', items: [
          { title: 'Defined - current product direction', tone: 'good', items: ['Persistent solo-first fantasy world', 'Up-to-four-character party model', 'Long-form progression and world memory', 'Factions, settlements and consequence as pillars'] },
          { title: 'In development - engine and release proof', tone: 'warn', items: ['Production world-state and save services', 'Operational NPC and faction simulation', 'Released campaign content and balance', 'Live launch, support and entitlement flows'] },
        ] },
        { t: 'note', title: 'How to read this page', text: 'The Veridian architecture documents are a build map, not a statement that every listed subsystem already operates. The world continuing to move while you are away is part of the persistent-world design direction, not a currently verified behaviour - production world-state scheduling and simulation must be implemented and tested before that is claimed as something the product does.' },
      ],
    },
  ],
};

// ===========================================================================
// 7. CYBERTECH
// ===========================================================================
const CYBERTECH = {
  key: 'cybertech',
  backTo: BACK_RPG,
  category: 'VISINVOKE RPG World',
  eyebrow: 'PERSISTENT SINGLE-CHARACTER CYBERPUNK',
  name: 'CyberTech',
  shortName: 'CyberTech',
  icon: '\u{1F916}',
  accent: '#2563eb',
  accent2: '#06b6d4',
  tagline: 'One identity. Rebuilt for every mission.',
  purpose: 'CyberTech is a persistent cyberpunk world built around a single character moving through cities, corporations, syndicates and technology. Inserted gems reconfigure that character for entirely different specialist roles - without discarding their body history, reputation, profession, injuries or choices.',
  price: { main: '$14.99 USD', billing: '/ month', note: 'One world', extra: '$24.95 USD/mo for CyberTech and Veridian together - planned future pricing, no checkout.' },
  status: { label: 'In development', tone: '#fbbf24' },
  accessNote: 'Early 2027 programme target. Not a guaranteed date.',
  action: { kind: 'disabled', label: 'Launch CyberTech', pill: 'Due Early 2027' },
  heroFacts: [
    { v: '1', l: 'Persistent character' },
    { v: '12', l: 'Initial towns and cities' },
    { v: '6', l: 'Environment types' },
    { v: 'Early 2027', l: 'Programme target' },
  ],
  highlights: [
    'One persistent character', 'Role-changing gems', 'Implants as real systems', 'Power, heat & medical limits',
    'Professions and access', 'Corporations & syndicates', 'Command vehicle progression', 'Consequence that persists',
  ],
  closing: {
    title: 'One character. A permanent record.',
    text: 'Follow CyberTech as the persistent systems come online. Access opens when the engine, content and release evidence are ready.',
  },
  sections: [
    {
      id: 'identity',
      eyebrow: 'Character-evolution-first',
      title: 'Change the role. Keep the person.',
      lead: 'CyberTech does not ask one player to manage a party. The approved architecture keeps one character as the complete long-term identity, and changes what that identity can do rather than replacing it.',
      blocks: [
        { t: 'statement', headline: 'Most RPGs make you roll a new character to try a new role. CyberTech makes you rebuild the one you already have - and live with everything that happened to them.', items: [
          'Inserted gems are planned to reconfigure the same character\u2019s operating profile: heavy weapons, light flight, cyber systems, medicine, engineering or another approved specialisation. What does not reset is the person underneath - their body history, the implants already installed, the professional access they earned, the debts they took on, the injuries they carry and the reputation they built with corporations and syndicates.',
          'That is the design centre of the world. The engine is still being built, so what follows is the approved design direction rather than systems that can be played today.',
        ] },
        { t: 'steps', cols: 4, items: [
          { n: '01 / ORIGIN', title: 'Begin with a history', text: 'City, profession, social position, loyalties and tolerance for invasive technology shape the starting identity before the first mission.' },
          { n: '02 / CONFIGURE', title: 'Insert a specialist gem', text: 'Move the same character toward heavy weapons, light flight, cyber systems, medicine, engineering or another approved profile.' },
          { n: '03 / OPERATE', title: 'Meet mission constraints', text: 'Technology, power, heat, equipment and learned skill are designed to determine what the active role can really do on the ground.' },
          { n: '04 / PERSIST', title: 'Carry the consequences', text: 'Body changes, reputation, injuries, debts and mission outcomes are intended to remain part of the same identity, permanently.' },
        ] },
      ],
    },
    {
      id: 'tech',
      eyebrow: 'Technology with limits',
      title: 'Implants are systems, not cosmetics',
      lead: 'The working architecture treats implants, chips and gems as functional systems with real costs - so capability comes with trade-offs instead of an unlimited hardware stack.',
      blocks: [
        { t: 'cards', cols: 4, items: [
          { sub: 'Body', title: 'Locations and compatibility', text: 'Implants belong to specific body locations under compatibility rules. What you install closes off other options.' },
          { sub: 'Skill', title: 'Hardware does not replace learning', text: 'Chips and gems are planned to add capability without erasing the value of learned skill - the character still has to be good at something.' },
          { sub: 'Cost', title: 'Power, heat and medical stability', text: 'Planned constraints on what a body can run at once. More capability is not automatically better if the system cannot carry it.' },
          { sub: 'Access', title: 'Facilities and specialists', text: 'Installation, repair and recovery require the right facilities and the right people - which is itself a reason to care about where you are and who you know.' },
        ] },
        { t: 'split', items: [
          { title: 'Defined - progression direction', tone: 'good', items: ['One character and one evolving body record', 'Gems, implants, chips and professions', 'Vehicle acquisition and staged expansion', 'Corporation, city and syndicate consequence'] },
          { title: 'In development - engine and release proof', tone: 'warn', items: ['Operational missions and mechanical resolution', 'Production installation, power and heat systems', 'Offline world-state and memory services', 'Released vehicle modules, balance and content'] },
        ] },
      ],
    },
    {
      id: 'world',
      eyebrow: 'A living network',
      title: 'Cities, corporations and syndicates remember',
      lead: 'The world direction extends well beyond a mission board. Twelve initial towns and cities and six broad environmental types appear in the working architecture.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Take a contract', text: 'Read the location, the faction position and the current mission state before committing to anything.' },
          { n: '02', title: 'Configure the role', text: 'Choose the gems, equipment and approach that suit this job - knowing the choice has a cost.' },
          { n: '03', title: 'Resolve the operation', text: 'Pay the costs, accept the risks, and continue afterwards even when the operation did not go to plan.' },
          { n: '04', title: 'Write the history', text: 'Persist the body changes, the reputation shift and the world changes into the same permanent record.' },
        ] },
        { t: 'callout', eyebrow: 'Long-term progression anchor', title: 'From a vehicle to a mobile headquarters', text: 'A central progression vision is an armoured command carrier that grows from basic transport into a workshop, a medical facility, a gem laboratory, a vehicle carrier and a deployable multi-room base. It is a documented design anchor for the long campaign rather than a shipped feature - exact modules, the acquisition path and the gameplay around it remain to be built.', items: [
          'Workshop', 'Medical facility', 'Gem laboratory', 'Vehicle carrier', 'Deployable multi-room base',
        ] },
        { t: 'note', title: 'How to read this page', text: 'The CyberTech source material is a working architecture and development base. It does not prove that missions, offline simulation, cities, corporations, implants, combat, save or guidance systems currently operate. Final names, scale, behaviour and production content remain open, and controlled offline world progression is part of the architecture rather than a proven service.' },
      ],
    },
  ],
};

const BACKUP_VERIFICATION_CLOUD = {
  key: 'backup-verification-cloud',
  backTo: BACK_PAID,
  category: 'Paid VISINVOKE Utility',
  eyebrow: 'IT OPERATIONS · BACKUP, RECOVERY AND RESILIENCE',
  name: 'Backup Verification Cloud',
  shortName: 'Backup Verification Cloud',
  icon: '☁️',
  accent: '#60a5fa',
  accent2: '#2563eb',
  tagline: 'Know your backups can actually recover the business, not just that a job went green.',
  purpose: 'Backup Verification Cloud restores selected recovery points into approved test locations, validates the recovered content and keeps clear evidence of what passed, what failed and whether recovery objectives were met. Heavy verification runs on your approved computer or runner while VISINVOKE Cloud coordinates schedules, alerts and shared history.',
  price: { main: '$29.99 USD', billing: 'Per Month / Company', note: 'Unlimited authorised company users' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Runs in a dedicated desktop window through VISINVOKE DCC. Planned product, not yet released.',
  action: { kind: 'coming-soon', label: 'Coming Soon' },
  heroFacts: [
    { v: '4', l: 'Capacity tiers' },
    { v: 'Company', l: 'Unlimited authorised users' },
    { v: 'Local', l: 'Verification on your computer' },
    { v: 'Due 2027', l: 'Planned release' },
  ],
  highlights: ['Restore rehearsals', 'File and database checks', 'RPO and RTO outcomes', 'Signed evidence', 'Multi-provider', 'Local verification', 'Audit-ready reports', 'Runs via DCC'],
  closing: { title: 'A backup you have tested is the only backup you can trust.', text: 'Rehearse real restores, measure the outcomes and keep the evidence, so a recovery is something you have already proven rather than something you hope will work.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'Proof that a recovery would actually work',
      lead: 'A green backup job tells you a copy was written. It does not tell you the copy can be restored, opened and used.',
      blocks: [
        { t: 'statement', headline: 'The worst time to discover a backup cannot be restored is the day you need it.', items: [
          'Most backup tools report on the job that wrote the copy, not on whether that copy can be recovered into something usable. The gap between a successful backup and a successful recovery is exactly where businesses get caught out.',
          'Backup Verification Cloud closes that gap. It restores selected recovery points into approved test locations, validates the recovered content, measures how quickly and how completely recovery happened, and keeps durable evidence of the result.',
        ] },
        { t: 'para', items: [
          'Verification runs on your own approved computer or a dedicated runner, so backup payloads stay in your controlled environment. VISINVOKE Cloud coordinates the schedules, the alerts and the shared history, and produces audit-ready reports across multiple backup providers.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'Key capabilities',
      title: 'What the product gives you',
      lead: 'Every capability is aimed at one thing: turning an untested backup into a proven recovery.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Rehearsals', title: 'Controlled restore rehearsals', text: 'Restore selected recovery points into approved, isolated test locations instead of trusting a job status that has never actually been recovered.' },
          { sub: 'Coverage', title: 'Files, databases, disks and apps', text: 'Validate files, archives, databases, disks and supported application runbooks, so the check matches what you would really need to bring back.' },
          { sub: 'Objectives', title: 'RPO and RTO outcomes', text: 'Measure recovery point and recovery time outcomes and expose protection that has gone stale before it becomes a problem.' },
          { sub: 'Evidence', title: 'Signed, audit-ready records', text: 'Keep signed evidence, exceptions, cleanup records and audit-ready reports of what passed and what failed.' },
          { sub: 'Coordination', title: 'Multiple providers, one view', text: 'Coordinate several backup providers and local runners together, without sending backup payloads to VISINVOKE Cloud.' },
          { sub: 'Safety', title: 'Isolated and clean', text: 'Rehearsals run in approved test locations and clean up after themselves, so verification never touches live systems.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A clear path from setup to proof',
      lead: 'The workflow is deliberately short, so the work stays with the people who own it rather than with the software.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Select recovery points', text: 'Choose the backups and recovery points to rehearse from your existing providers.' },
          { n: '02', title: 'Restore to a test location', text: 'Restore into an approved, isolated test location rather than over anything live.' },
          { n: '03', title: 'Validate the content', text: 'Validate files, archives, databases, disks and supported runbooks, and measure RPO and RTO.' },
          { n: '04', title: 'Keep the evidence', text: 'Record what passed, what failed, exceptions and cleanup, and produce an audit-ready report.' },
        ] },
      ],
    },
    {
      id: 'plans',
      eyebrow: 'Plans and status',
      title: 'Four capacity tiers, one feature set',
      lead: 'Pricing is per company per month with unlimited authorised company users. The tiers change capacity, not the capabilities.',
      blocks: [
        { t: 'tiers', cols: 4, items: [
          { eyebrow: 'INVOKE', title: 'INVOKE plan', price: '$29.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Restore rehearsals and validation', 'RPO and RTO measurement', 'Signed evidence and reports', 'More capacity at each tier'] },
          { eyebrow: 'FORGE', title: 'FORGE plan', price: '$59.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Restore rehearsals and validation', 'RPO and RTO measurement', 'Signed evidence and reports', 'More capacity at each tier'] },
          { eyebrow: 'NEXUS', title: 'NEXUS plan', price: '$129.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Restore rehearsals and validation', 'RPO and RTO measurement', 'Signed evidence and reports', 'More capacity at each tier'] },
          { eyebrow: 'APEX', title: 'APEX plan', price: '$249.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Restore rehearsals and validation', 'RPO and RTO measurement', 'Signed evidence and reports', 'More capacity at each tier'] },
        ] },
        { t: 'facts', cols: 3, items: [
          { label: 'Billing', value: 'Per company, per month' },
          { label: 'Authorised users', value: 'Unlimited company users' },
          { label: 'Planned release', value: 'Due 2027' },
        ] },
        { t: 'note', title: 'Current status', text: 'This is a planned VISINVOKE product with a target of 2027. The pricing shown is planned pricing held in the product configuration, not a current offer, and there is no checkout on this page. A permanent sample workspace is planned so the product can be explored before it is released.' },
      ],
    },
  ],
};

const CONTRACTOR_COMPLIANCE_VAULT = {
  key: 'contractor-compliance-vault',
  backTo: BACK_PAID,
  category: 'Paid VISINVOKE Utility',
  eyebrow: 'COMPLIANCE AND WORKFORCE OPERATIONS',
  name: 'Contractor Compliance Vault',
  shortName: 'Contractor Compliance Vault',
  icon: 'CCV',
  accent: '#2dd4bf',
  accent2: '#0d9488',
  tagline: 'Contractor readiness you can explain and prove, for every job, site and date.',
  purpose: 'Contractor Compliance Vault collects company, worker, vehicle and plant evidence through a secure portal, applies your versioned requirements and shows who is ready for each job, site and date. Local document processing keeps heavy work on your computer while VISINVOKE Cloud powers shared review, expiry monitoring and durable audit history.',
  price: { main: '$29.99 USD', billing: 'Per Month / Company', note: 'Unlimited authorised company users' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Runs in a dedicated desktop window through VISINVOKE DCC. Planned product, not yet released.',
  action: { kind: 'coming-soon', label: 'Coming Soon' },
  heroFacts: [
    { v: '4', l: 'Capacity tiers' },
    { v: 'Company', l: 'Unlimited authorised users' },
    { v: 'Local', l: 'Document processing on your computer' },
    { v: 'Due 2027', l: 'Planned release' },
  ],
  highlights: ['Versioned requirements', 'Secure evidence', 'Review and renew trail', 'Waive and suspend', 'Mobilisation readiness', 'Point-in-time export', 'Local OCR', 'Runs via DCC'],
  closing: { title: 'Readiness you can show, not just claim.', text: 'Versioned requirements, a secure evidence trail and a point-in-time export mean you can prove who was ready for a job, a site and a date, long after the work is done.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'Who is ready for this job, on this site, on this date',
      lead: 'Contractor compliance is not one list. It is company, worker, vehicle and plant evidence measured against requirements that change over time.',
      blocks: [
        { t: 'statement', headline: 'Readiness is a question about a specific job, site and date, not a folder of certificates.', items: [
          'Spreadsheets and shared drives cannot answer whether a particular worker, vehicle or piece of plant met your requirements for a particular job on a particular day, especially once the requirements themselves have changed.',
          'Contractor Compliance Vault collects the evidence through a secure portal, applies your versioned requirements, and answers the readiness question directly, with a trail you can reopen later.',
        ] },
        { t: 'para', items: [
          'Heavy document work, including OCR, preparation, bulk checks and large exports, runs locally on your computer where practical. VISINVOKE Cloud powers the shared review, the expiry monitoring and the durable audit history.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'Key capabilities',
      title: 'What the product gives you',
      lead: 'Everything is built around versioned requirements and an evidence trail you can defend.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Requirements', title: 'Versioned requirements', text: 'Define the requirements for company, worker, vehicle and plant, and keep them versioned so you can prove which rules applied when.' },
          { sub: 'Evidence', title: 'Secure evidence collection', text: 'Collect evidence through a secure portal, with heavy document processing kept on your own computer.' },
          { sub: 'Decisions', title: 'Review, renew, waive, suspend', text: 'Review submissions and record renewals, waivers and suspensions, each with a clear trail.' },
          { sub: 'Readiness', title: 'Mobilisation readiness', text: 'See who is ready for each job, site and date, so mobilisation is a decision you can stand behind.' },
          { sub: 'Export', title: 'Point-in-time export', text: 'Produce a privacy-controlled point-in-time export that shows exactly what was in place on a given date.' },
          { sub: 'Monitoring', title: 'Expiry monitoring', text: 'Track expiries in VISINVOKE Cloud so a lapsing document is surfaced before it stops work.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A clear path from setup to proof',
      lead: 'The workflow is deliberately short, so the work stays with the people who own it rather than with the software.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Define requirements', text: 'Set your versioned company, worker, vehicle and plant requirements.' },
          { n: '02', title: 'Collect evidence', text: 'Gather evidence through a secure portal, processed locally where practical.' },
          { n: '03', title: 'Review and decide', text: 'Review, renew, waive or suspend, with a full trail of every decision.' },
          { n: '04', title: 'Prove readiness', text: 'Show who is ready for each job, site and date, and export a point-in-time record.' },
        ] },
      ],
    },
    {
      id: 'plans',
      eyebrow: 'Plans and status',
      title: 'Four capacity tiers, one feature set',
      lead: 'Pricing is per company per month with unlimited authorised company users. The tiers change capacity, not the capabilities.',
      blocks: [
        { t: 'tiers', cols: 4, items: [
          { eyebrow: 'INVOKE', title: 'INVOKE plan', price: '$29.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Versioned requirements and evidence', 'Review, renew, waive and suspend', 'Point-in-time export', 'More capacity at each tier'] },
          { eyebrow: 'FORGE', title: 'FORGE plan', price: '$59.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Versioned requirements and evidence', 'Review, renew, waive and suspend', 'Point-in-time export', 'More capacity at each tier'] },
          { eyebrow: 'NEXUS', title: 'NEXUS plan', price: '$129.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Versioned requirements and evidence', 'Review, renew, waive and suspend', 'Point-in-time export', 'More capacity at each tier'] },
          { eyebrow: 'APEX', title: 'APEX plan', price: '$249.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Versioned requirements and evidence', 'Review, renew, waive and suspend', 'Point-in-time export', 'More capacity at each tier'] },
        ] },
        { t: 'facts', cols: 3, items: [
          { label: 'Billing', value: 'Per company, per month' },
          { label: 'Authorised users', value: 'Unlimited company users' },
          { label: 'Planned release', value: 'Due 2027' },
        ] },
        { t: 'note', title: 'Current status', text: 'This is a planned VISINVOKE product with a target of 2027. The pricing shown is planned pricing held in the product configuration, not a current offer, and there is no checkout on this page. A permanent sample workspace is planned so the product can be explored before it is released.' },
      ],
    },
  ],
};

const CREDENTIAL_RENEWAL_MANAGER = {
  key: 'credential-renewal-manager',
  backTo: BACK_PAID,
  category: 'Paid VISINVOKE Utility',
  eyebrow: 'COMPLIANCE AND WORKFORCE OPERATIONS',
  name: 'Credential & Renewal Manager',
  shortName: 'Credential & Renewal Manager',
  icon: 'CRM',
  accent: '#a78bfa',
  accent2: '#7c3aed',
  tagline: 'Renew every credential before it becomes a blocker, with proof of what was current.',
  purpose: 'Credential & Renewal Manager keeps licences, registrations, certifications, permits, memberships and other credentials in one verified lifecycle. Plan prerequisites, collect evidence securely, monitor issuer status and prove what was current at any date. Heavy document processing runs locally while VISINVOKE Cloud powers shared work, reminders and audit history.',
  price: { main: '$19.99 USD', billing: 'Per Month / Company', note: 'Unlimited authorised company users' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Runs in a dedicated desktop window through VISINVOKE DCC. Planned product, not yet released.',
  action: { kind: 'coming-soon', label: 'Coming Soon' },
  heroFacts: [
    { v: '4', l: 'Capacity tiers' },
    { v: 'Company', l: 'Unlimited authorised users' },
    { v: 'Local', l: 'Document processing on your computer' },
    { v: 'Due 2027', l: 'Planned release' },
  ],
  highlights: ['Typed credential register', 'Explicit verification', 'Renewal pipeline', 'Prerequisite aware', 'Future readiness', 'Signed audit exports', 'Local processing', 'Runs via DCC'],
  closing: { title: 'No credential should lapse by surprise.', text: 'A verified register, a prerequisite-aware renewal pipeline and reminders mean renewals happen on time, and a signed export proves what was current at any date.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'One verified lifecycle for every credential',
      lead: 'Licences, registrations, certifications, permits and memberships all expire on their own schedule, and each has its own prerequisites.',
      blocks: [
        { t: 'statement', headline: 'A lapsed credential is usually not a surprise. It is a reminder that never happened.', items: [
          'Credentials are tracked in scattered spreadsheets and calendars that do not understand prerequisites, do not verify anything, and cannot prove what was current on a past date.',
          'Credential and Renewal Manager keeps every credential in a typed register with explicit verification, a prerequisite-aware renewal pipeline and reminders, so renewals happen before they block work.',
        ] },
        { t: 'para', items: [
          'Heavy document processing runs locally on your computer, while VISINVOKE Cloud powers the shared work, the reminders and the audit history. Private signed exports let you prove what was current at any date.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'Key capabilities',
      title: 'What the product gives you',
      lead: 'Built around a verified register and a renewal pipeline that understands prerequisites.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Register', title: 'Typed credential register', text: 'Record licences, registrations, certifications, permits, memberships and more in one typed register.' },
          { sub: 'Verification', title: 'Explicit verification', text: 'Confirm each credential with explicit verification, rather than assuming a stored file is valid.' },
          { sub: 'Renewals', title: 'Prerequisite-aware pipeline', text: 'Run a renewal pipeline that understands prerequisites, so the steps that must come first actually come first.' },
          { sub: 'Readiness', title: 'Future readiness', text: 'See what is coming due and whether the prerequisites for renewal are already in place.' },
          { sub: 'Proof', title: 'Private signed exports', text: 'Produce private signed audit exports that prove what was current at any date.' },
          { sub: 'Reminders', title: 'Reminders that land', text: 'Let VISINVOKE Cloud carry the reminders, so a renewal is prompted well before it lapses.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A clear path from setup to proof',
      lead: 'The workflow is deliberately short, so the work stays with the people who own it rather than with the software.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Register credentials', text: 'Record licences, registrations, certifications, permits and memberships in a typed register.' },
          { n: '02', title: 'Verify', text: 'Confirm each credential with explicit verification.' },
          { n: '03', title: 'Plan renewals', text: 'Run a prerequisite-aware renewal pipeline before anything expires.' },
          { n: '04', title: 'Prove currency', text: 'Show future readiness and produce private signed audit exports.' },
        ] },
      ],
    },
    {
      id: 'plans',
      eyebrow: 'Plans and status',
      title: 'Four capacity tiers, one feature set',
      lead: 'Pricing is per company per month with unlimited authorised company users. The tiers change capacity, not the capabilities.',
      blocks: [
        { t: 'tiers', cols: 4, items: [
          { eyebrow: 'INVOKE', title: 'INVOKE plan', price: '$19.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Typed register and verification', 'Prerequisite-aware renewals', 'Signed audit exports', 'More capacity at each tier'] },
          { eyebrow: 'FORGE', title: 'FORGE plan', price: '$39.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Typed register and verification', 'Prerequisite-aware renewals', 'Signed audit exports', 'More capacity at each tier'] },
          { eyebrow: 'NEXUS', title: 'NEXUS plan', price: '$79.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Typed register and verification', 'Prerequisite-aware renewals', 'Signed audit exports', 'More capacity at each tier'] },
          { eyebrow: 'APEX', title: 'APEX plan', price: '$159.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Typed register and verification', 'Prerequisite-aware renewals', 'Signed audit exports', 'More capacity at each tier'] },
        ] },
        { t: 'facts', cols: 3, items: [
          { label: 'Billing', value: 'Per company, per month' },
          { label: 'Authorised users', value: 'Unlimited company users' },
          { label: 'Planned release', value: 'Due 2027' },
        ] },
        { t: 'note', title: 'Current status', text: 'This is a planned VISINVOKE product with a target of 2027. The pricing shown is planned pricing held in the product configuration, not a current offer, and there is no checkout on this page. A permanent sample workspace is planned so the product can be explored before it is released.' },
      ],
    },
  ],
};

const COMPLIANCE_EVIDENCE_VAULT = {
  key: 'compliance-evidence-vault',
  backTo: BACK_PAID,
  category: 'Paid VISINVOKE Utility',
  eyebrow: 'GOVERNANCE, RISK AND COMPLIANCE',
  name: 'Compliance Evidence Vault',
  shortName: 'Compliance Evidence Vault',
  icon: 'CEV',
  accent: '#2dd4bf',
  accent2: '#0d9488',
  tagline: 'Evidence that stays connected to exactly what it proves, ready for any audit.',
  purpose: 'Compliance Evidence Vault captures files and system records with provenance, maps them to versioned controls, monitors evidence gaps and creates tightly scoped audit rooms. Heavy hashing, OCR, redaction and package work runs locally while VISINVOKE Cloud provides encrypted sharing, recurring collection and durable history.',
  price: { main: '$29.99 USD', billing: 'Per Month / Company', note: 'Unlimited authorised company users' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Runs in a dedicated desktop window through VISINVOKE DCC. Planned product, not yet released.',
  action: { kind: 'coming-soon', label: 'Coming Soon' },
  heroFacts: [
    { v: '4', l: 'Capacity tiers' },
    { v: 'Company', l: 'Unlimited authorised users' },
    { v: 'Local', l: 'Hashing and redaction on your computer' },
    { v: 'Due 2027', l: 'Planned release' },
  ],
  highlights: ['Provenance and integrity', 'Scoped control mappings', 'Recurring requests', 'Audit rooms', 'Retention and hold trail', 'Local redaction', 'Encrypted sharing', 'Runs via DCC'],
  closing: { title: 'Evidence is only useful when it still proves something.', text: 'Provenance, control mappings and scoped audit rooms keep every piece of evidence connected to the control it supports, so an audit is a matter of opening a room rather than a scramble.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'Evidence mapped to the controls it supports',
      lead: 'A folder of files is not compliance evidence until you can show which control each file proves, and that the file has not changed since.',
      blocks: [
        { t: 'statement', headline: 'Audit season is not the time to work out which file proves which control.', items: [
          'Evidence usually lives apart from the controls it supports, with no record of where it came from or whether it has been altered, so every audit becomes a scramble to reconnect the two.',
          'Compliance Evidence Vault captures files and system records with provenance, maps them to versioned controls, monitors where evidence is missing, and opens tightly scoped rooms when someone needs to review it.',
        ] },
        { t: 'para', items: [
          'Heavy hashing, OCR, redaction and packaging run locally on your computer, while VISINVOKE Cloud provides encrypted sharing, recurring collection and durable history. A retention, hold and disposition trail follows every item.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'Key capabilities',
      title: 'What the product gives you',
      lead: 'Everything keeps evidence connected to what it proves, and provably unchanged.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Integrity', title: 'Provenance and integrity', text: 'Capture files and system records with provenance and integrity, so you can show where evidence came from and that it has not changed.' },
          { sub: 'Mapping', title: 'Scoped control mappings', text: 'Map each piece of evidence to your versioned controls, so it is always clear what a file actually proves.' },
          { sub: 'Collection', title: 'Recurring requests', text: 'Use recurring requests to keep evidence current, rather than gathering it in a panic before an audit.' },
          { sub: 'Sharing', title: 'Privacy-limited audit rooms', text: 'Open tightly scoped audit rooms that show only what a reviewer needs to see.' },
          { sub: 'Lifecycle', title: 'Retention, hold and disposition', text: 'Follow every item with a retention, hold and disposition trail.' },
          { sub: 'Local work', title: 'Local hashing and redaction', text: 'Run hashing, OCR, redaction and packaging on your own computer where practical.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A clear path from setup to proof',
      lead: 'The workflow is deliberately short, so the work stays with the people who own it rather than with the software.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Capture evidence', text: 'Capture files and system records with provenance and integrity.' },
          { n: '02', title: 'Map to controls', text: 'Map evidence to your versioned controls.' },
          { n: '03', title: 'Monitor gaps', text: 'Use recurring requests to keep evidence current and expose gaps.' },
          { n: '04', title: 'Share safely', text: 'Open privacy-limited audit rooms with a retention, hold and disposition trail.' },
        ] },
      ],
    },
    {
      id: 'plans',
      eyebrow: 'Plans and status',
      title: 'Four capacity tiers, one feature set',
      lead: 'Pricing is per company per month with unlimited authorised company users. The tiers change capacity, not the capabilities.',
      blocks: [
        { t: 'tiers', cols: 4, items: [
          { eyebrow: 'INVOKE', title: 'INVOKE plan', price: '$29.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Provenance and control mappings', 'Recurring evidence requests', 'Scoped audit rooms', 'More capacity at each tier'] },
          { eyebrow: 'FORGE', title: 'FORGE plan', price: '$59.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Provenance and control mappings', 'Recurring evidence requests', 'Scoped audit rooms', 'More capacity at each tier'] },
          { eyebrow: 'NEXUS', title: 'NEXUS plan', price: '$129.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Provenance and control mappings', 'Recurring evidence requests', 'Scoped audit rooms', 'More capacity at each tier'] },
          { eyebrow: 'APEX', title: 'APEX plan', price: '$249.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Provenance and control mappings', 'Recurring evidence requests', 'Scoped audit rooms', 'More capacity at each tier'] },
        ] },
        { t: 'facts', cols: 3, items: [
          { label: 'Billing', value: 'Per company, per month' },
          { label: 'Authorised users', value: 'Unlimited company users' },
          { label: 'Planned release', value: 'Due 2027' },
        ] },
        { t: 'note', title: 'Current status', text: 'This is a planned VISINVOKE product with a target of 2027. The pricing shown is planned pricing held in the product configuration, not a current offer, and there is no checkout on this page. A permanent sample workspace is planned so the product can be explored before it is released.' },
      ],
    },
  ],
};

const MAINTENANCE_AND_INSPECTION_CLOUD = {
  key: 'maintenance-and-inspection-cloud',
  backTo: BACK_PAID,
  category: 'Paid VISINVOKE Utility',
  eyebrow: 'ASSETS, MAINTENANCE AND FIELD OPERATIONS',
  name: 'Maintenance & Inspection Cloud',
  shortName: 'Maintenance & Inspection Cloud',
  icon: 'MIC',
  accent: '#f59e0b',
  accent2: '#b45309',
  tagline: 'Every asset, inspection and repair in one controlled history you can rely on.',
  purpose: 'Maintenance & Inspection Cloud connects assets, maintenance plans, inspections, defects, work orders, parts, field evidence and return-to-service decisions. Local processing keeps large media and analytics on your computer while VISINVOKE Cloud coordinates schedules, offline teams, portals and shared history.',
  price: { main: '$19.99 USD', billing: 'Per Month / Company', note: 'Unlimited authorised company users' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Runs in a dedicated desktop window through VISINVOKE DCC. Planned product, not yet released.',
  action: { kind: 'coming-soon', label: 'Coming Soon' },
  heroFacts: [
    { v: '4', l: 'Capacity tiers' },
    { v: 'Company', l: 'Unlimited authorised users' },
    { v: 'Offline', l: 'Field work without a signal' },
    { v: 'Due 2027', l: 'Planned release' },
  ],
  highlights: ['Asset hierarchy', 'Maintenance strategies', 'Typed inspections', 'Safe return-to-service', 'Offline field work', 'Reliability reporting', 'Local media', 'Runs via DCC'],
  closing: { title: 'One history for every asset, from inspection to return to service.', text: 'Assets, plans, inspections, defects, work orders and parts connected together mean a repair decision is made with the full history in front of you, not from memory.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'The whole asset lifecycle, connected',
      lead: 'Assets, inspections, defects, work orders and parts are usually tracked in separate places, so no one can see the full history of a single machine.',
      blocks: [
        { t: 'statement', headline: 'A safe return-to-service decision needs the whole history, not a sticky note.', items: [
          'When inspections, defects, work orders and parts live in different systems, the people making repair and return-to-service decisions are working from fragments, which is exactly where safety and cost problems start.',
          'Maintenance & Inspection Cloud connects assets, maintenance plans, inspections, defects, work orders, parts, field evidence and return-to-service decisions into one controlled history.',
        ] },
        { t: 'para', items: [
          'Local processing keeps large media and analytics on your own computer, while VISINVOKE Cloud coordinates schedules, offline field teams, portals and shared history. Field work continues even without a signal.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'Key capabilities',
      title: 'What the product gives you',
      lead: 'Built to hold the entire asset lifecycle, including the field work that usually gets lost.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Assets', title: 'Asset hierarchy', text: 'Model your assets in a hierarchy, so every machine, location and component has its place.' },
          { sub: 'Plans', title: 'Maintenance strategies', text: 'Define maintenance strategies and plans, so preventive work is scheduled rather than forgotten.' },
          { sub: 'Inspections', title: 'Typed inspections', text: 'Run typed inspections that capture consistent, structured results and field evidence.' },
          { sub: 'Safety', title: 'Safe return-to-service', text: 'Manage defects and return-to-service with controls that keep unsafe assets out of use.' },
          { sub: 'Field', title: 'Offline field work', text: 'Let field teams work offline and sync when a connection returns.' },
          { sub: 'Reporting', title: 'Reliability and cost', text: 'Report on reliability and cost across the estate, using analytics processed locally.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A clear path from setup to proof',
      lead: 'The workflow is deliberately short, so the work stays with the people who own it rather than with the software.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Model assets', text: 'Build the asset hierarchy and define maintenance strategies.' },
          { n: '02', title: 'Inspect', text: 'Run typed inspections and capture field evidence, including offline.' },
          { n: '03', title: 'Act on defects', text: 'Manage defects, work orders and parts with safe return-to-service controls.' },
          { n: '04', title: 'Report', text: 'Track reliability and cost across the whole estate.' },
        ] },
      ],
    },
    {
      id: 'plans',
      eyebrow: 'Plans and status',
      title: 'Four capacity tiers, one feature set',
      lead: 'Pricing is per company per month with unlimited authorised company users. The tiers change capacity, not the capabilities.',
      blocks: [
        { t: 'tiers', cols: 4, items: [
          { eyebrow: 'INVOKE', title: 'INVOKE plan', price: '$19.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Assets, plans and inspections', 'Defect and return-to-service controls', 'Offline field work', 'More capacity at each tier'] },
          { eyebrow: 'FORGE', title: 'FORGE plan', price: '$39.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Assets, plans and inspections', 'Defect and return-to-service controls', 'Offline field work', 'More capacity at each tier'] },
          { eyebrow: 'NEXUS', title: 'NEXUS plan', price: '$79.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Assets, plans and inspections', 'Defect and return-to-service controls', 'Offline field work', 'More capacity at each tier'] },
          { eyebrow: 'APEX', title: 'APEX plan', price: '$159.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Assets, plans and inspections', 'Defect and return-to-service controls', 'Offline field work', 'More capacity at each tier'] },
        ] },
        { t: 'facts', cols: 3, items: [
          { label: 'Billing', value: 'Per company, per month' },
          { label: 'Authorised users', value: 'Unlimited company users' },
          { label: 'Planned release', value: 'Due 2027' },
        ] },
        { t: 'note', title: 'Current status', text: 'This is a planned VISINVOKE product with a target of 2027. The pricing shown is planned pricing held in the product configuration, not a current offer, and there is no checkout on this page. A permanent sample workspace is planned so the product can be explored before it is released.' },
      ],
    },
  ],
};

const SECURE_CLIENT_DOCUMENT_PORTAL = {
  key: 'secure-client-document-portal',
  backTo: BACK_PAID,
  category: 'Paid VISINVOKE Utility',
  eyebrow: 'SECURE COLLABORATION AND CLIENT SERVICES',
  name: 'Secure Client Document Portal',
  shortName: 'Secure Client Document Portal',
  icon: 'SCDP',
  accent: '#38bdf8',
  accent2: '#0284c7',
  tagline: 'A safer, clearer way to exchange client documents than email attachments.',
  purpose: 'Secure Client Document Portal replaces sensitive email attachments with structured requests, resumable uploads, quarantine, review, replacement and final delivery. Heavy preparation, redaction and packaging runs locally while VISINVOKE Cloud provides encrypted storage, client access, reminders and audit history.',
  price: { main: '$29.99 USD', billing: 'Per Month / Company', note: 'Unlimited authorised company users' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Runs in a dedicated desktop window through VISINVOKE DCC. Planned product, not yet released.',
  action: { kind: 'coming-soon', label: 'Coming Soon' },
  heroFacts: [
    { v: '4', l: 'Capacity tiers' },
    { v: 'Company', l: 'Unlimited authorised users' },
    { v: 'Local', l: 'Preparation and redaction on your computer' },
    { v: 'Due 2027', l: 'Planned release' },
  ],
  highlights: ['Itemised requests', 'Safe upload and quarantine', 'Versioned review', 'Controlled delivery', 'Access and retention audit', 'Local redaction', 'Encrypted storage', 'Runs via DCC'],
  closing: { title: 'Client documents deserve better than an email attachment.', text: 'Structured requests, quarantined uploads, versioned review and controlled delivery replace the risk of loose attachments with a clear, audited exchange.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'A structured exchange instead of loose attachments',
      lead: 'Sensitive client documents usually travel as email attachments, with no structure, no quarantine and no record of who saw what.',
      blocks: [
        { t: 'statement', headline: 'Email was never built to be a safe place to exchange sensitive client documents.', items: [
          'Attachments scatter across inboxes with no itemised request, no safe holding area, no version history and no reliable record of access, which is a poor fit for confidential client information.',
          'Secure Client Document Portal replaces that with itemised requests, resumable uploads into quarantine, versioned review and replacement, and a controlled final delivery, all with an audit trail.',
        ] },
        { t: 'para', items: [
          'Heavy preparation, redaction and packaging run locally on your own computer, while VISINVOKE Cloud provides the encrypted storage, the client access, the reminders and the audit history.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'Key capabilities',
      title: 'What the product gives you',
      lead: 'Every step is structured, so a client exchange is clear and defensible from request to delivery.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Requests', title: 'Itemised requests', text: 'Ask for exactly the documents you need as itemised requests, rather than a vague email.' },
          { sub: 'Intake', title: 'Safe upload and quarantine', text: 'Take resumable uploads into a quarantine area, so nothing is trusted before it is checked.' },
          { sub: 'Review', title: 'Versioned review and replacement', text: 'Review versions and request replacements, with heavy preparation and redaction done locally.' },
          { sub: 'Delivery', title: 'Controlled final delivery', text: 'Release a controlled final delivery, so the client receives exactly what was approved.' },
          { sub: 'Audit', title: 'Access and retention audit', text: 'Keep an access and retention audit of who saw what and when.' },
          { sub: 'Storage', title: 'Encrypted client access', text: 'Give clients encrypted access through VISINVOKE Cloud, with reminders that keep an exchange moving.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A clear path from setup to proof',
      lead: 'The workflow is deliberately short, so the work stays with the people who own it rather than with the software.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Request documents', text: 'Send itemised requests instead of loose email attachments.' },
          { n: '02', title: 'Receive safely', text: 'Take resumable uploads into quarantine before anything is trusted.' },
          { n: '03', title: 'Review and replace', text: 'Review versions, request replacements, and prepare or redact locally.' },
          { n: '04', title: 'Deliver', text: 'Release a controlled final delivery with an access and retention audit.' },
        ] },
      ],
    },
    {
      id: 'plans',
      eyebrow: 'Plans and status',
      title: 'Four capacity tiers, one feature set',
      lead: 'Pricing is per company per month with unlimited authorised company users. The tiers change capacity, not the capabilities.',
      blocks: [
        { t: 'tiers', cols: 4, items: [
          { eyebrow: 'INVOKE', title: 'INVOKE plan', price: '$29.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Itemised requests and quarantine', 'Versioned review and replacement', 'Controlled delivery and audit', 'More capacity at each tier'] },
          { eyebrow: 'FORGE', title: 'FORGE plan', price: '$59.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Itemised requests and quarantine', 'Versioned review and replacement', 'Controlled delivery and audit', 'More capacity at each tier'] },
          { eyebrow: 'NEXUS', title: 'NEXUS plan', price: '$129.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Itemised requests and quarantine', 'Versioned review and replacement', 'Controlled delivery and audit', 'More capacity at each tier'] },
          { eyebrow: 'APEX', title: 'APEX plan', price: '$249.99 USD', billing: 'per month / company', capacity: 'per month / company', items: ['Itemised requests and quarantine', 'Versioned review and replacement', 'Controlled delivery and audit', 'More capacity at each tier'] },
        ] },
        { t: 'facts', cols: 3, items: [
          { label: 'Billing', value: 'Per company, per month' },
          { label: 'Authorised users', value: 'Unlimited company users' },
          { label: 'Planned release', value: 'Due 2027' },
        ] },
        { t: 'note', title: 'Current status', text: 'This is a planned VISINVOKE product with a target of 2027. The pricing shown is planned pricing held in the product configuration, not a current offer, and there is no checkout on this page. A permanent sample workspace is planned so the product can be explored before it is released.' },
      ],
    },
  ],
};

const FREE_BACKUP_UTILITY = {
  key: 'backup-utility',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · BACKUP AND RESTORE',
  name: 'VISINVOKE Backup Utility',
  shortName: 'VISINVOKE Backup Utility',
  icon: '💾',
  accent: '#a855f7',
  accent2: '#7c3aed',
  tagline: 'Visual backup management for the folders that actually matter to you.',
  purpose: 'Visual backup management for the folders that actually matter. Set up backup projects, send them to the drives you already own, and see what is protected, where it lives and when it last ran. It runs on your own computer through VISINVOKE DCC and works with the storage you already have.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Available', tone: '#34d399' },
  accessNote: 'A free utility that runs in a dedicated desktop window through VISINVOKE DCC.',
  action: { kind: 'disabled', label: 'Free with VISINVOKE', pill: 'Runs via DCC' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: 'DCC', l: 'Runs via VISINVOKE DCC' },
    { v: 'Windows', l: 'Uses your own drives' },
    { v: 'Local', l: 'Your files stay on your equipment' },
  ],
  highlights: ['Backup projects', 'NAS and network targets', 'One-off backups', 'Scheduling and retention', 'Restore anywhere', 'Verification and reports', 'Runs via DCC', 'Free'],
  closing: { title: 'Protection you can actually see.', text: 'Projects, targets, schedules and a clear history mean you always know what is backed up, where it lives and when it last ran.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'Backups you can see and trust',
      lead: 'Most backup goes wrong quietly. The Backup Utility is built so the state of your protection is always visible.',
      blocks: [
        { t: 'statement', headline: 'A backup you cannot see the state of is a backup you cannot rely on.', items: [
          'Copying folders by hand or trusting a background task you never check is how people discover, too late, that nothing was protected.',
          'The Backup Utility turns backup into visible projects: what is protected, which drive it goes to, when it last ran, and whether it can be restored.',
        ] },
        { t: 'para', items: [
          'Set up project-based backup jobs to NAS, local, external and network-share targets, schedule them with retention and exclusions, run quick one-off backups when you need to, and restore to the original or a custom location. Verification, history and reports keep the whole picture in one place.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'What you can do',
      title: 'Everything the utility gives you',
      lead: 'Everything is aimed at making protection visible and restores dependable.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Projects', title: 'Project-based backup jobs', text: 'Set up reusable backup projects for the folders that matter, instead of ad hoc manual copies.' },
          { sub: 'Targets', title: 'The drives you already own', text: 'Send backups to NAS, local drives, external drives and network shares you already have.' },
          { sub: 'Quick', title: 'One-off backups', text: 'Run a quick one-off backup without setting up a full project when you just need a copy now.' },
          { sub: 'Schedule', title: 'Scheduled, with retention', text: 'Schedule backups and control retention and exclusions, so the right versions are kept and the noise is left out.' },
          { sub: 'Restore', title: 'Restore anywhere', text: 'Restore to the original location or a custom one when you need your files back.' },
          { sub: 'Proof', title: 'Verification and reports', text: 'Verify backups and keep history and reports, so you can see what ran and confirm it worked.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A simple, direct workflow',
      lead: 'The tool is built to get you to a finished result quickly, without a setup project or an account for a third-party service.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Create a project', text: 'Choose the folders that matter and set up a backup project.' },
          { n: '02', title: 'Pick a target', text: 'Send it to a NAS, local, external or network-share drive you already own.' },
          { n: '03', title: 'Schedule and run', text: 'Set a schedule with retention and exclusions, or run a quick one-off backup.' },
          { n: '04', title: 'Verify and restore', text: 'Check verification, history and reports, and restore to the original or a custom location.' },
        ] },
      ],
    },
    {
      id: 'access',
      eyebrow: 'Local and private',
      title: 'Runs on your own computer',
      lead: 'This is a genuine VISINVOKE utility that does its work on your machine rather than on a web service.',
      blocks: [
        { t: 'callout', eyebrow: 'How it runs', title: 'Runs via VISINVOKE DCC', text: 'The Backup Utility runs in a dedicated desktop window through VISINVOKE DCC and uses the drives and shares you already own.', items: [
          'Runs in a dedicated desktop window through VISINVOKE DCC',
          'Your files are processed on your own computer',
          'Free with your VISINVOKE account',
          'Part of the same VISINVOKE account as your other products',
        ] },
        { t: 'cards', cols: 2, items: [
          { sub: 'Free', title: 'A full utility, not a trial', text: 'This is a complete free VISINVOKE utility. It is not a time-limited trial and not a cut-down edition that pushes you toward a paid upgrade.' },
          { sub: 'Local', title: 'Your files stay with you', text: 'Your files are copied to storage you control. VISINVOKE does not require your backups to pass through a web service.' },
        ] },
      ],
    },
    {
      id: 'about',
      eyebrow: 'At a glance',
      title: 'The essentials',
      lead: 'A quick summary of how this utility is delivered and what it costs.',
      blocks: [
        { t: 'facts', cols: 3, items: [
          { label: 'Price', value: 'Free with your VISINVOKE account' },
          { label: 'Access', value: 'Runs via VISINVOKE DCC' },
          { label: 'Storage', value: 'Your own NAS, local, external or network drives' },
        ] },
        { t: 'note', title: 'Good to know', text: 'The Backup Utility works with the storage you already own. Choosing sensible targets, schedules and retention is part of a good backup plan, and verification is there so a restore is something you have confirmed rather than assumed.' },
      ],
    },
  ],
};

const FREE_PDF_TOOLKIT = {
  key: 'pdf-toolkit',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · PDF DOCUMENT TOOLS',
  name: 'VISINVOKE PDF Toolkit',
  shortName: 'VISINVOKE PDF Toolkit',
  icon: '📄',
  accent: '#f87171',
  accent2: '#dc2626',
  tagline: 'Everyday PDF work without uploading your documents to an online converter.',
  purpose: 'Everyday document work without an online converter. Combine, separate, shrink and reorganise PDF files, then keep a record of every operation you have run. It works on your own computer through VISINVOKE DCC, so your documents never leave your machine for a web service.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Available', tone: '#34d399' },
  accessNote: 'A free utility that runs in a dedicated desktop window through VISINVOKE DCC.',
  action: { kind: 'disabled', label: 'Free with VISINVOKE', pill: 'Runs via DCC' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: 'DCC', l: 'Runs via VISINVOKE DCC' },
    { v: 'Local', l: 'Documents never uploaded' },
    { v: 'History', l: 'Every operation recorded' },
  ],
  highlights: ['Merge PDFs', 'Split PDFs', 'Compress (3 levels)', 'Rotate and extract', 'Images to PDF', 'Operation history', 'Runs via DCC', 'Free'],
  closing: { title: 'The common PDF jobs, done locally.', text: 'Merge, split, compress, rotate, extract and turn images into PDFs, with a history of every operation, all on your own computer.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'The PDF jobs you actually need',
      lead: 'Most PDF tasks are simple, but the usual answer is a website that wants you to upload the document first.',
      blocks: [
        { t: 'statement', headline: 'The document you are trying to fix is often the one you least want to upload to a stranger.', items: [
          'Free online PDF tools ask you to hand over the exact file you are working on, which is a poor fit for anything private or commercial.',
          'PDF Toolkit does the everyday jobs on your own computer: merge, split, compress, rotate, extract pages and turn images into a PDF, with a record of what you did.',
        ] },
        { t: 'para', items: [
          'Merge several PDFs into one, split a PDF into separate files, compress at maximum quality, balanced or maximum compression, rotate pages, extract page selections, and convert images into a PDF. Every operation is kept in a history with its output details.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'What you can do',
      title: 'Everything the utility gives you',
      lead: 'A focused set of the operations most people need most often.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Merge', title: 'Combine into one', text: 'Merge several PDFs into a single document in the order you choose.' },
          { sub: 'Split', title: 'Separate files', text: 'Split a PDF into separate files when you only need part of it.' },
          { sub: 'Compress', title: 'Three levels', text: 'Compress at maximum quality, balanced or maximum compression to hit the size you need.' },
          { sub: 'Pages', title: 'Rotate and extract', text: 'Rotate pages and extract a selection of pages into their own file.' },
          { sub: 'Images', title: 'Images into a PDF', text: 'Turn a set of images into a single PDF document.' },
          { sub: 'History', title: 'Operation history', text: 'Keep a record of every operation you have run, with its output details.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A simple, direct workflow',
      lead: 'The tool is built to get you to a finished result quickly, without a setup project or an account for a third-party service.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Add your files', text: 'Bring in the PDFs or images you want to work with.' },
          { n: '02', title: 'Choose an operation', text: 'Merge, split, compress, rotate, extract or convert images to PDF.' },
          { n: '03', title: 'Set the options', text: 'Pick the order, the compression level or the pages you need.' },
          { n: '04', title: 'Run and review', text: 'Produce the output and see it recorded in your operation history.' },
        ] },
      ],
    },
    {
      id: 'access',
      eyebrow: 'Local and private',
      title: 'Runs on your own computer',
      lead: 'This is a genuine VISINVOKE utility that does its work on your machine rather than on a web service.',
      blocks: [
        { t: 'callout', eyebrow: 'How it runs', title: 'Runs via VISINVOKE DCC', text: 'PDF Toolkit runs in a dedicated desktop window through VISINVOKE DCC and works on the files on your own computer.', items: [
          'Runs in a dedicated desktop window through VISINVOKE DCC',
          'Your files are processed on your own computer',
          'Free with your VISINVOKE account',
          'Part of the same VISINVOKE account as your other products',
        ] },
        { t: 'cards', cols: 2, items: [
          { sub: 'Free', title: 'A full utility, not a trial', text: 'This is a complete free VISINVOKE utility. It is not a time-limited trial and not a cut-down edition that pushes you toward a paid upgrade.' },
          { sub: 'Local', title: 'Your files stay with you', text: 'Your documents are processed on your own computer and are not uploaded to an online converter.' },
        ] },
      ],
    },
    {
      id: 'about',
      eyebrow: 'At a glance',
      title: 'The essentials',
      lead: 'A quick summary of how this utility is delivered and what it costs.',
      blocks: [
        { t: 'facts', cols: 3, items: [
          { label: 'Price', value: 'Free with your VISINVOKE account' },
          { label: 'Access', value: 'Runs via VISINVOKE DCC' },
          { label: 'Privacy', value: 'Documents processed on your own computer' },
        ] },
        { t: 'note', title: 'Good to know', text: 'PDF Toolkit focuses on the common everyday operations. Because the work happens on your computer, private and commercial documents do not need to be handed to a web service to get simple jobs done.' },
      ],
    },
  ],
};

const FREE_IMAGE_TOOLKIT = {
  key: 'image-toolkit',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · IMAGE PREPARATION',
  name: 'VISINVOKE Image Toolkit',
  shortName: 'VISINVOKE Image Toolkit',
  icon: '🖼️',
  accent: '#34d399',
  accent2: '#059669',
  tagline: 'Prepare images for anything without hunting for the right dimensions.',
  purpose: 'Prepare images for anything without hunting for the right dimensions. Add a file or a whole folder, tell VISINVOKE what the image is for, a social post, an A4 print, a website banner, and it sets the size, crop and quality for you. Everything runs on your own computer and your original files are never changed.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Available', tone: '#34d399' },
  accessNote: 'A free utility that runs via VISINVOKE DCC, or directly in your browser.',
  action: { kind: 'disabled', label: 'Free with VISINVOKE', pill: 'DCC or browser' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: 'DCC/browser', l: 'Runs via DCC or in your browser' },
    { v: 'Local', l: 'Originals never changed' },
    { v: 'Batch', l: 'Whole folders at once' },
  ],
  highlights: ['Size for a purpose', 'Batch folders', 'Format conversion', 'Target file size', 'Watermarks', 'Metadata removal', 'Local enhancement', 'Runs via DCC or browser'],
  closing: { title: 'The right image, ready for where it is going.', text: 'Purpose-based sizing, batch processing, conversion, watermarks and local enhancement, all on your own computer with your originals left untouched.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'Images sized for where they are going',
      lead: 'Most image tasks fail on one detail: the exact size, crop and quality the destination actually wants.',
      blocks: [
        { t: 'statement', headline: 'The hard part of preparing an image is rarely the editing. It is knowing the exact size the destination expects.', items: [
          'Every platform, print size and document has its own dimensions and quality expectations, and getting them wrong means a blurry post or a rejected upload.',
          'Image Toolkit lets you say what the image is for, then sets the size, crop and quality for you, on one image or a whole folder at once, without ever changing your originals.',
        ] },
        { t: 'para', items: [
          'Size images for social platforms, print and A-series, web, documents and screens; resize, crop, fit and pad, and batch-process whole folders; convert between JPEG, PNG, WebP, TIFF, BMP, GIF and ICO; optimise for quality or hit a target size; add text or logo watermarks; create thumbnails and full social sets; remove private metadata; enhance soft images locally; and save reusable recipes.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'What you can do',
      title: 'Everything the utility gives you',
      lead: 'A complete local image-preparation workflow, from a single file to a whole folder.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Purpose', title: 'Size for a purpose', text: 'Choose a destination, social platforms, print and A-series, web, documents or screens, and get the right size and crop.' },
          { sub: 'Transform', title: 'Resize, crop, fit, pad', text: 'Resize, crop, fit and pad, and batch-process whole folders in one pass.' },
          { sub: 'Convert', title: 'Between all common formats', text: 'Convert between JPEG, PNG, WebP, TIFF, BMP, GIF and ICO.' },
          { sub: 'Optimise', title: 'Quality or target size', text: 'Optimise for quality, or hit a target file size such as under 500 KB.' },
          { sub: 'Brand', title: 'Watermarks and sets', text: 'Add text or logo watermarks, single or tiled, and create thumbnails and full social sets from one image.' },
          { sub: 'Privacy', title: 'Metadata removed', text: 'Remove private metadata such as location and device, with your originals left untouched.' },
          { sub: 'Enhance', title: 'Local enhancement', text: 'Sharpen and clean up soft images with local enhancement.' },
          { sub: 'Recipes', title: 'Reusable recipes', text: 'Save reusable recipes and choose where your outputs go.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A simple, direct workflow',
      lead: 'The tool is built to get you to a finished result quickly, without a setup project or an account for a third-party service.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Add images', text: 'Add a single file or a whole folder.' },
          { n: '02', title: 'Say what it is for', text: 'Tell VISINVOKE the destination, and it sets size, crop and quality.' },
          { n: '03', title: 'Refine', text: 'Convert, optimise, watermark, remove metadata or enhance as needed.' },
          { n: '04', title: 'Export', text: 'Produce the outputs, or reuse a saved recipe, with originals untouched.' },
        ] },
      ],
    },
    {
      id: 'access',
      eyebrow: 'Local and private',
      title: 'Runs on your own computer',
      lead: 'This is a genuine VISINVOKE utility that does its work on your machine rather than on a web service.',
      blocks: [
        { t: 'callout', eyebrow: 'How it runs', title: 'Runs via VISINVOKE DCC or your browser', text: 'Image Toolkit normally runs in a dedicated desktop window through VISINVOKE DCC, and can also run directly in your browser. Either way it works on your own computer.', items: [
          'Runs via VISINVOKE DCC, or directly in your browser',
          'Your files are processed on your own computer',
          'Free with your VISINVOKE account',
          'Part of the same VISINVOKE account as your other products',
        ] },
        { t: 'cards', cols: 2, items: [
          { sub: 'Free', title: 'A full utility, not a trial', text: 'This is a complete free VISINVOKE utility. It is not a time-limited trial and not a cut-down edition that pushes you toward a paid upgrade.' },
          { sub: 'Local', title: 'Your files stay with you', text: 'Everything runs on your own computer and your original files are never changed. There is a paid, optional AI Rebuild cloud add-on, but it is never required to use Image Toolkit.' },
        ] },
      ],
    },
    {
      id: 'about',
      eyebrow: 'At a glance',
      title: 'The essentials',
      lead: 'A quick summary of how this utility is delivered and what it costs.',
      blocks: [
        { t: 'facts', cols: 3, items: [
          { label: 'Price', value: 'Free with your VISINVOKE account' },
          { label: 'Access', value: 'Runs via VISINVOKE DCC, or directly in your browser' },
          { label: 'Originals', value: 'Never modified' },
        ] },
        { t: 'note', title: 'Good to know', text: 'Image Toolkit is free and complete on its own. AI Rebuild is a separate, optional paid cloud add-on for reconstructing genuinely low-resolution originals; paying is never required to use Image Toolkit.' },
      ],
    },
  ],
};

const FREE_USB_BUILDER = {
  key: 'usb-builder',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · MANAGED USB MEDIA',
  name: 'VISINVOKE USB Builder',
  shortName: 'VISINVOKE USB Builder',
  icon: '🔌',
  accent: '#fb923c',
  accent2: '#c2410c',
  tagline: 'Build, verify and maintain VISINVOKE-managed USB drives with strict device safety checks.',
  purpose: 'Build, verify and maintain VISINVOKE-managed USB drives with strict device safety checks. Boot media is inspected and planned; destructive boot writing is deliberately not enabled. It runs on your own computer through VISINVOKE DCC and is careful about which device it will touch.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Available', tone: '#34d399' },
  accessNote: 'A free utility that runs in a dedicated desktop window through VISINVOKE DCC.',
  action: { kind: 'disabled', label: 'Free with VISINVOKE', pill: 'Runs via DCC' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: 'DCC', l: 'Runs via VISINVOKE DCC' },
    { v: 'Windows', l: 'Disk and volume aware' },
    { v: 'Safe', l: 'No destructive boot writing' },
  ],
  highlights: ['Disk discovery', 'System-disk protection', 'SHA-256 verification', 'Incremental updates', 'Build profiles', 'Versioned manifests', 'Boot planning', 'Runs via DCC'],
  closing: { title: 'Managed USB media, with the brakes on.', text: 'Discovery, system-disk protection, SHA-256 verification and versioned manifests, with destructive boot writing deliberately left off.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'Managed USB drives, built safely',
      lead: 'The dangerous part of USB tooling is writing to the wrong disk. USB Builder is built to make that hard to do.',
      blocks: [
        { t: 'statement', headline: 'The fastest way to lose data is a USB tool that writes to the wrong drive.', items: [
          'General USB and imaging tools will happily overwrite the wrong disk, and the mistake is usually discovered only after it is too late.',
          'USB Builder discovers your disks in detail, protects the system and boot disk, revalidates the target before acting, and deliberately does not enable destructive boot writing.',
        ] },
        { t: 'para', items: [
          'It performs Windows disk and volume discovery with capacity, model and bus detail, protects the system and boot disk with target revalidation, manages USB content with full SHA-256 verification, applies incremental add, change and remove updates, supports reusable build profiles and categories, keeps build history and versioned manifests, and inspects ISO or image files for boot compatibility planning.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'What you can do',
      title: 'Everything the utility gives you',
      lead: 'Every capability is built around device safety and verifiable content.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Discovery', title: 'Detailed disk discovery', text: 'See Windows disks and volumes with capacity, model and bus detail before you touch anything.' },
          { sub: 'Safety', title: 'System-disk protection', text: 'Protect the system and boot disk, with the target revalidated before any action.' },
          { sub: 'Integrity', title: 'SHA-256 verification', text: 'Manage USB content with full SHA-256 verification, so you can trust what is on the drive.' },
          { sub: 'Updates', title: 'Incremental updates', text: 'Add, change and remove content incrementally rather than rebuilding from scratch.' },
          { sub: 'Profiles', title: 'Reusable profiles', text: 'Use reusable build profiles and categories for consistent drives.' },
          { sub: 'Records', title: 'History and manifests', text: 'Keep build history and versioned manifests of what each drive contains.' },
          { sub: 'Boot', title: 'Boot planning', text: 'Inspect ISO and image files and plan boot compatibility, without destructive boot writing.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A simple, direct workflow',
      lead: 'The tool is built to get you to a finished result quickly, without a setup project or an account for a third-party service.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Discover devices', text: 'Review your disks and volumes with capacity, model and bus detail.' },
          { n: '02', title: 'Choose a safe target', text: 'Select a USB target, with the system and boot disk protected and the target revalidated.' },
          { n: '03', title: 'Build and verify', text: 'Apply managed content with full SHA-256 verification, using a reusable profile if you have one.' },
          { n: '04', title: 'Maintain', text: 'Apply incremental updates and keep versioned manifests and build history.' },
        ] },
      ],
    },
    {
      id: 'access',
      eyebrow: 'Local and private',
      title: 'Runs on your own computer',
      lead: 'This is a genuine VISINVOKE utility that does its work on your machine rather than on a web service.',
      blocks: [
        { t: 'callout', eyebrow: 'How it runs', title: 'Runs via VISINVOKE DCC', text: 'USB Builder runs in a dedicated desktop window through VISINVOKE DCC and inspects the disks on your own computer.', items: [
          'Runs in a dedicated desktop window through VISINVOKE DCC',
          'Your files are processed on your own computer',
          'Free with your VISINVOKE account',
          'Part of the same VISINVOKE account as your other products',
        ] },
        { t: 'cards', cols: 2, items: [
          { sub: 'Free', title: 'A full utility, not a trial', text: 'This is a complete free VISINVOKE utility. It is not a time-limited trial and not a cut-down edition that pushes you toward a paid upgrade.' },
          { sub: 'Local', title: 'Your files stay with you', text: 'USB Builder works with the drives connected to your own computer, and is deliberately cautious about which device it will write to.' },
        ] },
      ],
    },
    {
      id: 'about',
      eyebrow: 'At a glance',
      title: 'The essentials',
      lead: 'A quick summary of how this utility is delivered and what it costs.',
      blocks: [
        { t: 'facts', cols: 3, items: [
          { label: 'Price', value: 'Free with your VISINVOKE account' },
          { label: 'Access', value: 'Runs via VISINVOKE DCC' },
          { label: 'Safety', value: 'System-disk protection and target revalidation' },
        ] },
        { t: 'note', title: 'Good to know', text: 'USB Builder deliberately does not enable destructive boot writing; boot media is inspected and planned. This is a safety choice, so the tool cannot silently overwrite a disk you did not intend.' },
      ],
    },
  ],
};

const FREE_PRESENTATION_STUDIO = {
  key: 'presentation-studio',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · SLIDES AND DATA VISUALISATION',
  name: 'VISINVOKE Presentation Studio',
  shortName: 'VISINVOKE Presentation Studio',
  icon: '📊',
  accent: '#eab308',
  accent2: '#a16207',
  tagline: 'Turn figures, rough points or an existing deck into a clear presentation.',
  purpose: 'A local slide and data-visualisation studio. It normally runs through VISINVOKE DCC, and can also run directly in your browser, for locked-down business PCs where the DCC cannot be installed. Turn your figures, rough points or an existing deck into management, reporting and training presentations, with editable PowerPoint output.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Available', tone: '#34d399' },
  accessNote: 'A free utility that runs via VISINVOKE DCC, or directly in your browser.',
  action: { kind: 'disabled', label: 'Free with VISINVOKE', pill: 'DCC or browser' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: 'DCC/browser', l: 'Runs via DCC or in your browser' },
    { v: '.pptx', l: 'Editable PowerPoint output' },
    { v: 'Local', l: 'Nothing uploaded' },
  ],
  highlights: ['Deck or single slide', 'Business graphs and KPIs', 'Improve an existing deck', 'Autosave and recovery', 'Editable .pptx export', 'DCC or browser', 'Nothing uploaded', 'Free'],
  closing: { title: 'A presentation from your figures, not a blank slide.', text: 'Build a deck or a single slide from your data, improve an existing PowerPoint, and export editable .pptx, all on your own computer.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'From figures to a finished deck',
      lead: 'A blank slide is the slowest way to start. Presentation Studio starts from what you already have.',
      blocks: [
        { t: 'statement', headline: 'Most presentations stall at the blank first slide, not at the last one.', items: [
          'Turning raw figures and rough notes into clear management, reporting or training slides is slow, and starting from an empty deck makes it slower.',
          'Presentation Studio builds a full deck or a single slide from your data, chooses business graphs and KPI cards for your figures, and can improve an existing PowerPoint, with editable .pptx output.',
        ] },
        { t: 'para', items: [
          'Build a full deck or a single slide from your data, get business graphs and KPI cards chosen for your figures, improve an existing PowerPoint layout and readability, rely on local autosave and recovery, and export editable .pptx with real objects rather than flat pictures. It runs via the DCC or directly in your browser, with nothing uploaded and no account needed for the browser mode.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'What you can do',
      title: 'Everything the utility gives you',
      lead: 'Everything is aimed at getting from your data to an editable, professional deck.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Build', title: 'Deck or single slide', text: 'Build a full deck or just one slide from your data.' },
          { sub: 'Visualise', title: 'Graphs and KPI cards', text: 'Get business graphs and KPI cards chosen to suit your figures.' },
          { sub: 'Improve', title: 'Fix an existing deck', text: 'Improve the layout and readability of an existing PowerPoint.' },
          { sub: 'Safety', title: 'Autosave and recovery', text: 'Rely on local autosave and recovery of your current work.' },
          { sub: 'Export', title: 'Editable .pptx', text: 'Export editable PowerPoint with real objects, not flat pictures.' },
          { sub: 'Access', title: 'DCC or browser', text: 'Run via the DCC, or directly in your browser with nothing uploaded and no account needed.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A simple, direct workflow',
      lead: 'The tool is built to get you to a finished result quickly, without a setup project or an account for a third-party service.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Bring your material', text: 'Add your figures, rough points or an existing deck.' },
          { n: '02', title: 'Generate', text: 'Build a deck or a single slide, with graphs and KPI cards chosen for your data.' },
          { n: '03', title: 'Refine', text: 'Improve layout and readability, with local autosave keeping your work safe.' },
          { n: '04', title: 'Export', text: 'Export an editable .pptx with real, editable objects.' },
        ] },
      ],
    },
    {
      id: 'access',
      eyebrow: 'Local and private',
      title: 'Runs on your own computer',
      lead: 'This is a genuine VISINVOKE utility that does its work on your machine rather than on a web service.',
      blocks: [
        { t: 'callout', eyebrow: 'How it runs', title: 'Runs via VISINVOKE DCC or your browser', text: 'Presentation Studio normally runs in a dedicated desktop window through VISINVOKE DCC, and can also run directly in your browser for locked-down business PCs where the DCC cannot be installed.', items: [
          'Runs via VISINVOKE DCC, or directly in your browser',
          'Your files are processed on your own computer',
          'Free with your VISINVOKE account',
          'Part of the same VISINVOKE account as your other products',
        ] },
        { t: 'cards', cols: 2, items: [
          { sub: 'Free', title: 'A full utility, not a trial', text: 'This is a complete free VISINVOKE utility. It is not a time-limited trial and not a cut-down edition that pushes you toward a paid upgrade.' },
          { sub: 'Local', title: 'Your files stay with you', text: 'Your work is handled on your own computer with local autosave, and the browser mode uploads nothing and needs no account.' },
        ] },
      ],
    },
    {
      id: 'about',
      eyebrow: 'At a glance',
      title: 'The essentials',
      lead: 'A quick summary of how this utility is delivered and what it costs.',
      blocks: [
        { t: 'facts', cols: 3, items: [
          { label: 'Price', value: 'Free with your VISINVOKE account' },
          { label: 'Access', value: 'Runs via VISINVOKE DCC, or directly in your browser' },
          { label: 'Output', value: 'Editable PowerPoint (.pptx)' },
        ] },
        { t: 'note', title: 'Good to know', text: 'The browser mode exists for locked-down business PCs where the DCC cannot be installed. It uploads nothing and needs no account, and the .pptx export contains real editable objects rather than flat images.' },
      ],
    },
  ],
};

const FREE_IMAGE_COMPOSER = {
  key: 'image-composer',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · IMAGE COMPOSITION',
  name: 'VISINVOKE Image Composer',
  shortName: 'VISINVOKE Image Composer',
  icon: '🎨',
  accent: '#6366f1',
  accent2: '#4338ca',
  tagline: 'Combine separate images into one polished composition, on your own computer.',
  purpose: 'Combine separate images into one polished composition. Add a few images, move, resize, rotate and layer them, crop and add simple finishing, then export one finished image. Everything runs on your own computer, and the result drops straight into Presentation Studio, PowerPoint, a document or an email.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Available', tone: '#34d399' },
  accessNote: 'A free utility that runs via VISINVOKE DCC, or directly in your browser.',
  action: { kind: 'disabled', label: 'Free with VISINVOKE', pill: 'DCC or browser' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: 'DCC/browser', l: 'Runs via DCC or in your browser' },
    { v: 'Local', l: 'Runs on your computer' },
    { v: 'Export', l: 'PNG, JPG, WebP or copy' },
  ],
  highlights: ['Multi-image canvas', 'Move, resize, rotate', 'Layer control', 'Crop and finish', 'Custom canvas', 'Export or copy', 'Undo and redo', 'Runs via DCC or browser'],
  closing: { title: 'Several images in, one finished image out.', text: 'Layout, layering, cropping and finishing on a canvas you control, exported as one image that drops straight into your other work.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'One canvas for several images',
      lead: 'Putting a few images together neatly usually means opening a heavy editor for a simple job.',
      blocks: [
        { t: 'statement', headline: 'Combining a few images into one should not require a professional photo editor.', items: [
          'Placing several images together, layering and cropping them, and exporting a single clean result is a common need that heavyweight editors make more complicated than it should be.',
          'Image Composer gives you one canvas: add images, move, resize, rotate and layer them, crop and finish, then export a single image that drops straight into your other VISINVOKE work.',
        ] },
        { t: 'para', items: [
          'Add several images to one canvas by button or drag and drop, move, resize with corner handles and rotate each image, layer with bring forward, send backward, bring to front and send to back, crop with edge and corner handles, flip, duplicate and delete, add finishing touches such as shadow, border, rounded corners and opacity, choose a transparent, white or coloured canvas at 16:9, square, portrait or custom size, export PNG, transparent PNG, JPG or WebP or copy the image, and undo or redo across the whole composition.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'What you can do',
      title: 'Everything the utility gives you',
      lead: 'A direct, hands-on canvas for arranging and finishing several images.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Add', title: 'Several images, one canvas', text: 'Add several images to one canvas by button or drag and drop.' },
          { sub: 'Arrange', title: 'Move, resize, rotate', text: 'Move each image, resize it with corner handles and rotate it directly.' },
          { sub: 'Layer', title: 'Full layer control', text: 'Layer with bring forward, send backward, bring to front and send to back.' },
          { sub: 'Edit', title: 'Crop, flip, duplicate', text: 'Crop with edge and corner handles, flip, duplicate and delete.' },
          { sub: 'Finish', title: 'Simple finishing', text: 'Add shadow, border, rounded corners and opacity.' },
          { sub: 'Canvas', title: 'Any shape or colour', text: 'Choose transparent, white or coloured canvas at 16:9, square, portrait or a custom size.' },
          { sub: 'Export', title: 'Export or copy', text: 'Export PNG, transparent PNG, JPG or WebP, or copy the image.' },
          { sub: 'Undo', title: 'Full undo and redo', text: 'Undo and redo across the whole composition.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A simple, direct workflow',
      lead: 'The tool is built to get you to a finished result quickly, without a setup project or an account for a third-party service.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Add images', text: 'Add your images to one canvas by button or drag and drop.' },
          { n: '02', title: 'Arrange', text: 'Move, resize, rotate, layer and crop each image.' },
          { n: '03', title: 'Finish', text: 'Add shadow, border, rounded corners and opacity, and set the canvas.' },
          { n: '04', title: 'Export', text: 'Export one finished image, or copy it straight into your other work.' },
        ] },
      ],
    },
    {
      id: 'access',
      eyebrow: 'Local and private',
      title: 'Runs on your own computer',
      lead: 'This is a genuine VISINVOKE utility that does its work on your machine rather than on a web service.',
      blocks: [
        { t: 'callout', eyebrow: 'How it runs', title: 'Runs via VISINVOKE DCC or your browser', text: 'Image Composer runs in a dedicated desktop window through VISINVOKE DCC, and can also run directly in your browser. Either way it works on your own computer.', items: [
          'Runs via VISINVOKE DCC, or directly in your browser',
          'Your files are processed on your own computer',
          'Free with your VISINVOKE account',
          'Part of the same VISINVOKE account as your other products',
        ] },
        { t: 'cards', cols: 2, items: [
          { sub: 'Free', title: 'A full utility, not a trial', text: 'This is a complete free VISINVOKE utility. It is not a time-limited trial and not a cut-down edition that pushes you toward a paid upgrade.' },
          { sub: 'Local', title: 'Your files stay with you', text: 'Everything runs on your own computer, and the finished image drops straight into Presentation Studio, PowerPoint, a document or an email.' },
        ] },
      ],
    },
    {
      id: 'about',
      eyebrow: 'At a glance',
      title: 'The essentials',
      lead: 'A quick summary of how this utility is delivered and what it costs.',
      blocks: [
        { t: 'facts', cols: 3, items: [
          { label: 'Price', value: 'Free with your VISINVOKE account' },
          { label: 'Access', value: 'Runs via VISINVOKE DCC, or directly in your browser' },
          { label: 'Output', value: 'PNG, transparent PNG, JPG or WebP' },
        ] },
        { t: 'note', title: 'Good to know', text: 'Image Composer pairs naturally with Presentation Studio and Image Toolkit: compose a finished image here, then use it in a deck, a document or an email.' },
      ],
    },
  ],
};

const FREE_PEOPLE_EXPLORER = {
  key: 'people-explorer',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · PEOPLE AND WORKS RESEARCH',
  name: 'VISINVOKE People Explorer',
  shortName: 'VISINVOKE People Explorer',
  icon: '🔎',
  accent: '#14b8a6',
  accent2: '#0f766e',
  tagline: 'Explore public information about people and the works they are connected to.',
  purpose: 'Explore public information about people and the works they are connected to. Search across structured sources, inspect living and deceased cast lists, compare people, and see exactly where each fact came from. It runs on your own computer through VISINVOKE DCC with fast private local caching.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Available', tone: '#34d399' },
  accessNote: 'A free utility that runs in a dedicated desktop window through VISINVOKE DCC.',
  action: { kind: 'disabled', label: 'Free with VISINVOKE', pill: 'Runs via DCC' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: 'DCC', l: 'Runs via VISINVOKE DCC' },
    { v: 'Sourced', l: 'Facts show their origin' },
    { v: 'Local', l: 'Private local caching' },
  ],
  highlights: ['Universal search', 'Living and deceased status', 'Precise dates and ages', 'Sourced facts only', 'Cast and crew', 'Source and confidence', 'Local caching', 'CSV export'],
  closing: { title: 'Answers you can trace back to a source.', text: 'Universal people and works search, clear living and deceased status, and source, confidence and conflict details for the facts that matter.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'People and works, with the sources attached',
      lead: 'Looking someone up is easy. Knowing whether the answer is right, and where it came from, is not.',
      blocks: [
        { t: 'statement', headline: 'The problem with looking someone up is rarely finding an answer. It is trusting it.', items: [
          'General searches mix reliable and unreliable facts together, rarely show where a claim came from, and handle living versus deceased status and date precision poorly.',
          'People Explorer searches structured sources, shows living and deceased people together with clear status, preserves date precision, and attaches source, confidence and conflict detail to important facts.',
        ] },
        { t: 'para', items: [
          'Use universal search for people, television, movies, music, books and history; see living and deceased people together with clear status labels; view birth dates, death dates, current ages and age at death with date precision preserved; see cause-of-death facts only when a source states them; explore full cast and crew with filters, sorting and comparison; inspect source, confidence and conflict details; rely on fast private local caching, recent searches and favourites; and copy the current people or cast view to CSV.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'What you can do',
      title: 'Everything the utility gives you',
      lead: 'Everything is built so a fact comes with its provenance, not just its value.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Search', title: 'Universal search', text: 'Search for people, television, movies, music, books and history in one place.' },
          { sub: 'Status', title: 'Living and deceased', text: 'See living and deceased people together with clear status labels.' },
          { sub: 'Dates', title: 'Precise dates and ages', text: 'View birth and death dates, current ages and age at death with date precision preserved.' },
          { sub: 'Sourced', title: 'Only when stated', text: 'See cause-of-death facts only when a source actually states them.' },
          { sub: 'Casts', title: 'Cast and crew', text: 'Explore full cast and crew with filters, sorting and comparison.' },
          { sub: 'Provenance', title: 'Source and confidence', text: 'Inspect source, confidence and conflict details for important facts.' },
          { sub: 'Local', title: 'Private local caching', text: 'Rely on fast private local caching, recent searches and favourites.' },
          { sub: 'Export', title: 'CSV copy', text: 'Copy the current people or cast view to CSV.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A simple, direct workflow',
      lead: 'The tool is built to get you to a finished result quickly, without a setup project or an account for a third-party service.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Search', text: 'Search for a person, a work, a cast or a topic.' },
          { n: '02', title: 'Select a result', text: 'Open the person or the work you are interested in.' },
          { n: '03', title: 'Explore the full profile', text: 'Inspect dates, status, cast and crew, and where each fact came from.' },
          { n: '04', title: 'Compare or export', text: 'Compare people, or copy the current view to CSV.' },
        ] },
      ],
    },
    {
      id: 'access',
      eyebrow: 'Local and private',
      title: 'Runs on your own computer',
      lead: 'This is a genuine VISINVOKE utility that does its work on your machine rather than on a web service.',
      blocks: [
        { t: 'callout', eyebrow: 'How it runs', title: 'Runs via VISINVOKE DCC', text: 'People Explorer runs in a dedicated desktop window through VISINVOKE DCC, with fast private local caching on your own computer.', items: [
          'Runs in a dedicated desktop window through VISINVOKE DCC',
          'Your files are processed on your own computer',
          'Free with your VISINVOKE account',
          'Part of the same VISINVOKE account as your other products',
        ] },
        { t: 'cards', cols: 2, items: [
          { sub: 'Free', title: 'A full utility, not a trial', text: 'This is a complete free VISINVOKE utility. It is not a time-limited trial and not a cut-down edition that pushes you toward a paid upgrade.' },
          { sub: 'Local', title: 'Your files stay with you', text: 'Searches are cached privately on your own computer, and recent searches and favourites stay with you.' },
        ] },
      ],
    },
    {
      id: 'about',
      eyebrow: 'At a glance',
      title: 'The essentials',
      lead: 'A quick summary of how this utility is delivered and what it costs.',
      blocks: [
        { t: 'facts', cols: 3, items: [
          { label: 'Price', value: 'Free with your VISINVOKE account' },
          { label: 'Access', value: 'Runs via VISINVOKE DCC' },
          { label: 'Sources', value: 'Structured public sources, with provenance shown' },
        ] },
        { t: 'note', title: 'Good to know', text: 'People Explorer shows public information from structured sources and attaches provenance to important facts, including showing cause-of-death only when a source states it, so you can judge how much to trust each answer.' },
      ],
    },
  ],
};

const FREE_ANIMAL_EXPLORER = {
  key: 'animal-explorer',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · ANIMALS, TAXONOMY AND BIODIVERSITY',
  name: 'VISINVOKE Animal Explorer',
  shortName: 'VISINVOKE Animal Explorer',
  icon: '🐾',
  accent: '#0f9f8f',
  accent2: '#0f766e',
  tagline: 'Explore animal names and classification with evidence attached.',
  purpose: 'Search common and scientific names, follow accepted taxonomy, compare related animals, inspect licensed images and explore privacy-safe occurrence observations. Provider, licence, confidence and conflict information remains visible.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Available', tone: '#34d399' },
  accessNote: 'Runs inside VISINVOKE on Windows and Android. The public website is informational and never exposes the product as a browser substitute.',
  action: { kind: 'disabled', label: 'Free with VISINVOKE', pill: 'Windows + Android' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: 'Local first', l: 'Packaged taxonomy' },
    { v: 'Sourced', l: 'Provider and licence visible' },
    { v: 'Private', l: 'No raw occurrence coordinates' },
  ],
  highlights: ['Natural name search', 'Accepted taxonomy', 'Synonym handling', 'Compatible comparisons', 'Licensed imagery', 'Privacy-safe occurrences', 'Visible conflicts', 'Local favourites'],
  closing: { title: 'Discover animals without losing the evidence.', text: 'A local-first Explorer that keeps classification, attribution, uncertainty and privacy boundaries part of every answer.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'From a name to its place in Animalia', lead: 'Animal Explorer starts locally and enriches only when a source can add useful, attributable evidence.',
      blocks: [
        { t: 'statement', headline: 'A useful animal answer needs more than a picture and a paragraph.', items: ['Search common names, scientific names, synonyms, partial spellings and close typos.', 'Inspect accepted classification, source conflicts, compatible comparisons and reviewed provider enrichment without invented facts.'] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Taxonomy', title: 'Catalogue of Life foundation', text: 'The packaged Animalia taxonomy records its release, DOI, licence and validation state.' },
          { sub: 'Evidence', title: 'Sources stay separate', text: 'GBIF, Wikidata and Wikimedia Commons remain attributable provider layers rather than a silent merge.' },
          { sub: 'Privacy', title: 'Occurrence safety', text: 'Observation records are aggregated into coarse cells; sensitive data receives extra generalisation and raw coordinates never leave the native layer.' },
        ] },
      ],
    },
    {
      id: 'workflow', eyebrow: 'How you use it', title: 'Search, inspect, compare, continue', lead: 'The interface preserves results, filters and scroll position while you open a full animal depth layer.',
      blocks: [{ t: 'steps', cols: 4, items: [
        { n: '01', title: 'Search', text: 'Enter a common or scientific animal name.' },
        { n: '02', title: 'Choose carefully', text: 'Resolve synonyms and ambiguous matches with rank and classification context.' },
        { n: '03', title: 'Explore', text: 'Review taxonomy, sourced facts, licensed media and optional observation patterns.' },
        { n: '04', title: 'Compare', text: 'Align only compatible evidence and keep unknown values explicit.' },
      ] }],
    },
    {
      id: 'about', eyebrow: 'At a glance', title: 'The essentials', lead: 'A free product hosted by VISINVOKE, with provider boundaries kept narrow.',
      blocks: [
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'Windows DCC and Android in-app host' }, { label: 'Primary taxonomy', value: 'Versioned Catalogue of Life Animalia pack' }] },
        { t: 'note', title: 'Good to know', text: 'Animal Explorer is not a photo-identification tool and does not provide medical, veterinary or conservation-status advice. Missing evidence stays unknown.' },
      ],
    },
  ],
};

const FREE_MUSIC_EXPLORER = {
  key: 'music-explorer',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · MUSIC, ARTISTS AND DISCOGRAPHY',
  name: 'VISINVOKE Music Explorer',
  shortName: 'VISINVOKE Music Explorer',
  icon: '🎵',
  accent: '#24e0c1',
  accent2: '#1caee8',
  tagline: 'Explore artists, members and discographies with evidence attached.',
  purpose: 'Search artists, bands, albums, recordings, works and labels, read member timelines with join and leave dates, browse discographies in order and follow credits. Provider, licence and source-conflict information stays visible. No streaming, no lyrics and no assumed cover-art rights.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Available', tone: '#34d399' },
  accessNote: 'Runs inside VISINVOKE on Windows and Android. The public website is informational and never exposes the product as a browser substitute.',
  action: { kind: 'disabled', label: 'Free with VISINVOKE', pill: 'Windows + Android' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: 'Local first', l: 'Paced provider cache' },
    { v: 'Sourced', l: 'MusicBrainz core (CC0)' },
    { v: 'No streaming', l: 'Metadata, not audio or lyrics' },
  ],
  highlights: ['Artist and band search', 'Member timelines', 'Discography chronology', 'Work vs Recording', 'Release editions', 'Writer credits', 'People Explorer cross-link', 'Local favourites'],
  closing: { title: 'Understand the music without a streaming service.', text: 'A local-first Explorer that keeps artists, members, releases and works distinct, with sources attached to every answer.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'From a name to the whole discography', lead: 'Music Explorer starts fast, paces MusicBrainz responsibly and caches locally.',
      blocks: [
        { t: 'statement', headline: 'A useful music answer keeps the structure intact.', items: ['Search artists, bands, albums, singles, recordings, works and labels, or an exact MusicBrainz ID, ISRC or ISWC.', 'Read member timelines, discographies and credits without confusing a song, a performance and an edition.'] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Structure', title: 'MusicBrainz entities kept distinct', text: 'Work, Recording, Track, Release and Release Group each mean something different and stay separate.' },
          { sub: 'Members', title: 'Timelines with real dates', text: 'Current and former members, join and leave dates, and repeated membership periods, with imprecise dates shown honestly.' },
          { sub: 'Evidence', title: 'Sources stay separate', text: 'MusicBrainz core (CC0) and Wikidata (CC0) enrich the graph; non-commercial tags and ratings and copyrighted cover art are excluded.' },
        ] },
      ],
    },
    {
      id: 'workflow', eyebrow: 'How you use it', title: 'Search, open, follow, compare', lead: 'The interface preserves results, filters and scroll position while you open a full-screen depth layer.',
      blocks: [{ t: 'steps', cols: 4, items: [
        { n: '01', title: 'Search', text: 'Enter an artist, band, album, song or identifier.' },
        { n: '02', title: 'Disambiguate', text: 'Same-name artists stay separate with type, area and active years.' },
        { n: '03', title: 'Explore', text: 'Open member timelines, discographies, recordings and works.' },
        { n: '04', title: 'Compare', text: 'Compare artists on factual metadata, never on quality or popularity.' },
      ] }],
    },
    {
      id: 'about', eyebrow: 'At a glance', title: 'The essentials', lead: 'A free product hosted by VISINVOKE, with provider boundaries kept narrow.',
      blocks: [
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'Windows DCC and Android in-app host' }, { label: 'Primary source', value: 'MusicBrainz core database (CC0)' }] },
        { t: 'note', title: 'Good to know', text: 'Music Explorer is a metadata explorer. It does not stream audio, sell downloads, reproduce lyrics or assume cover-art rights. Missing data stays unknown rather than invented.' },
      ],
    },
  ],
};

const FREE_PLEX_DISCOVERY = {
  key: 'plex-discovery',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE COMPANION · PLEX DISCOVERY',
  name: 'VISINVOKE Plex Discovery',
  shortName: 'VISINVOKE Plex Discovery',
  icon: '🎬',
  accent: '#8b5cf6',
  accent2: '#6d28d9',
  tagline: 'Rediscover what is already on your own Plex server, in development for a 2027 companion release.',
  purpose: 'Rediscover what is already on your Plex server. Instead of scrolling past the same things, get a few strong choices from the media you already own. Plex Discovery is in development; the Plex connection and the discovery experience are being built, and this page describes the planned product rather than a released one.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'A planned free companion. In development and not yet released.',
  action: { kind: 'coming-soon', label: 'Coming Soon', pill: 'In development' },
  heroFacts: [
    { v: 'Free', l: 'Planned free companion' },
    { v: 'In dev', l: 'Being built now' },
    { v: 'Cross-device', l: 'DCC, Android and Google TV' },
    { v: 'Private', l: 'Uses only your Plex data' },
  ],
  highlights: ['Strong choices from your Plex', 'Free companion', 'Permanent taste model', 'From your Plex data only', 'Mood kept separate', 'Watch Together', 'Playback via Plex', 'In development'],
  closing: { title: 'A few strong choices from what you already own.', text: 'A planned free companion that turns your own Plex library into a handful of strong recommendations, with your taste kept private and playback handed back to Plex.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'Your own library, worth watching again',
      lead: 'The problem with a full Plex server is rarely too little to watch. It is finding the right thing tonight.',
      blocks: [
        { t: 'statement', headline: 'A wall of posters is not the same as knowing what to watch tonight.', items: [
          'A large Plex library quickly becomes hard to navigate, and most recommendation tools push outside content rather than surfacing what you already own.',
          'Plex Discovery is planned to give a few strong recommendations from your own Plex libraries, shaped by your permanent taste and your mood tonight, with playback always handed back to Plex.',
        ] },
        { t: 'para', items: [
          'The planned product will offer a few strong recommendations from your own Plex libraries rather than another wall of posters, work as one free companion across the DCC desktop, Android phone and tablet, and Google TV, hold a permanent taste model with LOVE, LIKE, NEUTRAL, LESS and AVOID plus hard exclusions, derive structural viewing characteristics only from your Plex data, keep your mood tonight separate from your permanent taste, support Watch Together for shared sessions across profiles, and always hand playback back to Plex.',
        ] },
      ],
    },
    {
      id: 'capabilities',
      eyebrow: 'What you can do',
      title: 'Everything the utility gives you',
      lead: 'These are the planned capabilities for Plex Discovery. It is in development and not yet released.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Recommend', title: 'A few strong choices', text: 'Planned: a few strong recommendations from your own Plex libraries, not another wall of posters.' },
          { sub: 'Companion', title: 'One free companion', text: 'Planned: one free companion across the DCC desktop, Android phone and tablet, and Google TV.' },
          { sub: 'Taste', title: 'Permanent taste', text: 'Planned: a permanent taste model with LOVE, LIKE, NEUTRAL, LESS and AVOID, plus hard exclusions.' },
          { sub: 'Private', title: 'From your Plex only', text: 'Planned: structural viewing characteristics derived only from your Plex data, never an outside source.' },
          { sub: 'Mood', title: 'Tonight kept separate', text: 'Planned: your mood tonight kept separate from your permanent taste.' },
          { sub: 'Together', title: 'Watch Together', text: 'Planned: Watch Together for shared sessions across profiles.' },
          { sub: 'Playback', title: 'Handed back to Plex', text: 'Planned: playback is always handed back to Plex itself.' },
        ] },
      ],
    },
    {
      id: 'workflow',
      eyebrow: 'How you use it',
      title: 'A simple, direct workflow',
      lead: 'The tool is built to get you to a finished result quickly, without a setup project or an account for a third-party service.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Connect Plex', text: 'Planned: connect Plex Discovery to your own Plex server.' },
          { n: '02', title: 'Set your taste', text: 'Planned: mark LOVE, LIKE, NEUTRAL, LESS and AVOID, and any hard exclusions.' },
          { n: '03', title: 'Pick a mood', text: 'Planned: choose your mood tonight, kept separate from your permanent taste.' },
          { n: '04', title: 'Watch', text: 'Planned: get a few strong choices, with playback handed back to Plex.' },
        ] },
      ],
    },
    {
      id: 'access',
      eyebrow: 'Local and private',
      title: 'Runs on your own computer',
      lead: 'This is a genuine VISINVOKE utility that does its work on your machine rather than on a web service.',
      blocks: [
        { t: 'callout', eyebrow: 'How it runs', title: 'A planned cross-device companion', text: 'Plex Discovery is planned to run as one free companion across the DCC desktop, Android phone and tablet, and Google TV. It is in development and not yet released.', items: [
          'Planned for the DCC desktop, Android phone and tablet, and Google TV',
          'Your files are processed on your own computer',
          'Free with your VISINVOKE account',
          'Part of the same VISINVOKE account as your other products',
        ] },
        { t: 'cards', cols: 2, items: [
          { sub: 'Free', title: 'A full utility, not a trial', text: 'This is a complete free VISINVOKE utility. It is not a time-limited trial and not a cut-down edition that pushes you toward a paid upgrade.' },
          { sub: 'Local', title: 'Your files stay with you', text: 'Plex Discovery is planned to derive its recommendations only from your own Plex data, keeping your taste private, with playback handed back to Plex.' },
        ] },
      ],
    },
    {
      id: 'about',
      eyebrow: 'At a glance',
      title: 'The essentials',
      lead: 'A quick summary of how this utility is delivered and what it costs.',
      blocks: [
        { t: 'facts', cols: 3, items: [
          { label: 'Price', value: 'Planned free companion' },
          { label: 'Status', value: 'In development, being built now' },
          { label: 'Devices', value: 'Planned for DCC desktop, Android and Google TV' },
        ] },
        { t: 'note', title: 'Good to know', text: 'Plex Discovery is in development. The Plex connection and the discovery experience are being built, this page describes the planned product, and there is nothing to launch or purchase yet.' },
      ],
    },
  ],
};

const WEBSITE_DNS_SSL_UPTIME_MONITOR = {
  key: 'website-dns-ssl-uptime-monitor',
  backTo: BACK_PAID,
  category: 'Paid VISINVOKE Utility',
  eyebrow: 'WEBSITE AND INTERNET OPERATIONS',
  name: 'Website / DNS / SSL / Uptime Monitor',
  shortName: 'Website Monitoring',
  icon: 'WDSUM',
  accent: '#22d3ee',
  accent2: '#0891b2',
  tagline: 'Know when the website, DNS or certificate really fails, with evidence from the regions that matter.',
  purpose: 'Website / DNS / SSL / Uptime Monitor checks availability, content, redirects, DNS, DNSSEC and certificate chains from independent regions, confirms incidents before escalating and records the recovery evidence. Private probes compare approved internal origins while VISINVOKE Cloud keeps authorised monitoring active when company computers are off.',
  price: { main: '$29.99 USD', billing: 'Per Month / Company', note: 'Unlimited authorised company users' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Runs in a dedicated desktop control centre with private local probes. Planned product, not yet released.',
  action: { kind: 'coming-soon', label: 'Coming Soon' },
  heroFacts: [
    { v: '3', l: 'HTTP, DNS and TLS evidence families' },
    { v: 'Regional', l: 'Independent confirmation quorum' },
    { v: 'Private', l: 'Approved local probe comparison' },
    { v: 'Due 2027', l: 'Planned release' },
  ],
  highlights: ['Regional quorum', 'HTTP assertions', 'DNS and DNSSEC', 'TLS certificate health', 'Correlated incidents', 'Privacy-safe status pages', 'Private probes', 'Explicit uptime formulas'],
  closing: { title: 'Availability deserves evidence, not a single green light.', text: 'Compare independent HTTP, DNS and certificate observations, confirm incidents before escalating and keep the recovery record without pretending that monitoring guarantees universal reachability.' },
  sections: [
    {
      id: 'what',
      eyebrow: 'What it does',
      title: 'One operational view for website, DNS and certificate health',
      lead: 'A website can appear healthy from one place while customers elsewhere see a DNS, certificate, content or routing failure.',
      blocks: [
        { t: 'statement', headline: 'A single successful request is not enough evidence of availability.', items: [
          'The monitor brings HTTP response evidence, DNS answers, DNSSEC validation and TLS certificate conclusions into the same service timeline. Each observation keeps its region, timing, freshness and policy context so a healthy result from one location does not erase a failure from another.',
          'Explicit states distinguish healthy, degraded, confirming, incident, recovering, maintenance, paused, no-data and misconfigured conditions. If Cloud or a probe is unavailable, the product reports that loss of evidence rather than turning it into a claim that the customer target failed.',
        ] },
        { t: 'cards', cols: 3, items: [
          { sub: 'HTTP', title: 'Bounded response assertions', text: 'Check safe methods, expected status ranges, redirect behaviour, content presence or absence, content type, response size and detailed timing with secret-safe evidence.' },
          { sub: 'DNS', title: 'Resolver and authority evidence', text: 'Compare recursive and authoritative records, expected values, propagation differences and DNSSEC validation across selected regions.' },
          { sub: 'TLS', title: 'Certificate-chain truth', text: 'Inspect hostname matching, chain construction, trust, algorithms, expiry and bounded revocation evidence without acting as a certificate authority.' },
        ] },
      ],
    },
    {
      id: 'confirmation',
      eyebrow: 'Trustworthy confirmation',
      title: 'Confirm an incident before escalating it',
      lead: 'Independent observations are preserved even when policy waits for a quorum before declaring a service incident.',
      blocks: [
        { t: 'steps', cols: 4, items: [
          { n: '01', title: 'Observe', text: 'Collect a bounded result from each selected regional or approved private probe with its own timings and conclusion.' },
          { n: '02', title: 'Correlate', text: 'Group observations by service, target, policy revision and time window without discarding regional disagreement.' },
          { n: '03', title: 'Confirm', text: 'Apply the chosen consecutive-failure and regional-quorum policy, while keeping the suspected state visible.' },
          { n: '04', title: 'Recover', text: 'Require recovery evidence, close the incident deliberately and retain the complete change and response history.' },
        ] },
        { t: 'split', items: [
          { title: 'Maintenance without blindness', text: 'Time-zone explicit maintenance windows can change alerting and uptime treatment, but they do not suppress probe observations. The evidence remains available for review and every exclusion is visible in the calculation.' },
          { title: 'Dependencies without guesswork', text: 'Service relationships help operators understand shared failures and likely blast radius, while each target and incident keeps its own evidence and conclusion.' },
        ] },
      ],
    },
    {
      id: 'response',
      eyebrow: 'Response and communication',
      title: 'Route alerts and publish status without leaking private detail',
      lead: 'Incident response needs accountable delivery, escalation and communication boundaries rather than an uncontrolled stream of messages.',
      blocks: [
        { t: 'cards', cols: 3, items: [
          { sub: 'Alerts', title: 'Routes with delivery truth', text: 'Configure destinations, acknowledgement and escalation timing, then retain whether delivery was attempted, accepted, failed or still pending.' },
          { sub: 'Status', title: 'Privacy-safe components', text: 'Publish only approved service components and customer-safe wording. Private targets, headers, addresses, secrets and internal probe details stay out of public status.' },
          { sub: 'Audit', title: 'A recovery record', text: 'Preserve observations, policy revisions, incident transitions, acknowledgements, maintenance decisions and status changes as a coherent record.' },
        ] },
        { t: 'callout', title: 'Monitoring is bounded evidence, not a guarantee', text: 'No monitoring product can prove continuous availability from every network. Results describe selected probes, regions, methods and time windows; unavailable or stale sources remain explicitly labelled.' },
      ],
    },
    {
      id: 'plans',
      eyebrow: 'Plans, demo and status',
      title: 'Four capacity tiers with the same monitoring model',
      lead: 'Pricing is monthly per company with unlimited authorised company users; higher tiers increase monitoring capacity rather than weakening safety rules.',
      blocks: [
        { t: 'tiers', cols: 4, items: [
          { eyebrow: 'INVOKE', title: 'INVOKE plan', price: '$29.99 USD', billing: 'per month / company', capacity: 'Core monitoring capacity', items: ['HTTP, DNS and TLS evidence', 'Regional confirmation', 'Incidents and alerts', 'Privacy-safe status'] },
          { eyebrow: 'FORGE', title: 'FORGE plan', price: '$59.99 USD', billing: 'per month / company', capacity: 'Expanded monitoring capacity', items: ['Same monitoring model', 'More checks and destinations', 'Private-probe capacity', 'Longer operational reach'] },
          { eyebrow: 'NEXUS', title: 'NEXUS plan', price: '$129.99 USD', billing: 'per month / company', capacity: 'Advanced monitoring capacity', items: ['Same safety boundaries', 'More regions and executions', 'Expanded status capacity', 'Company-wide reporting'] },
          { eyebrow: 'APEX', title: 'APEX plan', price: '$249.99 USD', billing: 'per month / company', capacity: 'Highest planned capacity', items: ['Same evidence semantics', 'Largest planned limits', 'Private and regional scale', 'Unlimited authorised users'] },
        ] },
        { t: 'note', title: 'Coming Soon / Due 2027', text: 'This product is not released. The permanent fictional sample cannot contact real targets, send real alerts or publish a real status page. A bounded live-target trial is planned for eligible company owners after release, with explicit caps, expiry and read-only retention. The prices shown are planned monthly pricing and there is no checkout on this page.' },
      ],
    },
  ],
};

const FREE_WORLD_EXPLORER = {
  key: 'world-explorer',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · COUNTRIES, CITIES, REGIONS AND WORLD GEOGRAPHY',
  name: 'VISINVOKE World Explorer',
  shortName: 'VISINVOKE World Explorer',
  icon: '🌍',
  accent: '#4cc2ff',
  accent2: '#1b3a6b',
  tagline: 'Explore countries, cities, regions and world facts with evidence attached.',
  purpose: 'Search countries, cities, regions, landmarks and natural features, follow geographic hierarchy, compare places, read population with its year, and see local maps and current local time. Provider, licence, confidence and conflict information remains visible.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Available', tone: '#34d399' },
  accessNote: 'Runs inside VISINVOKE on Windows and Android. The public website is informational and never exposes the product as a browser substitute.',
  action: { kind: 'disabled', label: 'Free with VISINVOKE', pill: 'Windows + Android' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: 'Local first', l: 'Small packaged geography' },
    { v: 'Sourced', l: 'Provider and licence visible' },
    { v: 'Private', l: 'No location tracking' },
  ],
  highlights: ['Natural place search', 'Population with year', 'Local maps', 'Time zones and local time', 'Comparisons', 'Languages and currencies', 'Visible conflicts', 'Local favourites'],
  closing: { title: 'Explore the world without losing the evidence.', text: 'A local-first Explorer that keeps geography, attribution, uncertainty and privacy boundaries part of every answer.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'From a name to its place in the world', lead: 'World Explorer starts locally and enriches only when a source can add useful, attributable evidence.',
      blocks: [
        { t: 'statement', headline: 'A useful place answer needs more than a pin and a paragraph.', items: ['Search common names, native names, ISO codes, City, Country and close typos.', 'Inspect population with its year, languages, currencies, time zones, maps, comparisons and reviewed enrichment without invented facts.'] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Geography', title: 'Open-data foundation', text: 'A small packaged pack from GeoNames and Natural Earth records its versions, licences and checksums.' },
          { sub: 'Evidence', title: 'Sources stay separate', text: 'Wikidata, Wikimedia Commons and vetted World Bank indicators remain attributable provider layers rather than a silent merge.' },
          { sub: 'Privacy', title: 'No location tracking', text: 'World Explorer does not request device location; current local time is computed on your device from the place time zone.' },
        ] },
      ],
    },
    {
      id: 'workflow', eyebrow: 'How you use it', title: 'Search, inspect, compare, continue', lead: 'The interface preserves results, filters and scroll position while you open a full place depth layer.',
      blocks: [{ t: 'steps', cols: 4, items: [
        { n: '01', title: 'Search', text: 'Enter a country, city, region, landmark or feature.' },
        { n: '02', title: 'Choose carefully', text: 'Resolve same-name places with region and country context; the larger place is never chosen for you.' },
        { n: '03', title: 'Explore', text: 'Review facts, local map, current local time and sourced enrichment.' },
        { n: '04', title: 'Compare', text: 'Align only compatible figures and keep unknown values explicit.' },
      ] }],
    },
    {
      id: 'about', eyebrow: 'At a glance', title: 'The essentials', lead: 'A free product hosted by VISINVOKE, with provider boundaries kept narrow.',
      blocks: [
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'Windows DCC and Android in-app host' }, { label: 'Primary geography', value: 'Versioned GeoNames and Natural Earth pack' }] },
        { t: 'note', title: 'Good to know', text: 'World Explorer is not a navigation, travel-booking or live-weather tool, and does not provide travel advisories. Missing figures stay unknown and stale values are never shown as current.' },
      ],
    },
  ],
};

const FREE_SCREEN_MANAGER = {
  key: 'screen-manager',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · VISUAL WORK AREAS, TOOLBARS AND PERSISTENT WINDOW MANAGEMENT',
  name: 'VISINVOKE Screen Manager',
  shortName: 'VISINVOKE Screen Manager',
  icon: '🖥️',
  accent: '#38bdf8',
  accent2: '#1e3a5f',
  tagline: 'Make one screen behave like many.',
  purpose: 'Create visual work areas, add persistent toolbars, keep applications inside their assigned areas and make your desktop restore itself every time Windows starts. Powerful window management underneath, a simple visual surface on top.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Available', tone: '#34d399' },
  accessNote: 'Runs inside VISINVOKE on Windows through the DCC. The public website is informational and never exposes the product as a browser substitute.',
  action: { kind: 'disabled', label: 'Free with VISINVOKE', pill: 'Windows' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: 'Persistent', l: 'Layout restores at startup' },
    { v: 'Visual', l: 'Draw areas, no coordinates' },
    { v: 'Windows', l: 'Runs via the DCC' },
  ],
  highlights: ['Visual work areas', 'Hard boundaries', 'Protected toolbars', 'Primary Work Area', 'Remembered applications', 'Persistent after reboot', 'Monitor recovery', 'Profiles'],
  closing: { title: 'Your layout. Your boundaries. Windows stay where you put them.', text: 'A visual work-area manager that keeps large displays, ultrawides and multi-monitor setups organised, and restores your layout every time Windows starts.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'From one large display to the work areas you actually want', lead: 'Screen Manager divides a display into visual work areas, keeps windows inside them, and brings the whole layout back automatically.',
      blocks: [
        { t: 'statement', headline: 'Powerful window management underneath, a simple visual surface on top.', items: ['Divide a large display, ultrawide or multi-monitor setup into exactly the working areas you want.', 'Maximized and restored windows stay inside their work area instead of covering the entire physical display.'] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Layout', title: 'Visual work areas', text: 'Draw or select work areas on the display, with no X/Y coordinates, monitor IDs or compatibility rules to enter.' },
          { sub: 'Boundaries', title: 'Hard boundaries', text: 'Windows are held inside their assigned work area, and protected toolbars are reserved space that applications never open underneath.' },
          { sub: 'Persistence', title: 'Restores itself', text: 'Your saved layout returns automatically when Windows starts, and a reconnected display is restored instead of rebuilt.' },
        ] },
      ],
    },
    {
      id: 'capabilities', eyebrow: 'Key capabilities', title: 'Everything Screen Manager keeps in place', lead: 'Eight capabilities that turn one physical screen into a set of dependable work areas.',
      blocks: [{ t: 'cards', cols: 4, items: [
        { sub: 'Areas', title: 'Visual Work Areas', text: 'Divide a large display, ultrawide or multi-monitor setup into exactly the working areas you want.' },
        { sub: 'Containment', title: 'Hard Boundaries', text: 'Maximized and restored windows stay inside their work area instead of covering the entire physical display.' },
        { sub: 'Toolbars', title: 'Protected Toolbars', text: 'Toolbars are treated as reserved space so applications do not open underneath them.' },
        { sub: 'New windows', title: 'Primary Work Area', text: 'Choose exactly where new unassigned windows should open.' },
        { sub: 'Assignments', title: 'Remember Applications', text: 'Optionally tell Screen Manager where specific applications belong.' },
        { sub: 'Startup', title: 'Persistent After Reboot', text: 'Your saved layout returns automatically when Windows starts.' },
        { sub: 'Displays', title: 'Monitor Recovery', text: 'Reconnect a display and Screen Manager restores its saved layout instead of making you rebuild it.' },
        { sub: 'Layouts', title: 'Profiles', text: 'Save different work layouts and switch between them without rebuilding the screen.' },
      ] }],
    },
    {
      id: 'workflow', eyebrow: 'How you set it up', title: 'Four steps to a persistent layout', lead: 'Normal setup is drag-and-drop and takes four steps.',
      blocks: [{ t: 'steps', cols: 4, items: [
        { n: '01', title: 'Choose your display', text: 'Select the screen you want to divide into work areas.' },
        { n: '02', title: 'Draw your work areas', text: 'Draw or select the work areas you want; outside columns can be narrower and the middle ones wider.' },
        { n: '03', title: 'Toolbars and Primary', text: 'Add toolbars where required and choose a Primary Work Area for new windows.' },
        { n: '04', title: 'Save', text: 'Save the layout; it returns automatically the next time Windows starts.' },
      ] }],
    },
    {
      id: 'about', eyebrow: 'At a glance', title: 'The essentials', lead: 'A free product hosted by VISINVOKE, built to stay simple on the surface.',
      blocks: [
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'Windows, via the VISINVOKE DCC' }, { label: 'Best for', value: 'Large TVs, ultrawides and multi-monitor workstations' }] },
        { t: 'note', title: 'Good to know', text: 'Screen Manager keeps windows inside the work areas you draw, reserves toolbar space, and restores your layout after a reboot or a display reconnect. It is powerful underneath but deliberately simple to set up.' },
      ],
    },
  ],
};

const FREE_EMAIL_CLIENT = {
  key: 'email-client',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · SIMPLE EMAIL, OUTLOOK IMPORT, FOLDERS AND RULES',
  name: 'VISINVOKE Email',
  shortName: 'VISINVOKE Email',
  icon: '📧',
  accent: '#3b82f6',
  accent2: '#1e3a8a',
  tagline: 'Simple email that keeps your old mail and sorts your new mail.',
  purpose: 'A deliberately simple email program for Windows, and for Android phones and tablets. On Windows, bring your old Outlook email across once and keep it in your own folders on your own computer. On every device, simple rules file new messages for you as they arrive, and everyday sending, replying and forwarding stay easy.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Available', tone: '#34d399' },
  accessNote: 'Runs inside VISINVOKE on Windows and Android. Outlook PST import is on Windows. The public website is informational and never exposes the product as a browser substitute.',
  action: { kind: 'disabled', label: 'Free with VISINVOKE', pill: 'Windows + Android' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: 'One-time', l: 'Outlook PST import' },
    { v: 'Local', l: 'Mail kept on your computer' },
    { v: 'Windows + Android', l: 'PST import on Windows' },
  ],
  highlights: ['Outlook PST import', 'Duplicate protection', 'Your own folders', 'Simple rules', 'Rules on new mail', 'Rules on existing mail', 'Reply and forward', 'Attachments and drafts'],
  closing: { title: 'Your old mail, your folders, your rules.', text: 'A simple Windows email program that brings your Outlook history across once and keeps new mail sorted the way you want it.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'Everyday email without the clutter', lead: 'VISINVOKE Email covers what most people actually use: reading, sending, folders and automatic sorting.',
      blocks: [
        { t: 'statement', headline: 'Deliberately simple on the surface, careful underneath.', items: ['Bring your old Outlook email across once and keep reading it long after the original file is gone.', 'Sort mail into your own folders by hand, or let simple rules do it for you.'] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Import', title: 'Outlook PST import', text: 'Choose one or more PST files and your old messages, folders and attachments come across. The original files are only read, never changed.' },
          { sub: 'Folders', title: 'Your own folders', text: 'Create, rename and delete folders, and move messages between them. Inbox, Sent, Drafts and Trash are always kept safe.' },
          { sub: 'Rules', title: 'Automatic sorting', text: 'Rules look at who sent a message, its subject, its text or other details, and move it to the folder you choose.' },
        ] },
      ],
    },
    {
      id: 'capabilities', eyebrow: 'Key capabilities', title: 'Everything VISINVOKE Email looks after', lead: 'Eight capabilities that cover everyday email from start to finish.',
      blocks: [{ t: 'cards', cols: 4, items: [
        { sub: 'History', title: 'One-time Import', text: 'Bring old Outlook email files across once, with messages kept in their original folders.' },
        { sub: 'Safety', title: 'Duplicate Protection', text: 'Importing the same file twice never doubles your mail.' },
        { sub: 'Organise', title: 'Folders', text: 'Create, rename, delete and move between folders you make yourself.' },
        { sub: 'Automatic', title: 'Rules on New Mail', text: 'New messages are sorted into your folders as soon as they arrive.' },
        { sub: 'Tidy up', title: 'Rules on Existing Mail', text: 'Run your rules over imported or older mail whenever you choose.' },
        { sub: 'Write', title: 'New, Reply and Forward', text: 'Write new email, Reply, Reply All and Forward, with attachments.' },
        { sub: 'Drafts', title: 'Saved Drafts', text: 'Unfinished messages are kept as drafts, and a message that could not be sent is never lost.' },
        { sub: 'Layout', title: 'Reading Pane', text: 'Read messages on the right or at the bottom, with pane sizes that stay as you set them.' },
      ] }],
    },
    {
      id: 'workflow', eyebrow: 'How you set it up', title: 'A guided start in a few steps', lead: 'START HERE walks you through setup one step at a time, and optional steps can be skipped.',
      blocks: [{ t: 'steps', cols: 4, items: [
        { n: '01', title: 'Add your account', text: 'Enter your email address and VISINVOKE Email fills in the usual settings for well-known providers.' },
        { n: '02', title: 'Test it', text: 'Check that receiving and sending both work before you rely on it.' },
        { n: '03', title: 'Bring old mail', text: 'Optionally import your Outlook PST files and create a few folders.' },
        { n: '04', title: 'Add rules', text: 'Optionally create simple rules and run them over the mail you imported.' },
      ] }],
    },
    {
      id: 'about', eyebrow: 'At a glance', title: 'The essentials', lead: 'A free product hosted by VISINVOKE, built to stay simple.',
      blocks: [
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'Windows DCC and Android in-app host' }, { label: 'Your mail', value: 'Kept on your own computer' }] },
        { t: 'note', title: 'Good to know', text: 'Email passwords are kept in the secure storage of Windows or Android, never in plain files. Pictures and scripts inside messages are handled safely, and program attachments are never opened automatically.' },
      ],
    },
  ],
};

const FREE_AGENT_CONTROL = {
  key: 'agent-control',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · RULES, PREFLIGHT AND TASK CONTROL FOR AI CODING AGENTS',
  name: 'VISINVOKE Agent Control',
  shortName: 'VISINVOKE Agent Control',
  icon: '\uD83E\uDDED',
  accent: '#22d3ee',
  accent2: '#164e63',
  tagline: 'Set the rules once. Keep your AI agents working the way you want.',
  purpose: 'Choose ready-made rules, write your own in plain English, or use ChatGPT in your browser to help structure them. Agent Control applies them to Claude and Codex for you, checks that they are really active, and never asks you to edit agent configuration files.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Available', tone: '#34d399' },
  accessNote: 'Runs inside VISINVOKE on Windows through the DCC. The public website is informational and never exposes the product as a browser substitute.',
  action: { kind: 'disabled', label: 'Free with VISINVOKE', pill: 'Windows' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: '2 agents', l: 'Claude and Codex' },
    { v: 'No AI inside', l: 'No API keys, no token billing' },
    { v: 'Windows', l: 'Runs via the DCC' },
  ],
  highlights: ['Ready-made rules', 'Plain-English rules', 'Scope and strength', 'Preflight', 'Completion checks', 'Current Task', 'ChatGPT copy and paste', 'Check Everything Is Working'],
  closing: { title: 'Expert-level control with consumer-level ease of use.', text: 'Decide what your agents should do in normal language. Agent Control handles how each agent stores and enforces it.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'Control how your AI agents work, without editing their files', lead: 'Agent Control keeps Claude and Codex working under the rules you choose, on every project.',
      blocks: [
        { t: 'statement', headline: 'Simple on the surface. Serious control underneath.', items: ['Your rules are stored once and applied to each agent in the form it understands.', 'Your own notes in existing agent files are always kept exactly as they are.'] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Rules', title: 'Ready-made or your own', text: 'Tick rules from the library, each explaining why it exists, or write your own in plain English.' },
          { sub: 'Control', title: 'Scope and strength', text: 'Apply a rule to all projects, one project or the current task, as guidance, a required check, or a guard where the agent supports one.' },
          { sub: 'Proof', title: 'Check Everything Is Working', text: 'A real check of this PC and the project shows Ready only with evidence, and says exactly what needs attention.' },
        ] },
      ],
    },
    {
      id: 'capabilities', eyebrow: 'Key capabilities', title: 'Everything Agent Control keeps in place', lead: 'Built for heavy, concurrent AI development work, and still easy to use.',
      blocks: [{ t: 'cards', cols: 4, items: [
        { sub: 'Before', title: 'Preflight', text: 'Agents establish the project, stage, authority and forbidden scope before editing anything.' },
        { sub: 'After', title: 'Completion checks', text: 'Agents confirm tests and acceptance criteria before saying done, and report extra stages instead of starting them.' },
        { sub: 'Now', title: 'Current Task', text: 'Define the task, its type, what to read, what to do and not do, and when it is done.' },
        { sub: 'Safety', title: 'Protects existing work', text: 'Rules and guards against restarting projects, destructive resets and overwriting concurrent changes.' },
      ] }],
    },
    {
      id: 'chatgpt', eyebrow: 'Optional help', title: 'ChatGPT in your browser, by copy and paste', lead: 'No AI is built in and no API key is needed.',
      blocks: [{ t: 'steps', cols: 4, items: [
        { n: '01', title: 'Describe it', text: 'Type what you want in normal language.' },
        { n: '02', title: 'Copy Prompt', text: 'Agent Control builds a complete prompt; you paste it into ChatGPT.' },
        { n: '03', title: 'Paste back', text: 'Paste the answer from ChatGPT. Agent Control validates it and shows a clear preview.' },
        { n: '04', title: 'Apply', text: 'Nothing changes until you click Apply.' },
      ] }],
    },
    {
      id: 'about', eyebrow: 'At a glance', title: 'The essentials', lead: 'A free product hosted by VISINVOKE, built to stay simple on the surface.',
      blocks: [
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'Windows, via the VISINVOKE DCC' }, { label: 'Agents', value: 'Claude (Claude Code) and Codex' }] },
        { t: 'note', title: 'Good to know', text: 'Where an agent cannot technically block an action, Agent Control says so and applies the rule as a required check instead. It never shows a setup as working without evidence.' },
      ],
    },
  ],
};

const FREE_BOOK_EXPLORER = {
  key: 'book-explorer',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · BOOKS, AUTHORS AND EDITIONS',
  name: 'VISINVOKE Books Explorer',
  shortName: 'VISINVOKE Books Explorer',
  icon: '📚',
  accent: '#6d67ff',
  accent2: '#4f46e5',
  tagline: 'Explore books, authors, editions and ISBNs in one fast, clean, ad-free Explorer. Coming soon.',
  purpose: 'Find a book by title, author or ISBN, see every catalogued edition with its publisher, language, format and identifiers, compare editions field by field and follow an author\'s bibliography in publication order. Every answer comes from the VISINVOKE book catalogue, with no adverts and no shop links. Books Explorer is complete inside VISINVOKE and is coming soon.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Coming soon to the VISINVOKE DCC on Windows and Android.',
  action: { kind: 'coming-soon', label: 'Coming Soon', pill: 'Windows + Android' },
  heroFacts: [
    { v: 'Free', l: 'With your VISINVOKE account' },
    { v: 'Ad-free', l: 'No shop links or adverts' },
    { v: 'Editions', l: 'Work and Edition kept distinct' },
    { v: 'Private', l: 'Your library stays on your device' },
  ],
  highlights: ['Title, author and ISBN search', 'Every catalogued edition', 'Edition comparison', 'Author bibliographies', 'Series statements as catalogued', 'Private shelves and ratings', 'Reading goals and lists', 'Sources on every record'],
  closing: { title: 'Every edition, clearly told apart.', text: 'A clean book catalogue that keeps books and their editions distinct, shows where each fact comes from and keeps your reading life private.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'From a title to the exact edition', lead: 'Books Explorer answers the practical questions readers, collectors and students actually ask.',
      blocks: [
        { t: 'statement', headline: 'A book is one work with many editions, and the details that matter usually belong to the edition.', items: [
          'Search a title, an author or an ISBN and see the book first, then every catalogued edition with its publisher, date, language, format, page count and identifiers.',
          'First publication stays with the book itself, so a later reprint never pretends to be the original.',
        ] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Search', title: 'Titles, authors and ISBNs', text: 'Accent-insensitive title and author search, with ISBN-10 and ISBN-13 numbers checked before they are looked up.' },
          { sub: 'Editions', title: 'Filter and compare', text: 'Filter editions by language, format and ISBN, then line up two to four editions field by field.' },
          { sub: 'Authors', title: 'Bibliographies', text: 'Life dates, other catalogued names and every linked work, ordered by first publication, title or number of editions.' },
        ] },
      ],
    },
    {
      id: 'library', eyebrow: 'Your library', title: 'Shelves, ratings, notes and goals', lead: 'Keep track of your reading without a social network watching over your shoulder.',
      blocks: [
        { t: 'list', items: [
          'Want to read, Reading, Read and Owned shelves',
          'Favourites and private one to five star ratings',
          'Your own notes on any book',
          'Lists for book clubs, courses or collections',
          'A yearly reading goal with progress',
          'Recently viewed books and authors',
        ] },
        { t: 'note', title: 'Private by design', text: 'Your shelves, ratings, notes, lists and goals are stored on your own device. They are never published, shared or used for recommendations.' },
      ],
    },
    {
      id: 'sources', eyebrow: 'Where facts come from', title: 'Honest catalogue information', lead: 'Books Explorer shows only facts it can trace, and says clearly what it does not record.',
      blocks: [
        { t: 'cards', cols: 2, items: [
          { sub: 'Sources', title: 'Visible on every record', text: 'Each book, edition and author view has a Sources panel with the catalogue record, its last update and the catalogue release VISINVOKE serves.' },
          { sub: 'Boundaries', title: 'No invented details', text: 'Series positions appear exactly as catalogued, missing facts are shown as not recorded, and cover images and publisher descriptions are left out until their rights are clear.' },
        ] },
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'VISINVOKE DCC on Windows and Android' }, { label: 'Status', value: 'Coming soon' }] },
      ],
    },
  ],
};

const FREE_FOOD_NUTRITION = {
  key: 'food-nutrition',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · FOOD AND NUTRITION',
  name: 'VISINVOKE Food & Nutrition',
  shortName: 'VISINVOKE Food & Nutrition',
  icon: '🥗',
  accent: '#22c55e',
  accent2: '#f59e0b',
  tagline: 'Look up calories, nutrients and serving sizes for everyday and branded foods in one clean, ad-free reference. Coming soon.',
  purpose: 'Search a food and see its energy in kilocalories and kilojoules, its macronutrients, vitamins and minerals per 100 g or per published serving, compare foods side by side and find foods high or low in a chosen nutrient. Every answer comes from the VISINVOKE-hosted copy of USDA FoodData Central, with no adverts and no shop links.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Coming soon to the VISINVOKE DCC on Windows and Android.',
  action: { kind: 'coming-soon', label: 'Coming Soon', pill: 'Windows + Android' },
  heroFacts: [{ v: 'Free', l: 'With your VISINVOKE account' }, { v: 'Ad-free', l: 'No shop links or adverts' }, { v: 'Per 100 g', l: 'Or per published serving' }, { v: 'USDA', l: 'FoodData Central data' }],
  highlights: ['Everyday and branded foods', 'Energy in kcal and kJ', 'Macronutrients, vitamins and minerals', 'Per 100 g or per serving', 'Side-by-side comparison', 'Rank foods by a nutrient', 'Data type on every food', 'Sources on every record'],
  closing: { title: 'Know what is in your food.', text: 'A clear nutrient reference that shows where every number comes from and never fills a gap with a guess.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'From a food to its full nutrient panel', lead: 'Food & Nutrition answers the everyday questions about what a food contains.',
      blocks: [
        { t: 'statement', headline: 'Nutrient values depend on where they come from, so every food shows its data type.', items: [
          'Foundation foods, standard reference foods, survey foods and branded label records are kept distinct, because they are measured and rounded differently.',
          'A nutrient that was not reported is shown as not reported, never as zero.',
        ] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Search', title: 'Everyday and branded foods', text: 'Search generic foods and branded products by name, brand or barcode number.' },
          { sub: 'Compare', title: 'Side by side', text: 'Line up foods and compare energy, protein, fat, carbohydrate, fibre, sugars and more.' },
          { sub: 'Rank', title: 'High or low in a nutrient', text: 'Find the foods highest or lowest in a chosen nutrient within a data type.' },
        ] },
      ],
    },
    {
      id: 'using', eyebrow: 'Using it', title: 'Clear numbers, honestly labelled', lead: 'Switch views without losing track of what a value means.',
      blocks: [
        { t: 'list', items: [
          'Switch between per 100 g and a published serving size',
          'Energy in both kilocalories and kilojoules',
          'Large, readable nutrient tables',
          'Branded label values shown as the label states them',
        ] },
        { t: 'note', title: 'Not medical advice', text: 'Food & Nutrition is a reference, not dietary or medical advice. Always check the label on the product you buy.' },
      ],
    },
    {
      id: 'sources', eyebrow: 'Where it comes from', title: 'Honest, credited information', lead: 'Every answer comes from VISINVOKE-hosted open data, credited as its licence requires.',
      blocks: [
        { t: 'cards', cols: 2, items: [
          { sub: 'Source', title: 'USDA FoodData Central', text: 'Food and nutrient data come from USDA FoodData Central, published in the public domain and served by VISINVOKE with its data version shown.' },
          { sub: 'Boundaries', title: 'No invented values', text: 'Brand names identify products only and never imply endorsement, and missing nutrients stay missing.' },
        ] },
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'VISINVOKE DCC on Windows and Android' }, { label: 'Status', value: 'Coming soon' }] },
      ],
    },
  ],
};

const FREE_PACKAGED_FOOD_SCANNER = {
  key: 'packaged-food-scanner',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · PACKAGED FOOD',
  name: 'VISINVOKE Packaged Food Scanner',
  shortName: 'VISINVOKE Packaged Food Scanner',
  icon: '🥫',
  accent: '#10b981',
  accent2: '#eab308',
  tagline: 'Scan or type a barcode and see allergens, ingredients and nutrition for packaged food, ad-free. Coming soon.',
  purpose: 'Scan a barcode with your Android camera, or type it or use a barcode scanner on Windows, and see the product\'s allergens first, then its ingredients, nutrition per 100 g or per serving and its published Nutri-Score and NOVA group. Compare products and keep favourites. Every answer comes from the VISINVOKE-hosted copy of Open Food Facts, with no adverts.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Coming soon to the VISINVOKE DCC on Windows and Android.',
  action: { kind: 'coming-soon', label: 'Coming Soon', pill: 'Windows + Android' },
  heroFacts: [{ v: 'Free', l: 'With your VISINVOKE account' }, { v: 'Allergens', l: 'Shown first on every product' }, { v: 'Private', l: 'Scans stay on your device' }, { v: 'ODbL', l: 'Open Food Facts data' }],
  highlights: ['Camera scan on Android', 'Manual or scanner entry on Windows', 'Allergens first', 'Ingredients as listed', 'Nutrition per 100 g or serving', 'Published Nutri-Score and NOVA', 'Product comparison', 'Favourites on your device'],
  closing: { title: 'Check the pack before you buy.', text: 'A fast, private product lookup that puts allergens first and always says where its information comes from.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'From a barcode to the facts on the pack', lead: 'Packaged Food Scanner answers the questions shoppers ask in the aisle.',
      blocks: [
        { t: 'statement', headline: 'Community data can be incomplete, so the physical label always has the final word.', items: [
          'Barcodes are checked before they are looked up, and EAN-13, EAN-8, UPC-A and UPC-E are supported.',
          'Missing facts are shown as not reported, never as zero.',
        ] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Scan', title: 'Camera or keyboard', text: 'On Android the camera scanner returns only the barcode number; on Windows type it or use a USB barcode scanner.' },
          { sub: 'Read', title: 'Allergens first', text: 'Allergens, then ingredients, nutrition and the published scores, with per 100 g and per serving views.' },
          { sub: 'Compare', title: 'Side by side', text: 'Line up products and compare their nutrition and published scores.' },
        ] },
      ],
    },
    {
      id: 'using', eyebrow: 'Using it', title: 'Quick answers, honest limits', lead: 'Scores are shown exactly as published, never as a VISINVOKE judgement.',
      blocks: [
        { t: 'list', items: [
          'Nutri-Score and NOVA explained in plain language',
          'Favourites and recent scans kept on your device',
          'No product photos, by design',
          'Works from the data version shown in the app',
        ] },
        { t: 'note', title: 'Always check the label', text: 'Product data comes from a community database. Check the physical label, especially for allergens.' },
      ],
    },
    {
      id: 'sources', eyebrow: 'Where it comes from', title: 'Honest, credited information', lead: 'Every answer comes from VISINVOKE-hosted open data, credited as its licence requires.',
      blocks: [
        { t: 'cards', cols: 2, items: [
          { sub: 'Source', title: 'Open Food Facts contributors', text: 'Product data: Open Food Facts contributors, available under the Open Database License, with each product\'s own page linked.' },
          { sub: 'Share-alike', title: 'An open copy for everyone', text: 'Once released, the VISINVOKE-derived product database is offered publicly under the same licence.' },
        ] },
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'VISINVOKE DCC on Windows and Android' }, { label: 'Status', value: 'Coming soon' }] },
      ],
    },
  ],
};

const FREE_KNOWLEDGE_EXPLORER = {
  key: 'knowledge-explorer',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · KNOWLEDGE AND REFERENCE',
  name: 'VISINVOKE Knowledge Explorer',
  shortName: 'VISINVOKE Knowledge Explorer',
  icon: '🧠',
  accent: '#0ea5e9',
  accent2: '#8b5cf6',
  tagline: 'Search people, places, events and ideas and follow the facts that connect them, ad-free. Coming soon.',
  purpose: 'Search for a person, place, event, organisation, creative work or idea and open a clear fact page with dates, places, identifiers and the entries it links to. Follow related entries, see what happened on this day, explore by category and compare entries side by side. Every answer comes from the VISINVOKE-hosted Knowledge Core built from Wikidata structured data, with no adverts.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Coming soon to the VISINVOKE DCC on Windows and Android.',
  action: { kind: 'coming-soon', label: 'Coming Soon', pill: 'Windows + Android' },
  heroFacts: [{ v: 'Free', l: 'With your VISINVOKE account' }, { v: 'Ad-free', l: 'No adverts or tracking' }, { v: 'Linked', l: 'Follow facts between entries' }, { v: 'CC0', l: 'Wikidata structured data' }],
  highlights: ['Search anything', 'Fact pages', 'Dates with their precision', 'Places and coordinates', 'Related entries', 'On this day', 'Explore by category', 'Side-by-side comparison'],
  closing: { title: 'Facts, and how they connect.', text: 'A clean reference that shows where each fact comes from and follows the links between them.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'From a name to everything it connects to', lead: 'Knowledge Explorer answers who, what, when and where questions.',
      blocks: [
        { t: 'statement', headline: 'Dates keep their stated precision, so a year is never shown as an exact day.', items: [
          'Dates before the common era are shown clearly, and coordinates are shown as numbers.',
          'A fact that is not recorded is shown as not recorded.',
        ] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Search', title: 'People, places and ideas', text: 'Search by name and filter by category.' },
          { sub: 'Explore', title: 'Related and On this day', text: 'Follow related entries and see what happened on any day of the year.' },
          { sub: 'Compare', title: 'Side by side', text: 'Line up entries and compare their key facts.' },
        ] },
      ],
    },
    {
      id: 'using', eyebrow: 'Using it', title: 'Honest about what it holds', lead: 'Some facts are missing or dated, and the page says so.',
      blocks: [
        { t: 'list', items: [
          'Sensitive personal attributes are not shown, by design',
          'No pictures, by design',
          'Outbound links to Wikipedia articles where they exist',
          'Works from the data version shown in the app',
        ] },
        { t: 'note', title: 'Private by design', text: 'What you search and save stays on your device.' },
      ],
    },
    {
      id: 'sources', eyebrow: 'Where it comes from', title: 'Honest, credited information', lead: 'Every answer comes from VISINVOKE-hosted open data, credited as its licence requires.',
      blocks: [
        { t: 'cards', cols: 2, items: [
          { sub: 'Source', title: 'Wikidata structured data', text: 'Facts come from Wikidata structured data, published under CC0 and served by VISINVOKE with its data version shown.' },
          { sub: 'Boundaries', title: 'No invented facts', text: 'Only recorded statements are shown, with their dates and qualifiers.' },
        ] },
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'VISINVOKE DCC on Windows and Android' }, { label: 'Status', value: 'Coming soon' }] },
      ],
    },
  ],
};

const FREE_ART_MUSEUM_EXPLORER = {
  key: 'art-museum-explorer',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · ART AND CULTURE',
  name: 'VISINVOKE Art & Museum Explorer',
  shortName: 'VISINVOKE Art & Museum Explorer',
  icon: '🖼️',
  accent: '#d4a373',
  accent2: '#9b2c2c',
  tagline: 'Explore artworks and artists from great open museum collections in one clean, ad-free explorer. Coming soon.',
  purpose: 'Search an artist, subject or title and explore artwork records from The Met and Smithsonian open-access collections, with dates, media, classifications and the museum\'s own record. Filter by museum, type and century, follow an artist to their works, compare artworks and keep favourites. Every answer comes from the VISINVOKE-hosted collection catalogue, with no adverts.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Coming soon to the VISINVOKE DCC on Windows and Android.',
  action: { kind: 'coming-soon', label: 'Coming Soon', pill: 'Windows + Android' },
  heroFacts: [{ v: 'Free', l: 'With your VISINVOKE account' }, { v: 'Ad-free', l: 'No shop links or adverts' }, { v: 'Two museums', l: 'The Met and Smithsonian' }, { v: 'CC0', l: 'Open-access records' }],
  highlights: ['Artist and subject search', 'Museum, type and century filters', 'Artwork records', 'Artist pages', 'Public-domain flags as published', 'Artwork comparison', 'Favourites on your device', 'Museum record links'],
  closing: { title: 'Great collections, clearly catalogued.', text: 'A calm way to explore open museum collections that shows exactly where every record comes from.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'From an artist to the works themselves', lead: 'Art & Museum Explorer brings open museum records together in one place.',
      blocks: [
        { t: 'statement', headline: 'Every record keeps its museum, so the same subject from two collections is never merged.', items: [
          'Dates and centuries, including those before the common era, are shown as the museum records them.',
          'An artist may appear under slightly different names in different museums, and the app explains why.',
        ] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Search', title: 'Artists and subjects', text: 'Search by artist, subject or title across both collections.' },
          { sub: 'Filter', title: 'Museum, type and century', text: 'Narrow results by museum, classification and century.' },
          { sub: 'Compare', title: 'Side by side', text: 'Line up artworks and compare their dates, media and classifications.' },
        ] },
      ],
    },
    {
      id: 'using', eyebrow: 'Using it', title: 'Records first, images later', lead: 'This stage shows collection records; artwork images are not included yet.',
      blocks: [
        { t: 'list', items: [
          'Public-domain status shown exactly as the museum publishes it',
          'Artist pages with every linked work',
          'Favourites kept on your device',
          'Works from the data version shown in the app',
        ] },
        { t: 'note', title: 'No images yet', text: 'Artwork images are added only where the museum marks the image itself as free to reuse.' },
      ],
    },
    {
      id: 'sources', eyebrow: 'Where it comes from', title: 'Honest, credited information', lead: 'Every answer comes from VISINVOKE-hosted open data, credited as its licence requires.',
      blocks: [
        { t: 'cards', cols: 2, items: [
          { sub: 'Sources', title: 'The Met and Smithsonian', text: 'Records come from The Met Open Access and Smithsonian Open Access, published under CC0 and served by VISINVOKE.' },
          { sub: 'Boundaries', title: 'No invented details', text: 'Missing facts are shown as not recorded, and every artwork links to its museum record.' },
        ] },
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'VISINVOKE DCC on Windows and Android' }, { label: 'Status', value: 'Coming soon' }] },
      ],
    },
  ],
};

const FREE_TRAVEL_GUIDE = {
  key: 'travel-guide',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · TRAVEL',
  name: 'VISINVOKE Travel Guide',
  shortName: 'VISINVOKE Travel Guide',
  icon: '🧳',
  accent: '#14b8a6',
  accent2: '#fb7185',
  tagline: 'Read free travel guides for destinations around the world, ad-free and easy to navigate. Coming soon.',
  purpose: 'Search a destination and read its guide in clear sections, with listings of things to see and do, places to eat, drink and stay, their hours, prices and coordinates where given, sub-destinations and nearby places. Save places to a trip list kept on your device. Every guide comes from the VISINVOKE-hosted guides converted from Wikivoyage, with no adverts.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Coming soon to the VISINVOKE DCC on Windows and Android.',
  action: { kind: 'coming-soon', label: 'Coming Soon', pill: 'Windows + Android' },
  heroFacts: [{ v: 'Free', l: 'With your VISINVOKE account' }, { v: 'Ad-free', l: 'No booking links or adverts' }, { v: 'Worldwide', l: 'Destinations everywhere' }, { v: 'CC BY-SA', l: 'Wikivoyage travel text' }],
  highlights: ['Destination search', 'Guides in clear sections', 'See and Do listings', 'Eat, Drink and Sleep listings', 'Sub-destinations', 'Nearby places', 'Featured guides', 'Trip list on your device'],
  closing: { title: 'Plan the trip, not the tabs.', text: 'Free, community-written travel guides, clearly presented and honestly dated.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'From a destination to a plan', lead: 'Travel Guide answers where to go, what to do and where to stay.',
      blocks: [
        { t: 'statement', headline: 'Every guide shows when it was last edited, because travel details change.', items: [
          'Guides are organised into their sections, with listings kept as their authors wrote them.',
          'Coordinates are shown as numbers, and no photos are included.',
        ] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Search', title: 'Any destination', text: 'Search countries, regions, cities and places worldwide.' },
          { sub: 'Read', title: 'Clear sections', text: 'Jump between sections and open listings with hours and prices where given.' },
          { sub: 'Save', title: 'Your trip list', text: 'Keep places for your trip on your device.' },
        ] },
      ],
    },
    {
      id: 'using', eyebrow: 'Using it', title: 'Community travel text, clearly credited', lead: 'Guides are written by Wikivoyage contributors and converted by VISINVOKE.',
      blocks: [
        { t: 'list', items: [
          'Last-edited date on every guide',
          'Nearby places and sub-destinations',
          'Your trip list stays on your device',
          'Works from the data version shown in the app',
        ] },
        { t: 'note', title: 'Check before you travel', text: 'Opening hours and prices can change. Confirm important details before you travel.' },
      ],
    },
    {
      id: 'sources', eyebrow: 'Where it comes from', title: 'Honest, credited information', lead: 'Every answer comes from VISINVOKE-hosted open data, credited as its licence requires.',
      blocks: [
        { t: 'cards', cols: 2, items: [
          { sub: 'Source', title: 'Wikivoyage contributors', text: 'Travel text: Wikivoyage contributors, under CC BY-SA, converted and presented by VISINVOKE, with each guide\'s history linked.' },
          { sub: 'Share-alike', title: 'An open copy for everyone', text: 'Once released, the converted guide text is offered publicly under the same licence.' },
        ] },
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'VISINVOKE DCC on Windows and Android' }, { label: 'Status', value: 'Coming soon' }] },
      ],
    },
  ],
};

const FREE_SPACE_EXPLORER = {
  key: 'space-explorer',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · SCIENCE',
  name: 'VISINVOKE Space Explorer',
  shortName: 'VISINVOKE Space Explorer',
  icon: '🪐',
  accent: '#818cf8',
  accent2: '#6366f1',
  tagline: 'Explore confirmed exoplanets, their star systems and mission candidates in one clean, ad-free explorer. Coming soon.',
  purpose: 'Search a planet, star system or mission candidate and open a clear page with its measured properties, discovery facts and references. Browse candidates from the TESS, Kepler and K2 missions, see how discoveries grew over time, chart planet properties and compare up to four planets. Every answer comes from the VISINVOKE-hosted snapshot of NASA Exoplanet Archive tables, with no adverts.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Coming soon to the VISINVOKE DCC on Windows and Android.',
  action: { kind: 'coming-soon', label: 'Coming Soon', pill: 'Windows + Android' },
  heroFacts: [{ v: 'Free', l: 'With your VISINVOKE account' }, { v: 'Ad-free', l: 'No adverts or tracking' }, { v: 'Planets', l: 'Confirmed planets and systems' }, { v: 'Candidates', l: 'TESS, Kepler and K2' }],
  highlights: ['Planet search', 'Star system pages', 'Mission candidates', 'Discovery timeline', 'Property charts', 'Planet comparison', 'Uncertainties shown', 'Citations on every page'],
  closing: { title: 'Worlds beyond our own.', text: 'A careful exoplanet explorer that keeps confirmed planets and candidates apart and cites every value.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'From a star to its planets', lead: 'Space Explorer answers what has been found around other stars.',
      blocks: [
        { t: 'statement', headline: 'Confirmed planets and candidates are never mixed.', items: [
          'Candidate pages always say they are not confirmed planets, and false positives are labelled as such.',
          'Values carry their source, and no planet is called habitable.',
        ] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Search', title: 'Planets and systems', text: 'Search confirmed planets, host stars and mission candidates.' },
          { sub: 'Discover', title: 'Over time', text: 'See discoveries by year and method.' },
          { sub: 'Compare', title: 'Up to four planets', text: 'Line up planets against Earth and Jupiter as reference points.' },
        ] },
      ],
    },
    {
      id: 'using', eyebrow: 'Using it', title: 'Careful with every number', lead: 'Composite values can combine different papers, and the page says so.',
      blocks: [
        { t: 'list', items: [
          'Minimum masses and other mass types are named',
          'Distances in parsecs with light years for comparison',
          'No images yet',
          'Works from the snapshot date shown in the app',
        ] },
        { t: 'note', title: 'No endorsement', text: 'Data come from NASA\'s Exoplanet Archive. VISINVOKE is not affiliated with or endorsed by NASA, Caltech or IPAC.' },
      ],
    },
    {
      id: 'sources', eyebrow: 'Where it comes from', title: 'Honest, credited information', lead: 'Every answer comes from VISINVOKE-hosted open data, credited as its licence requires.',
      blocks: [
        { t: 'cards', cols: 2, items: [
          { sub: 'Source', title: 'NASA Exoplanet Archive', text: 'This research has made use of the NASA Exoplanet Archive, which is operated by the California Institute of Technology, under contract with NASA under the Exoplanet Exploration Program.' },
          { sub: 'Citations', title: 'On every planet', text: 'Each planet shows its discovery and parameter references, and composite values cite their source.' },
        ] },
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'VISINVOKE DCC on Windows and Android' }, { label: 'Status', value: 'Coming soon' }] },
      ],
    },
  ],
};

const FREE_CLIMATE_EXPLORER = {
  key: 'climate-explorer',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · SCIENCE',
  name: 'VISINVOKE Historical Weather & Climate Explorer',
  shortName: 'VISINVOKE Historical Weather & Climate Explorer',
  icon: '🌦️',
  accent: '#38bdf8',
  accent2: '#fb923c',
  tagline: 'Explore long-term weather records, normals and trends for U.S. weather stations, ad-free. Coming soon.',
  purpose: 'Find a U.S. weather station and read its 1991-2020 normals, all-time and monthly records with their dates, its warmest, coolest, wettest and snowiest years, long-term trends and any year day by day, or check what happened on a date in history. Compare up to four stations. Every answer comes from VISINVOKE-hosted NOAA records, with no adverts.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Coming soon to the VISINVOKE DCC on Windows and Android.',
  action: { kind: 'coming-soon', label: 'Coming Soon', pill: 'Windows + Android' },
  heroFacts: [{ v: 'Free', l: 'With your VISINVOKE account' }, { v: 'Ad-free', l: 'No adverts or tracking' }, { v: 'Since 1849', l: 'Long station records' }, { v: 'NOAA', l: 'U.S. station data' }],
  highlights: ['Station search', 'Nearby stations', 'Monthly normals', 'Records with dates', 'Long-term trends', 'Year day by day', 'This day in history', 'Station comparison'],
  closing: { title: 'The long view of the weather.', text: 'Decades of station records, clearly presented, with every gap and rule explained.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'From a station to its full record', lead: 'Climate Explorer answers what the weather has been like over the long term.',
      blocks: [
        { t: 'statement', headline: 'These are historical records and 30-year averages, never forecasts or current conditions.', items: [
          'Months and years with too many missing days are marked, not counted as complete.',
          'Trends are shown with their limits explained.',
        ] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Find', title: 'Any U.S. station', text: 'Search by name or state, or enter coordinates to find nearby stations.' },
          { sub: 'Read', title: 'Normals and records', text: 'See 1991-2020 normals and records with the dates they were set.' },
          { sub: 'Compare', title: 'Up to four stations', text: 'Line up stations\' normals side by side.' },
        ] },
      ],
    },
    {
      id: 'using', eyebrow: 'Using it', title: 'Clear units, clear coverage', lead: 'Switch between Celsius and Fahrenheit, millimetres and inches.',
      blocks: [
        { t: 'list', items: [
          'Station names and abbreviations explained',
          'Quality-controlled values only',
          'Data-through date shown on every station',
          'Favourites and recent stations kept on your device',
        ] },
        { t: 'note', title: 'U.S. coverage', text: 'This release covers U.S. stations and territories. Stations outside the U.S. are not included.' },
      ],
    },
    {
      id: 'sources', eyebrow: 'Where it comes from', title: 'Honest, credited information', lead: 'Every answer comes from VISINVOKE-hosted open data, credited as its licence requires.',
      blocks: [
        { t: 'cards', cols: 2, items: [
          { sub: 'Source', title: 'NOAA NCEI', text: 'Station records come from NOAA NCEI GHCN-Daily and the U.S. Climate Normals 1991-2020, U.S. Government data served by VISINVOKE with their dates shown.' },
          { sub: 'Boundaries', title: 'No invented values', text: 'Missing data stay missing, and incomplete periods are marked.' },
        ] },
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'VISINVOKE DCC on Windows and Android' }, { label: 'Status', value: 'Coming soon' }] },
      ],
    },
  ],
};

const FREE_RECIPES = {
  key: 'recipes',
  backTo: BACK_FREE,
  category: 'Free VISINVOKE Utility',
  eyebrow: 'FREE UTILITY · FOOD',
  name: 'VISINVOKE Recipes',
  shortName: 'VISINVOKE Recipes',
  icon: '🍳',
  accent: '#4ade80',
  accent2: '#16a34a',
  tagline: 'Find, scale and cook recipes step by step, then plan meals and build a shopping list, ad-free. Coming soon.',
  purpose: 'Search recipes by cuisine, diet, meal, method and time, scale servings and switch between metric and US units, then cook step by step in a large-type cooking mode with timers. Find what you can cook from what you have, plan a week of meals, build a shopping list and keep your own recipes on your device. Recipes come from the VISINVOKE-hosted library converted from the Wikibooks Cookbook, with no adverts.',
  price: { main: 'Free', billing: 'With your VISINVOKE account', note: 'A full utility, not a trial or a feature-limited edition.' },
  status: { label: 'Coming soon', tone: '#fbbf24' },
  accessNote: 'Coming soon to the VISINVOKE DCC on Windows and Android.',
  action: { kind: 'coming-soon', label: 'Coming Soon', pill: 'Windows + Android' },
  heroFacts: [{ v: 'Free', l: 'With your VISINVOKE account' }, { v: 'Ad-free', l: 'No adverts or tracking' }, { v: 'Cook mode', l: 'Step by step with timers' }, { v: 'Private', l: 'Your recipes stay on your device' }],
  highlights: ['Recipe search with filters', 'Serving scaling', 'Metric and US units', 'Cooking mode with timers', 'What can I cook', 'Meal planner', 'Shopping list', 'Your own recipes'],
  closing: { title: 'From the recipe to the table.', text: 'Clear recipes, honest quantities and a calm cooking mode, with your own recipes kept private.' },
  sections: [
    {
      id: 'what', eyebrow: 'What it does', title: 'From a craving to a cooked meal', lead: 'Recipes answers what to cook tonight and how to cook it.',
      blocks: [
        { t: 'statement', headline: 'Quantities are converted only where it is safe, and otherwise left exactly as written.', items: [
          'Serving scaling and unit conversion show fractions the way cooks use them.',
          'Estimated nutrition is labelled as an estimate, with its limits explained.',
        ] },
        { t: 'cards', cols: 3, items: [
          { sub: 'Find', title: 'Filters that matter', text: 'Filter by cuisine, diet, meal, method, time and difficulty.' },
          { sub: 'Cook', title: 'Step by step', text: 'A large-type cooking mode with timers taken from the steps.' },
          { sub: 'Plan', title: 'Meals and shopping', text: 'Plan a week of meals and merge them into one shopping list.' },
        ] },
      ],
    },
    {
      id: 'using', eyebrow: 'Using it', title: 'Your kitchen, your data', lead: 'Personal recipes, plans and lists stay on your device.',
      blocks: [
        { t: 'list', items: [
          'Favourites and personal recipes kept on your device',
          'Related recipes on every page',
          'No photos, by design',
          'Works from the data version shown in the app',
        ] },
        { t: 'note', title: 'Nutrition is an estimate', text: 'Estimated nutrition is a guide, not dietary or medical advice.' },
      ],
    },
    {
      id: 'sources', eyebrow: 'Where it comes from', title: 'Honest, credited information', lead: 'Every answer comes from VISINVOKE-hosted open data, credited as its licence requires.',
      blocks: [
        { t: 'cards', cols: 2, items: [
          { sub: 'Source', title: 'Wikibooks Cookbook contributors', text: 'Recipes: Wikibooks Cookbook contributors, under CC BY-SA, converted and presented by VISINVOKE, with each recipe\'s history linked.' },
          { sub: 'Share-alike', title: 'An open copy for everyone', text: 'Once released, the converted recipe text is offered publicly under the same licence.' },
        ] },
        { t: 'facts', cols: 3, items: [{ label: 'Price', value: 'Free with your VISINVOKE account' }, { label: 'Access', value: 'VISINVOKE DCC on Windows and Android' }, { label: 'Status', value: 'Coming soon' }] },
      ],
    },
  ],
};

function plannedOperationalUtility({
  key, eyebrow, name, shortName, icon, accent, accent2, tagline, purpose,
  price, priceNote, highlights, capabilityTitle, capabilities, workflow,
  boundary, closing,
}) {
  return {
    key,
    backTo: BACK_PAID,
    category: 'Paid VISINVOKE Utility',
    eyebrow,
    name,
    shortName,
    icon,
    accent,
    accent2,
    tagline,
    purpose,
    price: { main: price, billing: 'Per Month / Company', note: priceNote },
    status: { label: 'Coming soon', tone: '#fbbf24' },
    accessNote: boundary,
    action: { kind: 'coming-soon', label: 'Coming Soon' },
    heroFacts: [
      { v: '4', l: 'Capacity tiers' },
      { v: 'Company', l: 'Unlimited authorised users' },
      { v: 'Local first', l: 'Desktop and field work' },
      { v: 'Due 2027', l: 'Planned release' },
    ],
    highlights,
    closing,
    sections: [
      {
        id: 'capabilities', eyebrow: 'Operational capability', title: capabilityTitle,
        lead: purpose,
        blocks: [{ t: 'cards', cols: 3, items: capabilities }],
      },
      {
        id: 'workflow', eyebrow: 'How it works', title: 'A controlled path from evidence to action',
        lead: 'Each stage preserves the context and evidence needed for the next decision.',
        blocks: [{ t: 'steps', cols: 4, items: workflow }],
      },
      {
        id: 'plans', eyebrow: 'Plans and boundaries', title: 'Capacity changes by tier; the operational controls do not',
        lead: 'INVOKE, FORGE, NEXUS and APEX use the same capability model with different published capacity limits.',
        blocks: [
          { t: 'facts', cols: 4, items: [
            { label: 'Entry plan', value: price + ' / month / company' },
            { label: 'Authorised users', value: 'Unlimited company users' },
            { label: 'Release', value: 'Coming soon · Due 2027' },
            { label: 'Access', value: 'Planned-capability details only' },
          ] },
          { t: 'note', title: 'Commercial and operational boundary', text: boundary },
        ],
      },
    ],
  };
}

const LOCAL_SEO_GRID_RANK_MONITOR = plannedOperationalUtility({
  key: 'local-seo-grid-rank-monitor',
  eyebrow: 'LOCAL MARKETING INTELLIGENCE',
  name: 'Local SEO Grid Rank Monitor',
  shortName: 'Local SEO Grid Rank Monitor',
  icon: 'LSGRM',
  accent: '#8f5dff',
  accent2: '#22d3ee',
  tagline: 'See where local visibility changes across the service area, with comparable grids and defensible evidence.',
  purpose: 'Local SEO Grid Rank Monitor measures location-specific local-search visibility with versioned grids, explicit observation outcomes, provider-policy provenance and privacy-safe service-area context. It preserves missing and ambiguous results instead of turning them into invented rankings.',
  price: '$29.99 USD',
  priceNote: 'Plans are based on locations and observations, not users',
  highlights: ['Versioned grids', 'Seven observation outcomes', 'Comparable movement', 'Policy provenance', 'Privacy-safe context', 'Responsible actions', 'Client reporting', 'Immutable evidence'],
  capabilityTitle: 'Spatial evidence that keeps every observation in context',
  capabilities: [
    { sub: 'Context', title: 'Versioned locations, queries and grids', text: 'Keep the exact location, query, point spacing and service-area context attached to every observation run.' },
    { sub: 'Outcomes', title: 'Seven explicit observation states', text: 'Separate ranked, not found, unavailable, blocked, ambiguous and other non-equivalent outcomes instead of forcing a false number.' },
    { sub: 'Comparison', title: 'Comparable spatial movement', text: 'Compare like-for-like grids and make changes in visibility readable across the service area.' },
    { sub: 'Provenance', title: 'Provider-policy evidence', text: 'Retain provider, collection-policy and attribution context with the result.' },
    { sub: 'Action', title: 'Responsible action planning', text: 'Turn evidence into bounded actions without promising or manufacturing ranking improvement.' },
    { sub: 'Reporting', title: 'Client-ready evidence', text: 'Package the grid, outcomes, change context and limitations into a report that can be explained.' },
  ],
  workflow: [
    { n: '01', title: 'Define the local context', text: 'Set locations, service area, queries and the versioned observation grid.' },
    { n: '02', title: 'Collect bounded observations', text: 'Run authorised observations while preserving provider-policy and missing-result states.' },
    { n: '03', title: 'Compare on the same basis', text: 'Review point-level and area-level movement against a comparable earlier run.' },
    { n: '04', title: 'Plan and report', text: 'Record responsible actions and create privacy-safe evidence for the customer.' },
  ],
  boundary: 'Planned-capability details only. There is no launch, purchase, checkout or subscription path. The permanent sample and bounded synthetic demo do not call a live provider, send email or webhooks, or create a Stripe charge.',
  closing: { title: 'Local visibility evidence you can explain.', text: 'Every point retains its location, outcome, provider context and comparison basis, so a map never hides the evidence underneath it.' },
});

const EQUIPMENT_SERVICE_CALIBRATION_MANAGER = plannedOperationalUtility({
  key: 'equipment-service-and-calibration-manager',
  eyebrow: 'EQUIPMENT ASSURANCE',
  name: 'Equipment Service & Calibration Manager',
  shortName: 'Equipment Service & Calibration Manager',
  icon: 'ESCM',
  accent: '#35b7a8',
  accent2: '#67e8f9',
  tagline: 'Know which equipment is fit for its intended use, what evidence supports that decision and what still needs action.',
  purpose: 'Equipment Service & Calibration Manager controls equipment status, service, calibration and verification evidence, certificate review, intended-use fitness and out-of-tolerance impact. It keeps service, calibration and verification distinct and makes reference-chain gaps visible.',
  price: '$19.99 USD',
  priceNote: 'Plans are based on equipment and completed activities',
  highlights: ['Equipment register', 'Due programmes', 'Service evidence', 'Calibration evidence', 'Verification evidence', 'Certificate review', 'Out-of-tolerance impact', 'Return to service'],
  capabilityTitle: 'A complete equipment-assurance record, from due state to return to service',
  capabilities: [
    { sub: 'Register', title: 'Controlled equipment records', text: 'Keep the equipment, site, intended use, programme and current due state together.' },
    { sub: 'Work', title: 'Distinct activity workflows', text: 'Record service, calibration and verification as different activities with the evidence each one requires.' },
    { sub: 'Measurement', title: 'As-found and as-left evidence', text: 'Capture readings, uncertainty and decision-rule context without collapsing them into one pass/fail label.' },
    { sub: 'Certificates', title: 'Technical certificate review', text: 'Review certificates and expose missing or incomplete reference-standard chains.' },
    { sub: 'Impact', title: 'Out-of-tolerance containment', text: 'Trace affected use, containment, impact decisions and corrective action when an instrument is found out of tolerance.' },
    { sub: 'Decision', title: 'Controlled return to service', text: 'Record the competent decision, supporting evidence and status change before the equipment returns to use.' },
  ],
  workflow: [
    { n: '01', title: 'Register equipment', text: 'Record identity, intended use, programme, site and evidence requirements.' },
    { n: '02', title: 'Perform the right activity', text: 'Run service, calibration or verification with its own structured evidence.' },
    { n: '03', title: 'Review fitness and impact', text: 'Assess certificates, measurement evidence and any out-of-tolerance impact.' },
    { n: '04', title: 'Release with evidence', text: 'Record competent clearance and preserve the return-to-service trail.' },
  ],
  boundary: 'Planned-capability details only. There is no launch, purchase, checkout, subscription, accreditation or certification path. The product is not a calibration laboratory, accreditation body, regulator, universal CMMS or substitute for competent technical judgement.',
  closing: { title: 'Fitness for use needs a visible chain of evidence.', text: 'The equipment record, activity evidence, reference chain, impact assessment and clearance decision remain connected.' },
});

const FLEET_SERVICE_INSPECTION_MANAGER = plannedOperationalUtility({
  key: 'fleet-service-and-inspection-manager',
  eyebrow: 'FLEET ROADWORTHINESS',
  name: 'Fleet Service & Inspection Manager',
  shortName: 'Fleet Service & Inspection Manager',
  icon: 'FSIM',
  accent: '#2f9f86',
  accent2: '#34d399',
  tagline: 'Know what is ready to dispatch, what is restricted and what still needs competent action.',
  purpose: 'Fleet Service & Inspection Manager connects daily observations, defects, due programmes, skilled inspections, repairs and competent clearance to dispatch decisions for vehicle, trailer and powered-attachment fleets.',
  price: '$19.99 USD',
  priceNote: 'Plans are based on fleet units and completed events',
  highlights: ['Fleet register', 'Daily checks', 'Skilled inspections', 'Due programmes', 'Defect control', 'Workshop evidence', 'Competent clearance', 'Dispatch snapshots'],
  capabilityTitle: 'One evidence chain from daily observation to dispatch readiness',
  capabilities: [
    { sub: 'Assets', title: 'Vehicles, trailers and attachments', text: 'Keep the fleet unit, combination, depot and operational context in one controlled register.' },
    { sub: 'Checks', title: 'Daily checks remain distinct', text: 'Record driver or operator observations without presenting them as a skilled inspection.' },
    { sub: 'Inspection', title: 'Skilled inspection evidence', text: 'Capture the competent person, scope, findings and evidence for scheduled and triggered inspections.' },
    { sub: 'Defects', title: 'Defect and workshop control', text: 'Carry a defect from observation through restriction, repair and supporting workshop evidence.' },
    { sub: 'Clearance', title: 'Competent return-to-service decision', text: 'Require a named, evidence-backed clearance before a restricted unit can return to dispatch.' },
    { sub: 'Dispatch', title: 'Point-in-time readiness snapshots', text: 'Show what was known, due, restricted and cleared for a unit or combination at dispatch time.' },
  ],
  workflow: [
    { n: '01', title: 'Observe and record', text: 'Capture a daily check, meter reading, defect or other fleet event at the point of use.' },
    { n: '02', title: 'Control the response', text: 'Apply the correct restriction, due programme, workshop or inspection workflow.' },
    { n: '03', title: 'Repair and inspect', text: 'Keep parts, work, findings and evidence attached to the unit and defect.' },
    { n: '04', title: 'Clear and dispatch', text: 'Record competent clearance and produce the current dispatch-readiness snapshot.' },
  ],
  boundary: 'Planned-capability details only. There is no launch, purchase, checkout, subscription, regulator approval or legal-roadworthiness path. The product does not guarantee compliance and is not a worker-disciplinary surveillance system or a substitute for competent judgement.',
  closing: { title: 'Dispatch readiness should be a decision with evidence.', text: 'Daily checks, skilled inspections, repairs, restrictions and competent clearance stay distinct while contributing to one point-in-time fleet decision.' },
});

export const PRODUCT_DETAILS = {
  'backup-utility': FREE_BACKUP_UTILITY,
  'pdf-toolkit': FREE_PDF_TOOLKIT,
  'image-toolkit': FREE_IMAGE_TOOLKIT,
  'usb-builder': FREE_USB_BUILDER,
  'presentation-studio': FREE_PRESENTATION_STUDIO,
  'image-composer': FREE_IMAGE_COMPOSER,
  'people-explorer': FREE_PEOPLE_EXPLORER,
  'animal-explorer': FREE_ANIMAL_EXPLORER,
  'plex-discovery': FREE_PLEX_DISCOVERY,
  'music-explorer': FREE_MUSIC_EXPLORER,
  'world-explorer': FREE_WORLD_EXPLORER,
  'screen-manager': FREE_SCREEN_MANAGER,
  'agent-control': FREE_AGENT_CONTROL,
  'email-client': FREE_EMAIL_CLIENT,
  'book-explorer': FREE_BOOK_EXPLORER,
  'food-nutrition': FREE_FOOD_NUTRITION,
  'packaged-food-scanner': FREE_PACKAGED_FOOD_SCANNER,
  'knowledge-explorer': FREE_KNOWLEDGE_EXPLORER,
  'art-museum-explorer': FREE_ART_MUSEUM_EXPLORER,
  'travel-guide': FREE_TRAVEL_GUIDE,
  'space-explorer': FREE_SPACE_EXPLORER,
  'climate-explorer': FREE_CLIMATE_EXPLORER,
  'recipes': FREE_RECIPES,
  'backup-verification-cloud': BACKUP_VERIFICATION_CLOUD,
  'contractor-compliance-vault': CONTRACTOR_COMPLIANCE_VAULT,
  'credential-renewal-manager': CREDENTIAL_RENEWAL_MANAGER,
  'compliance-evidence-vault': COMPLIANCE_EVIDENCE_VAULT,
  'maintenance-and-inspection-cloud': MAINTENANCE_AND_INSPECTION_CLOUD,
  'secure-client-document-portal': SECURE_CLIENT_DOCUMENT_PORTAL,
  'website-dns-ssl-uptime-monitor': WEBSITE_DNS_SSL_UPTIME_MONITOR,
  'local-seo-grid-rank-monitor': LOCAL_SEO_GRID_RANK_MONITOR,
  'equipment-service-and-calibration-manager': EQUIPMENT_SERVICE_CALIBRATION_MANAGER,
  'fleet-service-and-inspection-manager': FLEET_SERVICE_INSPECTION_MANAGER,
  'service-command-centre': SERVICE_COMMAND_CENTRE,
  'website-capture-suite': WEBSITE_CAPTURE_SUITE,
  'content-planner': CONTENT_PLANNER,
  'managed-pc-protection': MANAGED_PC_PROTECTION,
  'track-lock-wipe': TRACK_LOCK_WIPE,
  'veridian': VERIDIAN,
  'cybertech': CYBERTECH,
};

// Base route for every product detail page. Category cards link to
// PRODUCT_DETAIL_ROUTE + '/' + key via the shared "CLICK HERE FOR MORE INFO" pill.
export const PRODUCT_DETAIL_ROUTE = '/dashboard/product';

export function productDetailPath(key) {
  if (key === 'cybertech') return '/dashboard/rpg/cybertech/more-info';
  if (key === 'veridian') return '/dashboard/rpg/veridian/more-info';
  return PRODUCT_DETAIL_ROUTE + '/' + key;
}

// The one CTA label used by every product card that opens a detail page.
export const MORE_INFO_LABEL = 'CLICK HERE FOR MORE INFO';

export default PRODUCT_DETAILS;
